import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import StopIcon from '@mui/icons-material/Stop';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PeopleIcon from '@mui/icons-material/People';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CellTowerIcon from '@mui/icons-material/CellTower';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import jsPDF from 'jspdf';
// import PathPercentage from 'react-svg-path-percentage';
import { buildApiUrl, API_CONFIG } from '../config';
import { syncOperationsTypes, syncAllOperationsTypes, syncTasks, syncLocations, syncRadioChannels, syncFeedbacks } from '../utils/syncThrottled';
import { isAuthenticatedSync, apiFetch, getAccessTokenAsync } from '../utils/auth';
import { teamAPI, auditLogAPI, settingsAPI } from '../services/api';
import { canAccessSettings, canManageReports, canManageOperations, canManageTeams } from '../utils/permissions';
import { User, isNFCAvailable, syncUsers, startContinuousNFCScan } from '../services/nfc';
import TruppAuditlogModal from '../components/TruppAuditlogModal';
import MaydayNotificationManager from '../components/MaydayNotificationManager';
import GlobalSettingsModal from '../components/GlobalSettingsModal';
import UserIdentificationModal from '../components/UserIdentificationModal';
import { isTechnicalAccount, getRealUserIdentity, getRealUserDisplayName, getAuditUserInfo, RealUserIdentity } from '../utils/realUser';
import TruppHandoverModal from '../components/TruppHandoverModal';
import BulkFunkkanalModal from '../components/BulkFunkkanalModal';

interface TruppMitglied {
  vorname: string;
  nachname: string;
  druck?: number;
}

interface Trupp {
  id: number;
  aufgabe: string;
  standort: string;
  startdruck: number;
  currentPressure?: number; // Neues Feld für aktuellen Druck
  originalStartdruck?: number; // Ursprünglicher Startdruck vor Änderungen
  mitglieder: TruppMitglied[];
  truppfuehrerIndex: number;
  name: string;
  createdAt?: number;
  startedAt?: number | null; // Zeitpunkt, wenn der Trupp tatsächlich gestartet wurde
  outOfService?: boolean;
  abfrageCountdown?: number;
  abfrageResetAt?: number;
  statusOk?: boolean;
  mayday?: boolean;
  funkkanal?: string;
  creatorID?: number; // ID des Truppüberwachers, der den Trupp erstellt hat
  supervisorName?: string; // Name des Truppüberwachers (lastname firstname)
  // Für Pausieren/Fortsetzen
  pausedAt?: number;
  pausedElapsed?: number;
  // Ursprünglich gewählte Aufgabe (bevor Wartemodus)
  originalAufgabe?: string;
  geschlossen?: boolean;
  // Für Mayday-Benachrichtigungen
  maydayTriggeredBy?: string;
  maydayTriggeredAt?: number;
  emergencyTrupp?: number; // 0 = normal, 1 = emergency trupp
  remote?: boolean;
  // Alarm-Unterdrückung
  alarmMuted?: boolean;
  alarmMutedAt?: number; // Zeitpunkt der Stummschaltung (für Auto-Unmute nach 2 Min)
  einsatzCount?: number; // Wie oft der Trupp in diesem Einsatz war
  displayOrder?: number; // Manuelle Anzeige-Reihenfolge
  // Creator Objekt von API
  creator?: {
    id: number;
    firstName: string;
    lastName: string;
    rankShort: string;
    rankLong: string;
  };
}

interface TruppKachelProps {
  trupp: Trupp;
  onDelete: (id: number) => void;
  onStatus: (trupp: Trupp, resetAbfrage?: () => void) => Promise<void>;
  onOut: (id: number) => void;
  onReactivate: (id: number) => void;
  onCountdownChange: (id: number, abfrage: number) => void;
  onMitglieder: (trupp: Trupp) => void;
  onBearbeiten: (trupp: Trupp) => void;
  onRename?: (trupp: Trupp) => void;
  onShowAuditlog: (trupp: Trupp) => void;
  onEndMayday?: (trupp: Trupp) => void;
  onMuteToggle: (id: number) => void;
  onEmergencyChange?: (trupp: Trupp, emergency: boolean) => void;
  onRequestTypeChange?: (trupp: Trupp) => void;
  onPressureAdjust?: (trupp: Trupp) => void;
  showMitglieder?: boolean;
  setMitgliederModal?: React.Dispatch<React.SetStateAction<{ open: boolean; trupp: Trupp | null }>>;
  addAudit: (entry: Omit<AuditLogEntry, 'timestamp'>) => void;
  auditlog: AuditLogEntry[];
  einsatzGeschlossen: boolean;
  setShowDeleteTruppModal: React.Dispatch<React.SetStateAction<{ open: boolean, trupp: Trupp | null }>>;
  einsatzleiterMode?: boolean;
  getTruppueberwacherName?: (creatorID?: number) => string;
  hasSupervisorHeader?: boolean;
  supervisorBorderColor?: string;
  supervisorHeader?: {
    name: string;
    background: string;
    border: string;
    text: string;
  };
  canReorder?: boolean;
  isDragging?: boolean;
  onReorderPointerDown?: (e: React.PointerEvent) => void;
}

const truppOrderStorageKey = (einsatzId: number | string) => `truppDisplayOrder_${einsatzId}`;
const truppEinsatzCountStorageKey = (einsatzId: number | string) => `truppEinsatzCount_${einsatzId}`;

const readTruppOrder = (einsatzId: number | string): number[] => {
  try {
    const raw = localStorage.getItem(truppOrderStorageKey(einsatzId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(Number).filter((id) => Number.isFinite(id)) : [];
  } catch {
    return [];
  }
};

const writeTruppOrder = (einsatzId: number | string, ids: number[]) => {
  localStorage.setItem(truppOrderStorageKey(einsatzId), JSON.stringify(ids));
};

const readTruppEinsatzCounts = (einsatzId: number | string): Record<string, number> => {
  try {
    const raw = localStorage.getItem(truppEinsatzCountStorageKey(einsatzId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeTruppEinsatzCounts = (einsatzId: number | string, counts: Record<string, number>) => {
  localStorage.setItem(truppEinsatzCountStorageKey(einsatzId), JSON.stringify(counts));
};

const upsertTruppEinsatzCount = (einsatzId: number | string, truppId: number, count: number) => {
  const counts = readTruppEinsatzCounts(einsatzId);
  counts[String(truppId)] = Math.max(Number(counts[String(truppId)]) || 1, count || 1);
  writeTruppEinsatzCounts(einsatzId, counts);
};

const applyTruppEinsatzCounts = (teams: Trupp[], einsatzId: number | string): Trupp[] => {
  const saved = readTruppEinsatzCounts(einsatzId);
  const nextCounts: Record<string, number> = { ...saved };
  const result = teams.map((team) => {
    const savedCount = Number(saved[String(team.id)]) || 0;
    const count = Math.max(team.einsatzCount || 1, savedCount || 1);
    nextCounts[String(team.id)] = count;
    return { ...team, einsatzCount: count };
  });
  writeTruppEinsatzCounts(einsatzId, nextCounts);
  return result;
};

const applyTruppOrder = (teams: Trupp[], einsatzId: number | string): Trupp[] => {
  if (!teams.length) return teams;
  const saved = readTruppOrder(einsatzId);
  const byId = new Map(teams.map((team) => [team.id, team]));
  const ordered: Trupp[] = [];
  saved.forEach((id) => {
    const team = byId.get(id);
    if (team) {
      ordered.push(team);
      byId.delete(id);
    }
  });
  const remaining = Array.from(byId.values()).sort((a, b) => (a.createdAt || a.id) - (b.createdAt || b.id));
  const result = [...ordered, ...remaining].map((team, index) => ({ ...team, displayOrder: index }));
  writeTruppOrder(einsatzId, result.map((team) => team.id));
  return applyTruppEinsatzCounts(result, einsatzId);
};

const appendTruppOrderId = (einsatzId: number | string, id: number) => {
  const order = readTruppOrder(einsatzId);
  if (!order.includes(id)) writeTruppOrder(einsatzId, [...order, id]);
};

const replaceTruppOrderId = (einsatzId: number | string, oldId: number, newId: number) => {
  const order = readTruppOrder(einsatzId);
  const index = order.indexOf(oldId);
  if (index === -1) {
    appendTruppOrderId(einsatzId, newId);
    return;
  }
  order[index] = newId;
  writeTruppOrder(einsatzId, order);
};

const TruppKachel: React.FC<TruppKachelProps> = ({ trupp, onDelete, onStatus, onOut, onReactivate, onCountdownChange, onMitglieder, onBearbeiten, onRename, onShowAuditlog, onEndMayday, onMuteToggle, onEmergencyChange, onRequestTypeChange, onPressureAdjust, showMitglieder = true, setMitgliederModal, addAudit, auditlog, einsatzGeschlossen, setShowDeleteTruppModal, einsatzleiterMode = false, getTruppueberwacherName, hasSupervisorHeader = false, supervisorBorderColor, supervisorHeader, canReorder = false, isDragging = false, onReorderPointerDown }) => {
  // Animation für Druckanzeige
  const currentPressure = trupp.currentPressure || trupp.startdruck;
  const targetPercent = Math.min(Math.max((currentPressure / trupp.startdruck) * 100, 0), 100);
  const [animatedPercent, setAnimatedPercent] = React.useState(targetPercent);
  
  // Animation bei Änderung des Zielwerts
  React.useEffect(() => {
    const startPercent = animatedPercent;
    const endPercent = targetPercent;
    const duration = 150; // 0.15 Sekunden
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing-Funktion (ease-in-out)
      const easeInOut = progress < 0.5 
        ? 2 * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      
      const currentPercent = startPercent + (endPercent - startPercent) * easeInOut;
      setAnimatedPercent(currentPercent);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    if (Math.abs(startPercent - endPercent) > 0.1) {
      animate();
    }
  }, [targetPercent, animatedPercent]);

  // Zeit hochzählen ab Erstellzeitpunkt (nur wenn nicht Warten)
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    // Reset timer when no start time or when in Warten status or out of service
    const startTime = (trupp.startedAt && trupp.startedAt > 0) ? trupp.startedAt : trupp.createdAt;
    

    
    if (!startTime || trupp.aufgabe === 'Warten' || trupp.outOfService) {
      setElapsed(0);
      return;
    }
    
    const update = () => {
      const currentStartTime = (trupp.startedAt && trupp.startedAt > 0) ? trupp.startedAt : trupp.createdAt;
      if (!currentStartTime) {
        setElapsed(0);
        return;
      }
      const elapsed = Math.floor((Date.now() - currentStartTime) / 1000);
      setElapsed(Math.max(0, elapsed)); // Prevent negative values
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [trupp.startedAt, trupp.createdAt, trupp.aufgabe, trupp.outOfService]);
  
  // Additional reset when reactivating (outOfService changes from true to false)
  React.useEffect(() => {
    if (!trupp.outOfService && trupp.aufgabe === 'Warten') {
      setElapsed(0);
    }
  }, [trupp.outOfService, trupp.aufgabe]);
  // Zeitformatierung mm:ss
  const formatTime = (sec: number) => {
    const absSec = Math.max(0, sec); // Ensure non-negative values
    const m = Math.floor(absSec / 60).toString().padStart(2, '0');
    const s = (absSec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };
  const einsatzzeit = (trupp.aufgabe === 'Warten' || trupp.outOfService) ? formatTime(0) : formatTime(elapsed);

  // Countdown-Logik: Nur wenn nicht Warten
  const now = Date.now();
  const abfrageResetAt = trupp.abfrageResetAt || trupp.createdAt || now;
  const abfrageStart = typeof trupp.abfrageCountdown === 'number' ? trupp.abfrageCountdown : 300;
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    if (trupp.aufgabe === 'Warten') return; // Keine Countdowns
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [trupp.aufgabe]);
  
  // Robuste Timer-Berechnung mit Fallback für fehlerhafte Daten
  const calculateAbfrageCountdown = () => {
    if (trupp.aufgabe === 'Warten') return 300;
    
    const elapsed = Math.floor((now - abfrageResetAt) / 1000);
    const calculated = abfrageStart - elapsed;
    
    // Fallback: Nur zurücksetzen wenn Timer unplausibel (zu negativ oder zu positiv)
    // Negative Werte sind erlaubt (Abfrage überzogen)
    if (calculated < -3600 || calculated > 600) { // -1h bis +10min erlaubt
      return 300;
    }
    
    return calculated;
  };
  
  const abfrageCountdown = calculateAbfrageCountdown();

  // Formatierung für negative Zeiten
  const formatTimeSigned = (sec: number) => {
    const sign = sec < 0 ? '-' : '\u2007';
    const absSec = Math.abs(sec);
    const m = Math.floor(absSec / 60).toString().padStart(2, '0');
    const s = (absSec % 60).toString().padStart(2, '0');
    return `${sign}${m}:${s}`;
  };

  // Aus dem Einsatz ausgegraut
  const isOut = !!trupp.outOfService;

  // Funktion zum Zurücksetzen des Countdowns
  const resetAbfrage = () => {
    setTick(0);
  };

  // Reaktivieren: alles zurücksetzen
  const handleReactivate = () => {
    setTick(0);
    if (onReactivate) onReactivate(trupp.id);
  };

  // Druckstatus berechnen
  const getPressureStatus = () => {
    const pressure = trupp.currentPressure || trupp.startdruck;
    if (pressure >= 180) return { level: 'ok', color: '#1db954', text: 'OK' };
    if (pressure >= 100) return { level: 'warning', color: '#ffa500', text: 'Beobachten' };
    if (pressure >= 50) return { level: 'critical', color: '#c00', text: 'Rückzug' };
    return { level: 'alarm', color: '#c00', text: 'Notbereich' };
  };
  const pressureStatus = getPressureStatus();
  const isMaydayAlarm = !isOut && !!trupp.mayday;
  const isPressureAlarm = !isOut && (trupp.currentPressure || trupp.startdruck) <= 50;
  const isAbfrageAlarm = !isOut && abfrageCountdown <= 0;
  const isMuted = !!trupp.alarmMuted;

  // Blinken für Abfrage-Alarm und Druck-Alarm (nur wenn nicht stummgeschaltet)
  const [blink, setBlink] = React.useState(false);
  React.useEffect(() => {
    const shouldBlink = !isMuted && (isMaydayAlarm || isAbfrageAlarm || isPressureAlarm);
    let interval: NodeJS.Timeout | undefined;
    if (shouldBlink) {
      setBlink(false); // immer synchron starten
      interval = setInterval(() => setBlink(b => !b), 500);
    } else {
      setBlink(false);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isMaydayAlarm, isAbfrageAlarm, isPressureAlarm, isMuted]);

  // Mute-Countdown: Auto-Unmute nach 2 Minuten
  const MUTE_DURATION = 120; // 2 Minuten in Sekunden
  const [muteRemaining, setMuteRemaining] = React.useState<number>(0);
  React.useEffect(() => {
    if (!isMuted || !trupp.alarmMutedAt) {
      setMuteRemaining(0);
      return;
    }
    const update = () => {
      const elapsed = Math.floor((Date.now() - (trupp.alarmMutedAt || 0)) / 1000);
      const remaining = Math.max(MUTE_DURATION - elapsed, 0);
      setMuteRemaining(remaining);
      if (remaining <= 0) {
        // Auto-Unmute
        onMuteToggle(trupp.id);
        addAudit({
          type: 'Alarm aktiviert',
          message: '<b>Alarm aktiviert</b>: Stummschaltung automatisch nach 2 Minuten aufgehoben.',
          shortDescription: 'Alarm auto-aktiviert',
          data: {
            name: trupp.name,
            truppId: trupp.id,
            auto: true
          }
        });
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [isMuted, trupp.alarmMutedAt, trupp.id, trupp.name, onMuteToggle, addAudit]);
  const formatMuteTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Auto-Unmute wenn kein Alarm mehr aktiv ist
  const isAnyAlarmActive = isMaydayAlarm || isAbfrageAlarm || isPressureAlarm;
  React.useEffect(() => {
    if (isMuted && !isAnyAlarmActive) {
      onMuteToggle(trupp.id);
    }
  }, [isMuted, isAnyAlarmActive, trupp.id, onMuteToggle]);

  // Auditlog-Eintrag für Abfrage-Alarm direkt beim visuellen Alarm
  const abfrageAlarmLogged = React.useRef(false);
  
  // Funktion zum Abspielen eines akustischen Signals
  const playAlarmSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // Frequenz in Hz
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.warn('Audio playback not supported:', error);
    }
  };
  
  // Kontinuierliches Beep-Signal wenn Abfrage abgelaufen oder Druck kritisch
  // (nur wenn nicht stummgeschaltet und NICHT im Einsatzleitermodus – dort nur visuell)
  React.useEffect(() => {
    const shouldBeep = !isOut && !einsatzleiterMode && !isMuted && (isAbfrageAlarm || isPressureAlarm) && trupp.aufgabe !== 'Warten';
    
    if (shouldBeep) {
      // Sofort beim Start einmal beepen
      playAlarmSound();
      
      // Dann alle 3 Sekunden wiederholen
      const beepInterval = setInterval(() => {
        playAlarmSound();
      }, 3000);
      
      return () => {
        clearInterval(beepInterval);
      };
    }
  }, [isAbfrageAlarm, isPressureAlarm, trupp.aufgabe, isMuted, einsatzleiterMode]);
  
  React.useEffect(() => {
    // Reset flags when countdowns are reset
    if (abfrageCountdown > 0) {
      abfrageAlarmLogged.current = false;
    }
    
    // Only log alarms once when they first occur
    if (abfrageCountdown <= 0 && !abfrageAlarmLogged.current) {
      abfrageAlarmLogged.current = true;
    }
  }, [abfrageCountdown, trupp.id, trupp.name, addAudit]);

  // Status für Banner (OK/nicht OK/Warten)
  const statusOk = trupp.statusOk !== false && trupp.aufgabe !== 'Warten'; // true (OK), false (Nicht OK), Warten
  const isStatusNotOk = !isOut && trupp.aufgabe !== 'Warten' && trupp.statusOk === false;
  const isEmergency = trupp.emergencyTrupp === 1;
  const displayAufgabe = (trupp.aufgabe === 'Warten' ? (trupp.originalAufgabe || '') : trupp.aufgabe) || '';
  const channelLabel = (() => {
    const value = (trupp.funkkanal || '').trim();
    if (!value) return '–';
    return /^kanal\b/i.test(value) ? value : `Kanal ${value}`;
  })();
  const locationMeta = (trupp.standort || '').trim() || '–';
  const locationLabel = locationMeta;
  const assignmentLabel = displayAufgabe.trim() || '–';
  const leader = trupp.mitglieder[Math.min(trupp.truppfuehrerIndex || 0, Math.max(trupp.mitglieder.length - 1, 0))] || trupp.mitglieder[0];
  const leaderNachname = leader?.nachname?.trim() || '';
  const leaderVorname = leader?.vorname?.trim() || '';
  const leaderShortName = leader
    ? (leaderVorname
        ? `${leaderVorname.charAt(0).toUpperCase()}. ${leaderNachname}`
        : (leaderNachname || 'Kein Truppführer'))
    : 'Kein Truppführer';
  const leaderFullName = leader ? [leaderNachname, leaderVorname].filter(Boolean).join(', ') : 'Kein Truppführer';
  const [showTileSettings, setShowTileSettings] = React.useState(false);
  const [isMobileViewport, setIsMobileViewport] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [mobileExpanded, setMobileExpanded] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const apply = () => setIsMobileViewport(mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else mq.removeListener(apply);
    };
  }, []);
  React.useEffect(() => {
    // Alarme immer ausgeklappt zeigen (und andere zuklappen)
    if (isMaydayAlarm || isAbfrageAlarm || isPressureAlarm || isStatusNotOk) {
      setMobileExpanded(true);
      window.dispatchEvent(new CustomEvent('truppMobileExpand', { detail: { id: trupp.id } }));
    }
  }, [isMaydayAlarm, isAbfrageAlarm, isPressureAlarm, isStatusNotOk, trupp.id]);
  const canEditTile = !isOut && !einsatzleiterMode && !einsatzGeschlossen;
  const isImEinsatz = !isOut && trupp.aufgabe !== 'Warten' && !!(trupp.createdAt || trupp.startedAt);
  const canAdjustPressure = canEditTile && isImEinsatz && !!onPressureAdjust;
  const actualPressureValue = Math.round(trupp.currentPressure || trupp.startdruck);
  const statusBannerLabel = isOut
    ? 'Aus dem Einsatz'
    : trupp.aufgabe === 'Warten'
      ? 'Warten'
      : isMaydayAlarm
        ? 'MAYDAY'
        : isAbfrageAlarm
          ? 'Abfrage'
          : isStatusNotOk
            ? 'Nicht OK'
            : pressureStatus.text;
  const statusBannerBg = isOut
    ? '#9e9e9e'
    : trupp.aufgabe === 'Warten'
      ? '#9e9e9e'
      : isMaydayAlarm || isAbfrageAlarm || isPressureAlarm
        ? '#c00'
        : isStatusNotOk
          ? '#fbbd08'
          : pressureStatus.color;
  const statusTextColor = isOut
    ? '#888'
    : trupp.aufgabe === 'Warten'
      ? '#666'
      : isMaydayAlarm || isAbfrageAlarm || isPressureAlarm
        ? '#c00'
        : isStatusNotOk
          ? '#b78103'
          : pressureStatus.color;
  // Name für Mobile: Prefix und Rest getrennt (weniger Truncation)
  const mobileNameParts = (() => {
    const raw = String(trupp.name || '').trim();
    const m = /^(\S+)\s+(Trupp(?:\s+.+)?|Notfalltrupp)$/i.exec(raw);
    if (m) return { prefix: m[1], rest: m[2] };
    return { prefix: '', rest: raw || 'Trupp' };
  })();
  React.useEffect(() => {
    if (!isMobileViewport) return;
    const onOtherExpand = (e: Event) => {
      const detail = (e as CustomEvent<{ id: number }>).detail;
      if (detail?.id !== trupp.id) setMobileExpanded(false);
    };
    window.addEventListener('truppMobileExpand', onOtherExpand as EventListener);
    return () => window.removeEventListener('truppMobileExpand', onOtherExpand as EventListener);
  }, [isMobileViewport, trupp.id]);
  const toggleMobileExpanded = () => {
    setMobileExpanded((prev) => {
      const next = !prev;
      // Immer feuern: Parent setzt Höhen auf auto, damit Nachbarn nachrutschen
      window.dispatchEvent(new CustomEvent('truppMobileExpand', { detail: { id: trupp.id, expanded: next } }));
      return next;
    });
  };
  const einsatzAnzahl = React.useMemo(() => {
    const reactivations = (auditlog || []).filter((entry) => {
      const isReactivation = entry.type === 'Trupp reaktiviert' || entry.shortDescription === 'Trupp reaktiviert';
      const entryTruppId = Number(entry.data?.truppId ?? (entry as any).truppId);
      return isReactivation && entryTruppId === Number(trupp.id);
    }).length;
    return Math.max(trupp.einsatzCount || 1, reactivations + 1);
  }, [auditlog, trupp.id, trupp.einsatzCount]);
  const actionButtonStyle: React.CSSProperties = {
    border: 'none',
    borderRadius: 12,
    width: '50%',
    padding: '14px 8px',
    fontWeight: 800,
    fontSize: 13,
    letterSpacing: 0.4,
    minWidth: 0,
    margin: 0,
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    boxShadow: 'none',
    whiteSpace: 'nowrap',
  };

  // Auditlog-Modal wird jetzt im Parent gerendert
  // Ein Rahmen für die ganze Kachel (inkl. Überwacher-Header) – kein Doppelrand.
  const isCriticalAlarm = !isOut && (
    Boolean(trupp.mayday) || isAbfrageAlarm || isPressureAlarm
  );
  const showRedFrame = !isOut && (isCriticalAlarm || isEmergency);
  const frameColor = showRedFrame
    ? '#c00'
    : (hasSupervisorHeader && supervisorBorderColor ? supervisorBorderColor : null);

  return (
    <div style={{
      background: isOut ? '#e8e8e8' : '#fff',
      color: isOut ? '#aaa' : '#111',
      borderRadius: 22,
      padding: 0,
      width: '100%',
      marginBottom: 0,
      marginTop: 0,
      boxShadow: showRedFrame
        ? '0 2px 12px rgba(204, 0, 0, 0.28)'
        : '0 2px 10px rgba(0, 0, 0, 0.10)',
      position: 'relative',
      border: frameColor ? `2.5px solid ${frameColor}` : 'none',
      transition: 'box-shadow 0.2s, border-color 0.2s',
      opacity: isOut ? 0.6 : 1,
      filter: isOut ? 'grayscale(0.5)' : 'none',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      {supervisorHeader && (
        <div style={{
          background: supervisorHeader.background,
          padding: isMobileViewport ? '6px 12px' : '8px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          boxSizing: 'border-box',
          borderBottom: `1px solid ${showRedFrame ? 'rgba(204,0,0,0.25)' : supervisorHeader.border}`,
          flexShrink: 0,
        }}>
          <span style={{
            fontSize: isMobileViewport ? 13 : 14,
            fontWeight: 500,
            color: supervisorHeader.text,
            textAlign: 'center',
            lineHeight: 1.3,
          }}>
            Truppüberwacher: <strong>{supervisorHeader.name}</strong>
          </span>
        </div>
      )}
      <div style={{
        overflow: 'hidden',
        borderRadius: supervisorHeader ? '0 0 20px 20px' : 'inherit',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: 'auto',
        position: 'relative'
      }}>
      {/* Mobile: kompakte Zeile – Tippen klappt Details auf */}
      {isMobileViewport && (
        <button
          type="button"
          onClick={toggleMobileExpanded}
          style={{
            display: 'flex',
            alignItems: 'stretch',
            width: '100%',
            border: 'none',
            borderRadius: 0,
            borderBottom: mobileExpanded ? '1px solid #eee' : 'none',
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
            textAlign: 'left',
            minHeight: 68,
          }}
        >
          <div style={{
            width: 10,
            flexShrink: 0,
            alignSelf: 'stretch',
            margin: 0,
            background: statusBannerBg,
            boxShadow: ((isMaydayAlarm || isAbfrageAlarm || isPressureAlarm) && blink) ? 'inset 0 0 0 999px rgba(204,0,0,0.25)' : 'none',
          }} />
          <div style={{
            flex: 1,
            minWidth: 0,
            padding: '11px 12px 11px 12px',
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            columnGap: 10,
            rowGap: 2,
            alignItems: 'center',
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 6,
                minWidth: 0,
              }}>
                {mobileNameParts.prefix ? (
                  <span style={{
                    flexShrink: 0,
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: 0.6,
                    color: isOut ? '#aaa' : '#c00',
                    textTransform: 'uppercase',
                    lineHeight: 1,
                  }}>
                    {mobileNameParts.prefix}
                  </span>
                ) : null}
                <span style={{
                  fontSize: 17,
                  fontWeight: 800,
                  color: isOut ? '#888' : '#111',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.15,
                  minWidth: 0,
                }}>
                  {mobileNameParts.rest}
                </span>
              </div>
              {/* Meta: Bar, Standort, Auftrag, Funk – ohne Warten-Badge */}
              <div style={{
                marginTop: 5,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                minWidth: 0,
                flexWrap: 'nowrap',
                overflow: 'hidden',
                color: isOut ? '#aaa' : '#666',
                fontSize: 12,
                fontWeight: 600,
              }}>
                {(isMaydayAlarm || isAbfrageAlarm || isPressureAlarm || isStatusNotOk || isOut) && (
                  <span style={{
                    flexShrink: 0,
                    fontSize: 10,
                    fontWeight: 800,
                    lineHeight: 1,
                    padding: '3px 6px',
                    borderRadius: 999,
                    background: isOut
                      ? '#ececec'
                      : (isMaydayAlarm || isAbfrageAlarm || isPressureAlarm)
                        ? '#fdecea'
                        : '#fff6da',
                    color: statusTextColor,
                  }}>
                    {statusBannerLabel}
                  </span>
                )}
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  minWidth: 0,
                  overflow: 'hidden',
                }}>
                  <LocationOnOutlinedIcon style={{ fontSize: 13, color: '#bdbdbd', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{locationMeta}</span>
                </span>
                <span style={{ width: 1, height: 12, background: '#e0e0e0', flexShrink: 0 }} />
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  minWidth: 0,
                  overflow: 'hidden',
                }}>
                  <AssignmentOutlinedIcon style={{ fontSize: 13, color: '#bdbdbd', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{assignmentLabel}</span>
                </span>
                <span style={{ width: 1, height: 12, background: '#e0e0e0', flexShrink: 0 }} />
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  minWidth: 0,
                  overflow: 'hidden',
                  flexShrink: 0,
                }}>
                  <SettingsInputAntennaIcon style={{ fontSize: 13, color: '#bdbdbd', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(trupp.funkkanal || '').trim() || '–'}
                  </span>
                </span>
              </div>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              flexShrink: 0,
            }}>
              <div style={{ textAlign: 'right', lineHeight: 1 }}>
                <div style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: isOut || trupp.aufgabe === 'Warten'
                    ? '#aaa'
                    : (abfrageCountdown <= 0 ? '#c00' : '#111'),
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: -0.5,
                }}>
                  {isOut || trupp.aufgabe === 'Warten' ? '—' : formatTimeSigned(abfrageCountdown)}
                </div>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#9e9e9e', marginTop: 2, letterSpacing: 0.4 }}>
                  ABFRAGE
                </div>
              </div>
              <span style={{ color: '#bdbdbd', display: 'inline-flex', marginLeft: 2 }}>
                {mobileExpanded ? <ExpandLessIcon style={{ fontSize: 22 }} /> : <ExpandMoreIcon style={{ fontSize: 22 }} />}
              </span>
            </div>
          </div>
        </button>
      )}

      {(!isMobileViewport || mobileExpanded) && (
      <>
      {!isMobileViewport && (
      <div style={{
        position: 'relative',
        width: '100%',
        minHeight: 46,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        fontSize: trupp.mayday ? 20 : 16,
        letterSpacing: trupp.mayday ? 2 : 0.8,
        background: trupp.aufgabe === 'Warten'
          ? '#bbb'
          : isMaydayAlarm
            ? ((blink || isMuted) ? '#c00' : 'transparent')
            : isAbfrageAlarm
              ? ((blink || isMuted) ? '#c00' : 'transparent')
              : isPressureAlarm
                ? ((blink || isMuted) ? '#c00' : 'transparent')
                : isStatusNotOk
                  ? '#fbbd08'
                  : pressureStatus.color,
        color: trupp.aufgabe === 'Warten'
          ? '#444'
          : isMaydayAlarm
            ? ((blink || isMuted) ? '#fff' : '#c00')
            : isAbfrageAlarm
              ? ((blink || isMuted) ? '#fff' : '#c00')
              : isPressureAlarm
                ? ((blink || isMuted) ? '#fff' : '#c00')
                : '#fff',
        boxShadow: (trupp.mayday && blink) || ((isAbfrageAlarm || isPressureAlarm) && blink) ? '0 0 16px 4px #c00' : 'none',
        transition: 'background 0.2s, color 0.2s',
        textShadow: trupp.mayday && blink ? '0 2px 8px #c00' : 'none',
      }}>
        {(isAbfrageAlarm || isPressureAlarm || trupp.mayday) && trupp.aufgabe !== 'Warten' && (
          <span
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              color: (isMuted || blink) ? '#fff' : '#222',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center'
            }}
            onClick={e => { e.stopPropagation(); onMuteToggle(trupp.id); }}
            title={isMuted ? 'Alarm aktivieren' : 'Alarm stummschalten'}
          >
            {isMuted ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/>
              </svg>
            )}
          </span>
        )}
        <span style={{ flex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.15 }}>
          <span>
            {trupp.aufgabe === 'Warten'
              ? 'Warten'
              : isMaydayAlarm
              ? 'MAYDAY'
              : isAbfrageAlarm
                ? 'Abfrage zwingend'
                : isStatusNotOk
                  ? 'Nicht OK'
                  : pressureStatus.text}
          </span>
          {isMuted && muteRemaining > 0 && (
            <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.85, letterSpacing: 0.5 }}>
              Stumm {formatMuteTime(muteRemaining)}
            </span>
          )}
        </span>
        {!einsatzleiterMode && (
          <span
            style={{
              position: 'absolute',
              right: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: isOut ? 'not-allowed' : 'pointer',
              color: trupp.aufgabe === 'Warten' ? '#444' : (isMaydayAlarm || isAbfrageAlarm || isPressureAlarm) ? ((blink || isMuted) ? '#fff' : '#222') : '#fff',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              opacity: isOut ? 0.5 : 1
            }}
            onClick={e => {
              e.stopPropagation();
              if (isOut) return;
              setShowTileSettings(true);
            }}
            title="Trupp-Einstellungen"
          >
            <SettingsIcon fontSize="medium" />
          </span>
        )}
      </div>
      )}

      {/* Mobile expanded: Toolbar */}
      {isMobileViewport && mobileExpanded && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '6px 10px',
          background: '#fafafa',
          borderBottom: '1px solid #efefef',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            {canReorder && (
              <span
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onReorderPointerDown?.(e);
                }}
                title="Trupp verschieben"
                style={{
                  cursor: isDragging ? 'grabbing' : 'grab',
                  touchAction: 'none',
                  color: '#9e9e9e',
                  display: 'inline-flex',
                  padding: 4,
                }}
              >
                <DragIndicatorIcon style={{ fontSize: 20 }} />
              </span>
            )}
            <span style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#666',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {channelLabel !== '–' ? channelLabel : 'Kein Funkkanal'}
              {assignmentLabel !== '–' ? ` · ${assignmentLabel}` : ''}
              {isMuted && muteRemaining > 0 ? ` · Stumm ${formatMuteTime(muteRemaining)}` : ''}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            {(isAbfrageAlarm || isPressureAlarm || trupp.mayday) && trupp.aufgabe !== 'Warten' && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onMuteToggle(trupp.id); }}
                title={isMuted ? 'Alarm aktivieren' : 'Alarm stummschalten'}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#444', display: 'inline-flex', padding: 6 }}
              >
                {isMuted ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/>
                  </svg>
                )}
              </button>
            )}
            {!einsatzleiterMode && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isOut) return;
                  setShowTileSettings(true);
                }}
                disabled={isOut}
                title="Trupp-Einstellungen"
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: isOut ? 'not-allowed' : 'pointer',
                  color: '#444',
                  display: 'inline-flex',
                  padding: 6,
                  opacity: isOut ? 0.5 : 1,
                }}
              >
                <SettingsIcon style={{ fontSize: 18 }} />
              </button>
            )}
          </div>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: isMobileViewport ? '10px 12px 12px' : '16px 16px 14px' }}>
        <div style={{ display: isMobileViewport ? 'none' : 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {canReorder && (
              <span
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onReorderPointerDown?.(e);
                }}
                title="Trupp verschieben"
                style={{
                  cursor: isDragging ? 'grabbing' : 'grab',
                  touchAction: 'none',
                  userSelect: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: '#9e9e9e',
                  padding: 4,
                  marginLeft: -4,
                  flexShrink: 0,
                  position: 'relative',
                  zIndex: 40
                }}
              >
                <DragIndicatorIcon style={{ fontSize: 22 }} />
              </span>
            )}
            <strong
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: isOut ? '#888' : '#111',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {trupp.name}
            </strong>
          </div>
          {einsatzAnzahl >= 2 && (
            <span
              title={`${einsatzAnzahl}. Einsatz dieses Trupps`}
              style={{
                flexShrink: 0,
                marginLeft: 'auto',
                background: '#111',
                color: '#fff',
                borderRadius: 8,
                padding: '3px 8px',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 0.3,
                lineHeight: 1.2,
                whiteSpace: 'nowrap'
              }}
            >
              {einsatzAnzahl}. Einsatz
            </span>
          )}
        </div>

        {!isMobileViewport && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '4px 10px',
          marginTop: 8,
          color: isOut ? '#bbb' : '#757575',
          fontSize: 12,
          fontWeight: 500,
          minWidth: 0
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%' }}>
            <LocationOnOutlinedIcon style={{ fontSize: 16, color: isOut ? '#bbb' : '#9e9e9e', flexShrink: 0 }} />
            <span>{locationMeta}</span>
          </span>
          <span style={{ width: 1, height: 14, background: '#e0e0e0', flexShrink: 0 }} />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%' }}>
            <SettingsInputAntennaIcon style={{ fontSize: 16, color: isOut ? '#bbb' : '#9e9e9e', flexShrink: 0 }} />
            <span>{channelLabel}</span>
          </span>
          <span style={{ width: 1, height: 14, background: '#e0e0e0', flexShrink: 0 }} />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%' }}>
            <AssignmentOutlinedIcon style={{ fontSize: 16, color: isOut ? '#bbb' : '#9e9e9e', flexShrink: 0 }} />
            <span>{assignmentLabel}</span>
          </span>
        </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: isMobileViewport ? 0 : 14 }}>
          <button
            type="button"
            disabled={!canEditTile || !onRequestTypeChange}
            onClick={() => { if (canEditTile && onRequestTypeChange) onRequestTypeChange(trupp); }}
            title={canEditTile ? (isEmergency ? 'Zu einem Normaltrupp wechseln' : 'Zu einem Notfalltrupp wechseln') : undefined}
            style={{
              flex: '1 1 50%',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              border: 'none',
              borderRadius: 12,
              padding: '8px 10px',
              background: isEmergency ? '#fdecea' : '#f5f5f5',
              cursor: canEditTile && onRequestTypeChange ? 'pointer' : 'default',
              textAlign: 'left',
              opacity: canEditTile ? 1 : 0.7
            }}
          >
            <span style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              background: isEmergency ? '#c00' : '#e0e0e0'
            }}>
              {isEmergency
                ? <HealthAndSafetyIcon style={{ fontSize: 18, color: '#fff' }} />
                : <PeopleIcon style={{ fontSize: 18, color: '#616161' }} />}
            </span>
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 11, color: '#9e9e9e', fontWeight: 500 }}>Trupptyp</span>
              <span style={{
                fontSize: 14,
                fontWeight: 700,
                color: isOut ? '#888' : (isEmergency ? '#c00' : '#111'),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {isEmergency ? 'Notfalltrupp' : 'Normaltrupp'}
              </span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              if (isOut) return;
              onMitglieder(trupp);
            }}
            disabled={isOut}
            title={leaderFullName}
            style={{
              flex: '1 1 50%',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              border: 'none',
              borderRadius: 12,
              padding: '8px 10px',
              background: '#f5f5f5',
              cursor: isOut ? 'not-allowed' : 'pointer',
              textAlign: 'left'
            }}
          >
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 11, color: '#9e9e9e', fontWeight: 500 }}>Truppführer</span>
              <span style={{
                fontSize: 15,
                fontWeight: 800,
                color: isOut ? '#888' : '#111',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.2
              }}>{leaderShortName}</span>
            </span>
            <ChevronRightIcon style={{ color: '#bdbdbd', flexShrink: 0 }} />
          </button>
        </div>

        {isMobileViewport ? (
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (!canAdjustPressure || !onPressureAdjust) return;
              onPressureAdjust(trupp);
            }}
            title={canAdjustPressure ? 'Druck anpassen' : undefined}
            style={{
              marginTop: 10,
              padding: '10px 12px',
              borderRadius: 12,
              background: '#f7f7f7',
              cursor: canAdjustPressure ? 'pointer' : 'default',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <div>
                <span style={{
                  fontSize: 28,
                  fontWeight: 800,
                  color: isOut ? '#888' : (isPressureAlarm ? '#c00' : '#111'),
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: -0.5,
                }}>
                  {actualPressureValue}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#9e9e9e', marginLeft: 4 }}>Bar</span>
              </div>
              <span style={{ fontSize: 12, fontWeight: 800, color: isOut ? '#bbb' : pressureStatus.color }}>
                {pressureStatus.text}
              </span>
            </div>
            <div style={{
              marginTop: 8,
              height: 10,
              borderRadius: 999,
              background: '#e6e6e6',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: `${Math.min(Math.max(animatedPercent, 2), 100)}%`,
                borderRadius: 999,
                background: isOut ? '#ccc' : pressureStatus.color,
                transition: 'width 0.2s ease',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: '#9e9e9e', fontWeight: 600 }}>
              <span>0</span>
              <span>{trupp.startdruck}</span>
            </div>
            <div style={{
              display: 'flex',
              marginTop: 10,
              gap: 8,
            }}>
              <div style={{
                flex: 1,
                background: '#fff',
                borderRadius: 10,
                padding: '8px 10px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 11, color: '#9e9e9e', fontWeight: 600 }}>Einsatzzeit</div>
                <div style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: isOut ? '#888' : '#111',
                  fontVariantNumeric: 'tabular-nums',
                  marginTop: 2,
                }}>{einsatzzeit}</div>
              </div>
              <div style={{
                flex: 1,
                background: '#fff',
                borderRadius: 10,
                padding: '8px 10px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 11, color: '#9e9e9e', fontWeight: 600 }}>Abfrage</div>
                <div style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: !isOut && abfrageCountdown <= 0 ? '#c00' : (isOut ? '#888' : '#111'),
                  fontVariantNumeric: 'tabular-nums',
                  marginTop: 2,
                }}>{formatTimeSigned(abfrageCountdown)}</div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (!canAdjustPressure || !onPressureAdjust) return;
                onPressureAdjust(trupp);
              }}
              title={canAdjustPressure ? 'Druck anpassen' : undefined}
              style={{
                position: 'relative',
                width: '100%',
                maxWidth: 280,
                height: 156,
                margin: '2px auto 0',
                cursor: canAdjustPressure ? 'pointer' : 'default'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 150" width="100%" height="150" preserveAspectRatio="xMidYMid meet">
                <path
                  d="M 30 120 A 90 90 0 0 1 210 120"
                  style={{ fill: 'transparent', stroke: '#ececec', strokeWidth: 22, strokeLinecap: 'round' }}
                />
                {(() => {
                  const pressurePercent = animatedPercent;
                  let strokeColor = '#1db954';
                  if (actualPressureValue < 50) strokeColor = '#db2828';
                  else if (actualPressureValue < 100) strokeColor = '#db2828';
                  else if (actualPressureValue < 180) strokeColor = '#fbbd08';
                  const radius = 90;
                  const centerX = 120;
                  const centerY = 120;
                  const startAngle = Math.PI;
                  const endAngle = startAngle + (Math.min(Math.max(pressurePercent, 0.5), 100) / 100) * Math.PI;
                  const x1 = centerX + radius * Math.cos(startAngle);
                  const y1 = centerY + radius * Math.sin(startAngle);
                  const x2 = centerX + radius * Math.cos(endAngle);
                  const y2 = centerY + radius * Math.sin(endAngle);
                  return (
                    <path
                      d={`M ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2}`}
                      style={{ fill: 'transparent', stroke: isOut ? '#ccc' : strokeColor, strokeWidth: 22, strokeLinecap: 'round', transition: 'stroke 0.3s ease-in-out' }}
                    />
                  );
                })()}
                <text x="30" y="146" textAnchor="middle" style={{ fontSize: 13, fill: isOut ? '#bbb' : '#9e9e9e' }}>0</text>
                <text x="210" y="146" textAnchor="middle" style={{ fontSize: 13, fill: isOut ? '#bbb' : '#9e9e9e' }}>{trupp.startdruck}</text>
              </svg>
              <div style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 78,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pointerEvents: 'none'
              }}>
                <span style={{ fontSize: 40, fontWeight: 800, letterSpacing: -0.5, lineHeight: 0.95, color: isOut ? '#888' : '#111' }}>{actualPressureValue}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#9e9e9e', letterSpacing: 0.4, lineHeight: 1, marginTop: 1 }}>Bar</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: isOut ? '#bbb' : pressureStatus.color, lineHeight: 1, marginTop: 3 }}>{pressureStatus.text}</span>
              </div>
            </div>

            <div style={{ display: 'flex', marginTop: 4, padding: '4px 0 10px' }}>
              <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#9e9e9e', fontWeight: 500 }}>Einsatzzeit</div>
                <div style={{
                  fontSize: 34,
                  fontWeight: 800,
                  letterSpacing: 0,
                  lineHeight: 1.2,
                  color: isOut ? '#888' : '#111',
                  fontVariantNumeric: 'tabular-nums',
                  fontFeatureSettings: '"tnum"',
                  minWidth: '6ch',
                  display: 'inline-block',
                  textAlign: 'center'
                }}>{einsatzzeit}</div>
              </div>
              <div style={{ width: 1, background: '#e6e6e6', margin: '6px 0', flexShrink: 0 }} />
              <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#9e9e9e', fontWeight: 500 }}>Abfrage in</div>
                <div style={{
                  fontSize: 34,
                  fontWeight: 800,
                  letterSpacing: 0,
                  lineHeight: 1.2,
                  color: !isOut && abfrageCountdown <= 0 ? (blink ? '#c00' : '#111') : (isOut ? '#888' : '#111'),
                  fontVariantNumeric: 'tabular-nums',
                  fontFeatureSettings: '"tnum"',
                  minWidth: '7ch',
                  display: 'inline-block',
                  textAlign: 'center'
                }}>{formatTimeSigned(abfrageCountdown)}</div>
              </div>
            </div>
          </>
        )}

        {isOut && !einsatzleiterMode && (
          <div style={{ display: 'flex', gap: 10, marginTop: 4, position: 'relative', zIndex: 30 }}>
            <button
              style={{ ...actionButtonStyle, background: '#1db954', color: '#fff', cursor: einsatzGeschlossen ? 'not-allowed' : 'pointer', opacity: einsatzGeschlossen ? 0.5 : 1 }}
              onClick={einsatzGeschlossen ? undefined : handleReactivate}
              disabled={einsatzGeschlossen}
            >
              REAKTIVIEREN
            </button>
            <button
              style={{ ...actionButtonStyle, background: '#c00', color: '#fff', cursor: einsatzGeschlossen ? 'not-allowed' : 'pointer', opacity: einsatzGeschlossen ? 0.5 : 1 }}
              onClick={einsatzGeschlossen ? undefined : () => onDelete(trupp.id)}
              disabled={einsatzGeschlossen}
              title="Trupp löschen"
            >
              LÖSCHEN
            </button>
          </div>
        )}
        {!isOut && !einsatzleiterMode && (
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            {trupp.aufgabe === 'Warten' || (!trupp.createdAt && !trupp.outOfService) ? (
              <>
                <button
                  style={{ ...actionButtonStyle, background: '#1db954', color: '#fff', cursor: 'pointer' }}
                  onClick={async () => await onStatus(trupp, resetAbfrage)}
                >
                  STARTEN
                </button>
                <button
                  style={{ ...actionButtonStyle, background: '#111', color: '#fff', cursor: 'pointer' }}
                  onClick={() => onBearbeiten(trupp)}
                >
                  BEARBEITEN
                </button>
              </>
            ) : trupp.mayday && onEndMayday && !einsatzleiterMode ? (
              <button
                style={{ ...actionButtonStyle, width: '100%', background: '#21ba45', color: '#fff', cursor: 'pointer' }}
                onClick={() => onEndMayday(trupp)}
                disabled={isOut}
              >
                MAYDAY BEENDEN
              </button>
            ) : (
              <>
                <button
                  style={{ ...actionButtonStyle, background: '#c00', color: '#fff', cursor: isOut ? 'not-allowed' : 'pointer' }}
                  onClick={() => onStatus(trupp, resetAbfrage)}
                  disabled={isOut}
                >
                  STATUSUPDATE
                </button>
                <button
                  style={{ ...actionButtonStyle, background: isOut ? '#888' : '#111', color: '#fff', cursor: isOut ? 'not-allowed' : 'pointer' }}
                  onClick={() => onOut(trupp.id)}
                  disabled={isOut}
                >
                  AUS DEM EINSATZ
                </button>
              </>
            )}
          </div>
        )}
      </div>
      </>
      )}
      {isOut && !isMobileViewport && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100%',
          borderRadius: 22,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 20,
          background: 'rgba(80,80,80,0.72)',
          flexDirection: 'column',
          gap: 6,
          margin: 0,
          padding: 8,
          pointerEvents: 'auto',
          textAlign: 'center',
        }}>
          <span style={{
            color: '#ffffff',
            fontWeight: 900,
            fontSize: 'clamp(18px, 4vw, 32px)',
            lineHeight: 1.15,
            letterSpacing: 1.2,
            textShadow: '0 2px 8px #222',
            maxWidth: '90%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>{trupp.name}</span>
          <span style={{
            color: '#ffffff',
            fontWeight: 900,
            fontSize: 'clamp(16px, 3.5vw, 28px)',
            lineHeight: 1.15,
            letterSpacing: 1.6,
            textTransform: 'uppercase',
            textShadow: '0 2px 8px #222',
            maxWidth: '92%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            Aus dem Einsatz
          </span>
        </div>
      )}
      </div>
      {showTileSettings && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onClick={() => setShowTileSettings(false)}
        >
          <div
            style={{ background: '#fff', borderRadius: 16, padding: 24, minWidth: 280, maxWidth: 360, boxShadow: '0 4px 32px #0002', display: 'flex', flexDirection: 'column', gap: 12 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ color: '#c00', fontWeight: 900, fontSize: 20, margin: 0, textAlign: 'center' }}>{trupp.name}</h3>
            <div style={{ fontSize: 14, color: '#666', textAlign: 'center', marginBottom: 4 }}>Trupp-Einstellungen</div>
            <button
              onClick={() => {
                setShowTileSettings(false);
                if (onRename) onRename(trupp);
              }}
              disabled={!onRename || einsatzGeschlossen}
              style={{
                background: !onRename || einsatzGeschlossen ? '#ccc' : '#111',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                padding: '12px 16px',
                fontWeight: 700,
                fontSize: 15,
                cursor: !onRename || einsatzGeschlossen ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}
            >
              <EditIcon style={{ fontSize: 18 }} />
              Truppname ändern
            </button>
            <button
              onClick={() => {
                setShowTileSettings(false);
                setShowDeleteTruppModal({ open: true, trupp });
              }}
              disabled={einsatzGeschlossen || isOut}
              style={{
                background: einsatzGeschlossen || isOut ? '#ccc' : '#c00',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                padding: '12px 16px',
                fontWeight: 700,
                fontSize: 15,
                cursor: einsatzGeschlossen || isOut ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}
            >
              <DeleteIcon style={{ fontSize: 18 }} />
              Trupp löschen
            </button>
            <button
              onClick={() => setShowTileSettings(false)}
              style={{ background: '#eee', color: '#222', border: 'none', borderRadius: 10, padding: '10px 16px', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Typen für Auditlog
type AuditLogEntry = {
  timestamp: number;
  type: string;
  message: string;
  shortDescription?: string;
  data?: any;
  performedBy?: string;    // Name des ausführenden Benutzers
  performedById?: number;  // ID des ausführenden Benutzers (wenn aus API-Liste)
};

interface EinsatzDetailProps {
  einsatzleiterMode: boolean;
  setEinsatzleiterMode: React.Dispatch<React.SetStateAction<boolean>>;
}

const EinsatzDetail: React.FC<EinsatzDetailProps> = ({ einsatzleiterMode, setEinsatzleiterMode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isNewOperation = location.search.includes('new=true');
  
  const [einsatz, setEinsatz] = React.useState<any>(null);
  const [trupps, setTrupps] = React.useState<Trupp[]>([]);
  const [draggingTruppId, setDraggingTruppId] = React.useState<number | null>(null);
  const [dropTargetTruppId, setDropTargetTruppId] = React.useState<number | null>(null);
  const truppsRef = React.useRef<Trupp[]>(trupps);
  truppsRef.current = trupps;
  const [modalOpen, setModalOpen] = React.useState(false);
  const [einsatzEditModalOpen, setEinsatzEditModalOpen] = React.useState(false);
  const [step, setStep] = React.useState(1);
  const [aufgabe, setAufgabe] = React.useState('');
  const [isEmergencyTrupp, setIsEmergencyTrupp] = React.useState(false);
  const [standort, setStandort] = React.useState('');
  const [startdruck, setStartdruck] = React.useState(300);
  const [funkkanal, setFunkkanal] = React.useState<string>('');
  const [funkkanalSelected, setFunkkanalSelected] = React.useState(false);
  const [mitglieder, setMitglieder] = React.useState<TruppMitglied[]>([]);
  const [truppfuehrerIndex, setTruppfuehrerIndex] = React.useState<number | null>(null);
  const [mitgliedVorname, setMitgliedVorname] = React.useState('');
  const [mitgliedNachname, setMitgliedNachname] = React.useState('');

  // Technischer Account: Benutzer-Identifikation
  const [showUserIdentModal, setShowUserIdentModal] = React.useState(false);
  const [realUserName, setRealUserName] = React.useState<string | null>(getRealUserDisplayName());
  const isTechAccount = isTechnicalAccount();

  // Alle Mitglieder aller Trupps im gesamten Einsatz (auch von anderen Benutzern)
  // Wird beim Öffnen der Mitgliederauswahl von der API geladen
  const [allOperationMembers, setAllOperationMembers] = React.useState<Set<string>>(new Set());
  // Alle Truppüberwacher des gesamten Einsatzes (auch von anderen Benutzern/Geräten)
  // Diese Benutzer dürfen nicht als Truppmitglieder ausgewählt werden
  const [allOperationSupervisorIds, setAllOperationSupervisorIds] = React.useState<Set<number>>(new Set());
  const [allOperationSupervisorKeys, setAllOperationSupervisorKeys] = React.useState<Set<string>>(new Set());

  // Trupp-Übergabe (Handover) bei Benutzerwechsel auf techn. Account
  const [showHandoverModal, setShowHandoverModal] = React.useState(false);
  const [handoverPreviousUserName, setHandoverPreviousUserName] = React.useState('');
  const [handoverNewIdentity, setHandoverNewIdentity] = React.useState<RealUserIdentity | null>(null);
  const [handoverTrupps, setHandoverTrupps] = React.useState<any[]>([]);
  // Vorherige Identität wird VOR dem Öffnen des UserIdentificationModal gespeichert,
  // weil das Modal setRealUserIdentity() aufruft bevor onConfirm() feuert
  const previousIdentityRef = React.useRef<RealUserIdentity | null>(null);

  // Kollektiver Funkkanalwechsel für eigene Trupps
  const [showBulkFunkkanalModal, setShowBulkFunkkanalModal] = React.useState(false);
  const [bulkFunkkanalSaving, setBulkFunkkanalSaving] = React.useState(false);

  // Schnell-Erstellung State - wird nur aus localStorage gelesen
  // Die API-Synchronisation erfolgt ausschließlich über GlobalSettingsModal
  const [quickTruppCreation, setQuickTruppCreation] = React.useState(() => {
    const saved = localStorage.getItem('quickTruppCreation');
    return saved === 'true';
  });
  const [truppNamingMode, setTruppNamingMode] = React.useState<'leader' | 'number'>(() => {
    const saved = localStorage.getItem('truppNamingMode');
    return saved === 'number' ? 'number' : 'leader';
  });
  const [orgShortName, setOrgShortName] = React.useState(() => localStorage.getItem('orgShortName') || '');
  const [truppNamePrefixEnabled, setTruppNamePrefixEnabled] = React.useState(
    () => localStorage.getItem('truppNamePrefixEnabled') === 'true'
  );
  const [statusUpdateScreens, setStatusUpdateScreens] = React.useState(() => {
    try {
      const selectedOrgID = localStorage.getItem('selectedOrgID') || sessionStorage.getItem('selectedOrgID');
      const raw = selectedOrgID
        ? localStorage.getItem(`statusUpdateScreens_${selectedOrgID}`)
        : localStorage.getItem('statusUpdateScreens');
      if (raw) return settingsAPI.normalizeStatusUpdateScreens(JSON.parse(raw));
    } catch {}
    return settingsAPI.DEFAULT_STATUS_UPDATE_SCREENS.map((s) => ({ ...s }));
  });
  const STATUS_SCREEN_STEP_MAP: Record<string, number> = {
    status: 1,
    standort: 2,
    aufgabe: 3,
    druck: 4,
    funkkanal: 5,
    feedback: 6,
  };
  const STATUS_STEP_LABELS: Record<number, string> = {
    1: 'Status',
    2: 'Standort',
    3: 'Aufgabe',
    4: 'Druck',
    5: 'Funk',
    6: 'Feedback',
  };
  const [renameTruppModal, setRenameTruppModal] = React.useState<{ open: boolean; trupp: Trupp | null }>({ open: false, trupp: null });
  const [renameTruppValue, setRenameTruppValue] = React.useState('');
  // Einstellung für den aktuell geöffneten Trupp-Erstellen-Dialog.
  // Wird beim Öffnen frisch aus der API geladen und bleibt dann stabil.
  const [quickTruppCreationForModal, setQuickTruppCreationForModal] = React.useState(false);

  // Schnell-Erstellung aus API laden (wichtig für Benutzer ohne Zugriff auf Settings-Modal)
  const loadQuickTruppCreationSetting = async (): Promise<boolean> => {
    const selectedOrgID = localStorage.getItem('selectedOrgID') || sessionStorage.getItem('selectedOrgID');
    const fallbackOrgValue = selectedOrgID ? localStorage.getItem(`quickTruppCreation_${selectedOrgID}`) : null;
    const fallbackGlobalValue = localStorage.getItem('quickTruppCreation');
    const fallbackValue = (fallbackOrgValue ?? fallbackGlobalValue) === 'true';
    console.log('⚙️ QUICK-TRUPP LOAD: Start', {
      selectedOrgID,
      fallbackOrgValue,
      fallbackGlobalValue,
      fallbackValue,
      online: navigator.onLine,
    });

    // Offline: lokale Einstellung verwenden
    if (!navigator.onLine) {
      setQuickTruppCreation(fallbackValue);
      console.log('⚙️ QUICK-TRUPP LOAD: Offline fallback used', { value: fallbackValue });
      return fallbackValue;
    }

    // orgID bevorzugt aus selectedOrgID, sonst aus JWT
    let orgID: number | null = null;
    if (selectedOrgID) {
      const parsed = parseInt(selectedOrgID, 10);
      if (!Number.isNaN(parsed) && parsed > 0) {
        orgID = parsed;
      }
    }

    if (!orgID) {
      try {
        const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (accessToken) {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          if (typeof payload.orgID === 'number' && payload.orgID > 0) {
            orgID = payload.orgID;
          }
        }
      } catch {}
    }

    if (!orgID) {
      setQuickTruppCreation(fallbackValue);
      console.log('⚙️ QUICK-TRUPP LOAD: No orgID, fallback used', { value: fallbackValue });
      return fallbackValue;
    }

    try {
      const response = await settingsAPI.getQuickTruppCreation(orgID);
      const enabled = response?.enabled === true;
      localStorage.setItem(`quickTruppCreation_${orgID}`, enabled.toString());
      localStorage.setItem('quickTruppCreation', enabled.toString());
      setQuickTruppCreation(enabled);
      console.log('⚙️ QUICK-TRUPP LOAD: API value applied', {
        orgID,
        response,
        enabled,
      });
      return enabled;
    } catch (error) {
      console.warn('Failed to load quick trupp creation setting in EinsatzDetail:', error);
      setQuickTruppCreation(fallbackValue);
      console.log('⚙️ QUICK-TRUPP LOAD: API failed, fallback used', {
        orgID,
        value: fallbackValue,
        error,
      });
      return fallbackValue;
    }
  };

  const loadStatusUpdateScreensSetting = async () => {
    const selectedOrgID = localStorage.getItem('selectedOrgID') || sessionStorage.getItem('selectedOrgID');
    const fallbackRaw = selectedOrgID
      ? localStorage.getItem(`statusUpdateScreens_${selectedOrgID}`)
      : localStorage.getItem('statusUpdateScreens');
    let fallback = settingsAPI.DEFAULT_STATUS_UPDATE_SCREENS.map((s) => ({ ...s }));
    try {
      if (fallbackRaw) fallback = settingsAPI.normalizeStatusUpdateScreens(JSON.parse(fallbackRaw));
    } catch {}

    if (!navigator.onLine) {
      setStatusUpdateScreens(fallback);
      return fallback;
    }

    let orgID: number | null = null;
    if (selectedOrgID) {
      const parsed = parseInt(selectedOrgID, 10);
      if (!Number.isNaN(parsed) && parsed > 0) orgID = parsed;
    }
    if (!orgID) {
      try {
        const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (accessToken) {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          if (typeof payload.orgID === 'number' && payload.orgID > 0) orgID = payload.orgID;
        }
      } catch {}
    }

    if (!orgID) {
      setStatusUpdateScreens(fallback);
      return fallback;
    }

    try {
      const response = await settingsAPI.getStatusUpdateScreens(orgID);
      const screens = settingsAPI.normalizeStatusUpdateScreens(response?.screens);
      localStorage.setItem(`statusUpdateScreens_${orgID}`, JSON.stringify(screens));
      localStorage.setItem('statusUpdateScreens', JSON.stringify(screens));
      setStatusUpdateScreens(screens);
      return screens;
    } catch (error) {
      console.warn('Failed to load status update screens setting:', error);
      setStatusUpdateScreens(fallback);
      return fallback;
    }
  };

  type StatusUpdateScreenConfig = { id: string; label: string; enabled: boolean; order: number };

  const getActiveStatusSteps = React.useCallback((opts?: {
    includeFeedback?: boolean;
    screens?: StatusUpdateScreenConfig[];
  }) => {
    const includeFeedback = opts?.includeFeedback !== false;
    const source = opts?.screens || statusUpdateScreens;
    const steps = source
      .filter((screen) => screen.enabled)
      .filter((screen) => includeFeedback || screen.id !== 'feedback')
      .map((screen) => STATUS_SCREEN_STEP_MAP[screen.id])
      .filter((step): step is number => typeof step === 'number');
    return steps.length > 0 ? steps : [STATUS_SCREEN_STEP_MAP.status || 1];
  }, [statusUpdateScreens]);

  const getStatusStepNeighbors = (currentStep: number, activeSteps: number[]) => {
    const idx = activeSteps.indexOf(currentStep);
    return {
      prev: idx > 0 ? activeSteps[idx - 1] : null,
      next: idx >= 0 && idx < activeSteps.length - 1 ? activeSteps[idx + 1] : null,
      isFirst: idx <= 0,
      isLast: idx === -1 || idx === activeSteps.length - 1,
    };
  };

  const loadTruppNamingModeSetting = async (): Promise<'leader' | 'number'> => {
    const selectedOrgID = localStorage.getItem('selectedOrgID') || sessionStorage.getItem('selectedOrgID');
    const fallbackOrgMode = selectedOrgID ? localStorage.getItem(`truppNamingMode_${selectedOrgID}`) : null;
    const globalMode = localStorage.getItem('truppNamingMode');
    const fallbackMode: 'leader' | 'number' = (fallbackOrgMode ?? globalMode) === 'number' ? 'number' : 'leader';

    if (!navigator.onLine) {
      setTruppNamingMode(fallbackMode);
      return fallbackMode;
    }

    let orgID: number | null = null;
    if (selectedOrgID) {
      const parsed = parseInt(selectedOrgID, 10);
      if (!Number.isNaN(parsed) && parsed > 0) orgID = parsed;
    }
    if (!orgID) {
      try {
        const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (accessToken) {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          if (typeof payload.orgID === 'number' && payload.orgID > 0) orgID = payload.orgID;
        }
      } catch {}
    }

    if (!orgID) {
      setTruppNamingMode(fallbackMode);
      return fallbackMode;
    }

    try {
      const response = await settingsAPI.getTruppNamingMode(orgID);
      const mode: 'leader' | 'number' = response?.mode === 'number' ? 'number' : 'leader';
      localStorage.setItem(`truppNamingMode_${orgID}`, mode);
      localStorage.setItem('truppNamingMode', mode);
      setTruppNamingMode(mode);
      return mode;
    } catch (error) {
      console.warn('Failed to load trupp naming mode in EinsatzDetail:', error);
      setTruppNamingMode(fallbackMode);
      return fallbackMode;
    }
  };

  const extractTruppNumber = (teamName: string | undefined): number | null => {
    return settingsAPI.extractTruppNumberFromName(teamName);
  };

  const buildPrefixedTruppName = (
    baseName: string,
    shortNameOverride?: string,
    prefixEnabledOverride?: boolean
  ): string => {
    return settingsAPI.applyTruppNamePrefix(
      baseName,
      shortNameOverride ?? orgShortName,
      prefixEnabledOverride ?? truppNamePrefixEnabled
    );
  };

  const loadOrgPrefixSettings = async (): Promise<{ shortName: string; prefixEnabled: boolean }> => {
    const selectedOrgID = localStorage.getItem('selectedOrgID') || sessionStorage.getItem('selectedOrgID');
    const fallbackShort =
      (selectedOrgID ? localStorage.getItem(`orgShortName_${selectedOrgID}`) : null) ||
      localStorage.getItem('orgShortName') ||
      '';
    const fallbackEnabled =
      ((selectedOrgID ? localStorage.getItem(`truppNamePrefixEnabled_${selectedOrgID}`) : null) ??
        localStorage.getItem('truppNamePrefixEnabled')) === 'true';

    const orgID = resolveEffectiveOrgID();
    if (!navigator.onLine || !orgID) {
      setOrgShortName(fallbackShort);
      setTruppNamePrefixEnabled(fallbackEnabled);
      return { shortName: fallbackShort, prefixEnabled: fallbackEnabled };
    }

    try {
      const [shortRes, prefixRes] = await Promise.all([
        settingsAPI.getOrgShortName(orgID),
        settingsAPI.getTruppNamePrefix(orgID),
      ]);
      const shortName = String(shortRes?.shortName || '').trim();
      const prefixEnabled = prefixRes?.enabled === true;
      localStorage.setItem(`orgShortName_${orgID}`, shortName);
      localStorage.setItem('orgShortName', shortName);
      localStorage.setItem(`truppNamePrefixEnabled_${orgID}`, prefixEnabled.toString());
      localStorage.setItem('truppNamePrefixEnabled', prefixEnabled.toString());
      setOrgShortName(shortName);
      setTruppNamePrefixEnabled(prefixEnabled);
      return { shortName, prefixEnabled };
    } catch (error) {
      console.warn('Failed to load org prefix settings:', error);
      setOrgShortName(fallbackShort);
      setTruppNamePrefixEnabled(fallbackEnabled);
      return { shortName: fallbackShort, prefixEnabled: fallbackEnabled };
    }
  };

  const resolveEffectiveOrgID = (): number | null => {
    const selectedOrgID = localStorage.getItem('selectedOrgID') || sessionStorage.getItem('selectedOrgID');
    if (selectedOrgID) {
      const parsed = parseInt(selectedOrgID, 10);
      if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    }
    try {
      const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      if (accessToken) {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        if (typeof payload.orgID === 'number' && payload.orgID > 0) return payload.orgID;
      }
    } catch {}
    return null;
  };

  const getNextOperationTruppNumber = async (): Promise<number> => {
    const numbers = new Set<number>();

    trupps.forEach((t) => {
      const n = extractTruppNumber(t.name);
      if (n !== null) numbers.add(n);
    });

    if (navigator.onLine && isAuthenticatedSync() && einsatz?.id) {
      try {
        const orgID = resolveEffectiveOrgID();
        if (orgID) {
          const allTeams = await teamAPI.getTeamsByOperation(orgID, einsatz.id);
          if (Array.isArray(allTeams)) {
            allTeams.forEach((team: any) => {
              const n = extractTruppNumber(team?.name);
              if (n !== null) numbers.add(n);
            });
          }
        }
      } catch (error) {
        console.warn('Failed to load all operation teams for numbering, using local fallback only:', error);
      }
    }

    return (numbers.size > 0 ? Math.max(...Array.from(numbers)) : 0) + 1;
  };

  // Überwache Änderungen der Schnell-Erstellung-Einstellung
  React.useEffect(() => {
    const handleStorageChange = () => {
        const saved = localStorage.getItem('quickTruppCreation');
        setQuickTruppCreation(saved === 'true');
        void loadTruppNamingModeSetting();
    };

    // Custom Event Listener für Änderungen im selben Tab (von GlobalSettingsModal)
    const handleQuickTruppCreationChanged = (e: CustomEvent<{ enabled: boolean }>) => {
      setQuickTruppCreation(e.detail.enabled);
    };
    const handleTruppNamingModeChanged = (e: CustomEvent<{ mode: 'leader' | 'number' }>) => {
      setTruppNamingMode(e.detail.mode);
    };
    const handleOrgShortNameChanged = (e: CustomEvent<{ shortName: string }>) => {
      setOrgShortName(String(e.detail?.shortName || '').trim());
    };
    const handleTruppNamePrefixChanged = (e: CustomEvent<{ enabled: boolean }>) => {
      setTruppNamePrefixEnabled(e.detail?.enabled === true);
    };

    // Event Listener für localStorage-Änderungen (andere Tabs)
    window.addEventListener('storage', handleStorageChange);
    
    // Auch bei Fokus auf das Fenster prüfen (falls in anderem Tab geändert)
    window.addEventListener('focus', handleStorageChange);
    
    // Custom Event für Änderungen im selben Tab
    window.addEventListener('quickTruppCreationChanged', handleQuickTruppCreationChanged as EventListener);
    window.addEventListener('truppNamingModeChanged', handleTruppNamingModeChanged as EventListener);
    window.addEventListener('orgShortNameChanged', handleOrgShortNameChanged as EventListener);
    window.addEventListener('truppNamePrefixChanged', handleTruppNamePrefixChanged as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleStorageChange);
      window.removeEventListener('quickTruppCreationChanged', handleQuickTruppCreationChanged as EventListener);
      window.removeEventListener('truppNamingModeChanged', handleTruppNamingModeChanged as EventListener);
      window.removeEventListener('orgShortNameChanged', handleOrgShortNameChanged as EventListener);
      window.removeEventListener('truppNamePrefixChanged', handleTruppNamePrefixChanged as EventListener);
    };
  }, []);

  React.useEffect(() => {
    void loadTruppNamingModeSetting();
    void loadStatusUpdateScreensSetting();
    void loadOrgPrefixSettings();
  }, []);

  // Dynamische Schritt-Berechnung für Schnell-Erstellung
  const effectiveQuickTruppCreation = modalOpen ? quickTruppCreationForModal : quickTruppCreation;
  // Bei genau 1 Mitglied entfällt die Truppführer-Auswahl (wird automatisch gesetzt)
  const skipLeaderSelection = mitglieder.length === 1;

  const getStepInfo = (stepNumber: number) => {
    if (!effectiveQuickTruppCreation) {
      // Normaler Prozess: 7 Schritte – Step 7 entfällt bei nur 1 Mitglied
      const lastStep = skipLeaderSelection ? 6 : 7;
      return {
        step: stepNumber,
        title: stepNumber === 1 ? 'Aufgabe wählen' :
               stepNumber === 2 ? 'Trupp-Typ' :
               stepNumber === 3 ? 'Standort' :
               stepNumber === 4 ? 'Startdruck (Bar)' :
               stepNumber === 5 ? 'Funkkanal' :
               stepNumber === 6 ? 'Mitglieder hinzufügen' :
               stepNumber === 7 ? 'Truppführer wählen' : '',
        nextStep: stepNumber < lastStep ? stepNumber + 1 : null,
        prevStep: stepNumber > 1 ? stepNumber - 1 : null
      };
    } else {
      // Schnell-Erstellung: Schritte 1, 2, 6 (, 7) – Aufgabe/Standort/Funkkanal übersprungen
      const quickSteps = skipLeaderSelection ? [1, 2, 6] : [1, 2, 6, 7];
      const currentIndex = quickSteps.indexOf(stepNumber);
      return {
        step: stepNumber,
        title: stepNumber === 1 ? 'Trupp-Typ' :
               stepNumber === 2 ? 'Startdruck (Bar)' :
               stepNumber === 6 ? 'Mitglieder hinzufügen' :
               stepNumber === 7 ? 'Truppführer wählen' : '',
        nextStep: currentIndex < quickSteps.length - 1 ? quickSteps[currentIndex + 1] : null,
        prevStep: currentIndex > 0 ? quickSteps[currentIndex - 1] : null
      };
    }
  };

  // Navigation-Funktionen
  const goToNextStep = () => {
    // Von Mitglieder → bei 1 Person direkt anlegen
    if (step === 6 && skipLeaderSelection) {
      setTruppfuehrerIndex(0);
      void handleSaveTrupp(0);
      return;
    }
    const stepInfo = getStepInfo(step);
    if (stepInfo.nextStep) {
      setStep(stepInfo.nextStep);
    }
  };

  const goToPrevStep = () => {
    const stepInfo = getStepInfo(step);
    if (stepInfo.prevStep) {
      setStep(stepInfo.prevStep);
    }
  };

  // Prüft ob ein Trupp fehlende Daten hat (für Schnell-Erstellung)
  const hasMissingData = (trupp: Trupp) => {
    const hasTask = Boolean(
      (typeof trupp.originalAufgabe === 'string' && trupp.originalAufgabe.trim() !== '') ||
      (typeof trupp.aufgabe === 'string' && trupp.aufgabe.trim() !== '' && trupp.aufgabe !== 'Warten')
    );
    return !trupp.standort || !trupp.funkkanal || !hasTask;
  };

  // Erster Screen laut Portal-Konfiguration (nicht mehr hart Status=1)
  const getFirstConfiguredStatusStep = (opts?: {
    includeFeedback?: boolean;
    screens?: StatusUpdateScreenConfig[];
  }) => {
    const activeSteps = getActiveStatusSteps(opts);
    return activeSteps[0] || STATUS_SCREEN_STEP_MAP.status || 1;
  };

  // Bestimmt den ersten Schritt für das Status-Modal basierend auf Config + fehlenden Daten
  const getInitialStatusStep = (
    trupp: Trupp,
    skipFeedback = false,
    screensOverride?: StatusUpdateScreenConfig[]
  ) => {
    const activeSteps = getActiveStatusSteps({
      includeFeedback: !skipFeedback,
      screens: screensOverride,
    });
    const firstConfigured = activeSteps[0] || STATUS_SCREEN_STEP_MAP.status || 1;

    // Normale Statusabfrage / Quick ohne Lücken: immer mit konfiguriertem erstem Screen starten
    if (skipFeedback || !hasMissingData(trupp)) {
      return firstConfigured;
    }

    const hasTask = Boolean(
      (typeof trupp.originalAufgabe === 'string' && trupp.originalAufgabe.trim() !== '') ||
      (typeof trupp.aufgabe === 'string' && trupp.aufgabe.trim() !== '' && trupp.aufgabe !== 'Warten')
    );

    // Nur bei fehlenden Pflichtfeldern (z. B. Schnell-Erstellung) gezielt dorthin springen
    if (!trupp.standort && activeSteps.includes(STATUS_SCREEN_STEP_MAP.standort)) return STATUS_SCREEN_STEP_MAP.standort;
    if (!hasTask && activeSteps.includes(STATUS_SCREEN_STEP_MAP.aufgabe)) return STATUS_SCREEN_STEP_MAP.aufgabe;
    if (!trupp.funkkanal && activeSteps.includes(STATUS_SCREEN_STEP_MAP.funkkanal)) return STATUS_SCREEN_STEP_MAP.funkkanal;
    return firstConfigured;
  };

  // NFC-related state variables
  const [nfcStatus, setNfcStatus] = React.useState<'checking' | 'available' | 'unavailable'>('checking');
  const [isScanning, setIsScanning] = React.useState(false);
  const [localUsers, setLocalUsers] = React.useState<User[]>([]);
  const [autocompleteValue, setAutocompleteValue] = React.useState('');
  const [autocompleteSuggestions, setAutocompleteSuggestions] = React.useState<User[]>([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [nfcScanResult, setNfcScanResult] = React.useState<{ success: boolean; message: string } | null>(null);
  const [scannedTags, setScannedTags] = React.useState<Set<string>>(new Set());
  const [continuousNFCStop, setContinuousNFCStop] = React.useState<(() => void) | null>(null);

  // Status-Dialog
  const [statusDialog, setStatusDialog] = React.useState<{ open: boolean; trupp: Trupp | null; resetAbfrage?: () => void; isReactivation?: boolean; isQuickTrupp?: boolean }>({ open: false, trupp: null, isReactivation: false });
  const [statusStep, setStatusStep] = React.useState(1);
  const [statusOk, setStatusOk] = React.useState(true);
  const [editStandort, setEditStandort] = React.useState('');
  const [editAufgabe, setEditAufgabe] = React.useState('');
  const [editDruck, setEditDruck] = React.useState(300);
  const [selectedFeedback, setSelectedFeedback] = React.useState('');

  // Einsatz Edit Modal State
  const [editEinsatzTyp, setEditEinsatzTyp] = React.useState<string>('');
  const [editEinsatzOrt, setEditEinsatzOrt] = React.useState<string>('');
  const [editEinsatzCategoryID, setEditEinsatzCategoryID] = React.useState<number | null>(null);
  const [operationsTypes, setOperationsTypes] = React.useState<any[]>([]);

  // Mitglieder-Dialog
  const [mitgliederDialog, setMitgliederDialog] = React.useState<{ open: boolean; trupp: Trupp | null }>({ open: false, trupp: null });
  const [editMitglieder, setEditMitglieder] = React.useState<TruppMitglied[]>([]);
  const [editTruppfuehrerIndex, setEditTruppfuehrerIndex] = React.useState<number | null>(null);
  // State für Autocomplete im Mitglieder-Dialog
  const [mitgliederAutocompleteValue, setMitgliederAutocompleteValue] = React.useState('');
  const [mitgliederAutocompleteUsers, setMitgliederAutocompleteUsers] = React.useState<User[]>([]);
  const [mitgliederAutocompleteShowList, setMitgliederAutocompleteShowList] = React.useState(false);
  const [mitgliederAutocompleteSelectedUser, setMitgliederAutocompleteSelectedUser] = React.useState<User | null>(null);

  // Auditlog State
  const [auditlog, setAuditlog] = React.useState<AuditLogEntry[]>([]);
  
  // Trupp Auditlog Modal State
  const [showTruppAuditlog, setShowTruppAuditlog] = React.useState(false);

  // API Integration for Teams and Audit Logs
  const [isLoadingTeams, setIsLoadingTeams] = React.useState(false);
  const [isLoadingAuditLog, setIsLoadingAuditLog] = React.useState(false);

  // Load teams from API
  const loadTeamsFromAPI = async (opsID: number, einsatzData?: any, einsatzleiterMode?: boolean) => {
    const isAuth = isAuthenticatedSync();
    if (!isAuth) return;
    
    // Check if we're online before making API calls
    if (!navigator.onLine) {
  
      return;
    }

    
    try {
      setIsLoadingTeams(true);
      setTruppsLoading(true); // Neuer Loading-State
      setTruppsError(null); // Fehler zurücksetzen
      
      // Clear any cached teams data to ensure fresh data
      if ('caches' in window) {
        try {
          // Clear all caches that might contain teams data
          const allCacheNames = await caches.keys();
          for (const cacheName of allCacheNames) {
            const cache = await caches.open(cacheName);
            const requests = await cache.keys();
            const teamsRequests = requests.filter(request => 
              request.url.includes('/api/v1/teams/')
            );
            await Promise.all(teamsRequests.map(request => cache.delete(request)));

          }
        } catch (error) {
          console.warn('Failed to clear cache:', error);
        }
      }
      
      // Im Trupp Übersicht Modus alle Teams der Organisation laden, sonst nur die des Users
      let teams;
      if (einsatzleiterMode) {
        
        // Verwende den neuen Endpoint für alle Teams des Einsatzes
        const accessToken = await getAccessTokenAsync();
        let orgID = null;
        
        if (accessToken) {
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            orgID = payload.orgID;
          } catch (error) {
            console.error('❌ Fehler beim Decodieren des Tokens:', error);
            return;
          }
        }
        
        if (!orgID) {
          console.error('❌ Keine orgID im Token gefunden');
          return;
        }
        
                  try {
            const apiTeams = await teamAPI.getTeamsByOperation(orgID, opsID);
            
            // Debug: Zeige alle Trupp-Daten
            console.log('🔍 API Teams loaded:', apiTeams);
            console.log('🔍 Number of API teams:', apiTeams.length);
            
            if (apiTeams.length > 0) {
              console.log('🔍 First API team structure:', apiTeams[0]);
              console.log('🔍 All API team fields:', Object.keys(apiTeams[0]));
            }
            
            // Debug: Prüfe welche Teams vom lokalen Benutzer erstellt wurden
            const accessToken = await getAccessTokenAsync();
            let currentUserID: number | null = null;
            
            if (accessToken) {
              try {
                const payload = JSON.parse(atob(accessToken.split('.')[1]));
                currentUserID = payload.userID;
                console.log('🔍 Current User ID from token:', currentUserID);
              } catch (error) {
                console.error('❌ Fehler beim Decodieren des Tokens für userID:', error);
              }
            }
            
            // Debug: Zeige alle Trupp-Daten mit creatorID und supervisorName
            apiTeams.forEach((team: any, index: number) => {
              console.log(`🔍 Team ${index + 1}:`, {
                id: team.id,
                name: team.name,
                creatorID: team.creatorID,
                creatorIDType: typeof team.creatorID,
                supervisorName: team.supervisorName,
                supervisorNameType: typeof team.supervisorName,
                allFields: Object.keys(team)
              });
            });
            
              apiTeams.forEach((team: any, index: number) => {
                const isLocalUserTeam = team.creatorID === currentUserID;
              });
          
          // Lade auch lokale Teams und kombiniere sie
          const localEinsaetze = JSON.parse(localStorage.getItem('einsaetze') || '[]');
          const localTeams = localEinsaetze.flatMap((e: any) => e.trupps || []);
          
          // Debug: Zeige lokale Teams
          console.log('🔍 Local teams loaded:', localTeams);
          console.log('🔍 Number of local teams:', localTeams.length);
          
          if (localTeams.length > 0) {
            console.log('🔍 First local team structure:', localTeams[0]);
            console.log('🔍 All local team fields:', Object.keys(localTeams[0]));
          }
          
          // Debug: Zeige alle lokalen Trupp-Daten mit creatorID und supervisorName
          localTeams.forEach((team: any, index: number) => {
            console.log(`🔍 Local Team ${index + 1}:`, {
              id: team.id,
              name: team.name,
              creatorID: team.creatorID,
              creatorIDType: typeof team.creatorID,
              supervisorName: team.supervisorName,
              supervisorNameType: typeof team.supervisorName,
              allFields: Object.keys(team)
            });
          });
          
          // Kombiniere API-Teams und lokale Teams, entferne Duplikate
          const allTeams = [...apiTeams];
          localTeams.forEach((localTeam: any) => {
            const exists = allTeams.some((apiTeam: any) => apiTeam.id === localTeam.id);
            if (!exists) {
              // Konvertiere lokales Team zu API-Format
              const convertedLocalTeam = {
                id: localTeam.id,
                name: localTeam.name,
                task: localTeam.aufgabe,
                location: localTeam.standort,
                startPressure: localTeam.startdruck,
                currentPressure: localTeam.currentPressure || localTeam.startdruck,
                radioChannel: localTeam.funkkanal,
                teamLeaderIndex: localTeam.truppfuehrerIndex,
                createdAt: localTeam.createdAt,
                started_At: localTeam.startedAt,
                outOfService: localTeam.outOfService || false,
                emergencyTrupp: localTeam.emergencyTrupp || 0,
                statusOk: localTeam.statusOk,
                mayday: localTeam.mayday || false,
                originalTask: localTeam.originalAufgabe,
                closed: localTeam.geschlossen || false,
                creatorID: localTeam.creatorID, // WICHTIG: creatorID übertragen
                supervisorName: localTeam.supervisorName, // WICHTIG: supervisorName übertragen
                abfrageCountdown: 300, // Immer 5 Minuten
                abfrageResetAt: localTeam.abfrageResetAt,
                pausedAt: localTeam.pausedAt,
                pausedElapsed: localTeam.pausedElapsed,
                einsatzCount: localTeam.einsatzCount || 1,
                members: localTeam.mitglieder.map((m: any) => ({
                  firstName: m.vorname,
                  lastName: m.nachname,
                  pressure: m.druck
                }))
              };
              allTeams.push(convertedLocalTeam);
            }
          });
          
          teams = allTeams;
        } catch (error) {
          console.error('❌ Fehler beim Laden der Teams im Trupp Übersicht Modus:', error);
          console.error('❌ OrgID:', orgID, 'OpsID:', opsID);
          return;
        }
      } else {
        // Bei technischen Accounts: supervisorID (echte Person) an API übergeben,
        // damit nur die Teams der echten Person geladen werden
        if (isTechAccount) {
          const realUser = getRealUserIdentity();
          if (realUser?.id) {
            console.log('👤 loadTeamsFromAPI: Technischer Account → lade Teams für echte Person:', realUser.id);
            teams = await teamAPI.getTeams(opsID, realUser.id);
          } else {
            console.warn('⚠️ loadTeamsFromAPI: Technischer Account ohne echte Person → lade alle Teams des Token-Users');
            teams = await teamAPI.getTeams(opsID);
          }
        } else {
          teams = await teamAPI.getTeams(opsID);
        }
      }
      
      // Prüfe ob teams ein Array oder Objekt ist und konvertiere bei Bedarf
      const teamsArray = Array.isArray(teams) ? teams : Object.values(teams);
      
      // Convert API teams to Trupp format
      const convertedTeams: PromiseSettledResult<Trupp>[] = await Promise.allSettled(
        teamsArray.map(async (team: any, index: number) => {
          
          
          // Verwende Mitglieder direkt aus der API-Antwort, falls vorhanden
          let members = [];
          if (team.members && Array.isArray(team.members) && team.members.length > 0) {
            members = team.members;
          } else {
            try {
              const apiMembers = await teamAPI.getTeamMembers(team.id);
              if (apiMembers && Array.isArray(apiMembers) && apiMembers.length > 0) {
          
                members = apiMembers;
              } else {
                throw new Error('Keine Mitglieder in API-Antwort');
              }
            } catch (error) {
              console.warn(`⚠️ Fehler beim Laden der Mitglieder für Team ${team.name} (ID: ${team.id}):`, error);
              console.warn(`⚠️ Versuche lokale Mitglieder als Fallback...`);
              
              // Fallback: Versuche lokale Mitglieder zu finden
              const localTeams = JSON.parse(localStorage.getItem('einsaetze') || '[]');
              const localTeam = localTeams.flatMap((e: any) => e.trupps || []).find((t: any) => t.id === team.id);
              if (localTeam && localTeam.mitglieder && localTeam.mitglieder.length > 0) {
                members = localTeam.mitglieder.map((m: any) => ({
                  firstName: m.vorname,
                  lastName: m.nachname,
                  pressure: m.druck
                }));
              } else {
                console.warn(`⚠️ Keine lokalen Mitglieder gefunden für Team ${team.name}`);
                members = [];
              }
            }
          }
          
          // Determine if team is active (not out of service, not closed, and has active timers)
          const hasActiveTimers = team.createdAt && !team.outOfService;
          const isActive = !team.outOfService && !team.closed && hasActiveTimers;
          
          // Use the actual task from database, preserve original task even when out of service
          const task = team.task || 'Sicherung';
          
          // Set createdAt only if team is active and has a start time
          // Im Trupp Übersicht Modus verwenden wir die API-Zeit, aber normalisieren sie
          let createdAt = undefined;
          if (!team.outOfService && team.createdAt) {
            // Konvertiere API-Zeit zu lokaler Zeit für Konsistenz
            const apiTime = new Date(team.createdAt).getTime();
            createdAt = apiTime;
          }
          
          // Set startedAt from API response
          let startedAt = undefined;
          if (!team.outOfService) {
            if (team.started_At) {
              // Konvertiere API-Zeit zu lokaler Zeit für Konsistenz
              const apiStartedTime = new Date(team.started_At).getTime();
              startedAt = apiStartedTime;
            } else if (team.startedAt) {
              // Fallback für camelCase
              const apiStartedTime = new Date(team.startedAt).getTime();
              startedAt = apiStartedTime;
            } else if (team.createdAt && team.task !== 'Warten') {
              // Fallback: Verwende createdAt als startedAt für Teams, die nicht im Wartemodus sind
              const apiCreatedTime = new Date(team.createdAt).getTime();
              startedAt = apiCreatedTime;
            }
          } 
          

          
          // Zusätzlich: Prüfe ob es eine lokale Version des Teams gibt und verwende diese Zeit
          // Immer lokale Zeit bevorzugen, wenn verfügbar (sowohl im Normal- als auch Trupp Übersicht Modus)
          // ABER: Nicht für outOfService Teams, da diese keine aktiven Zeiten haben sollten
          let localEinsatzCount = Number(team.einsatzCount) || 0;
          if (team.id) {
            const localTeams = JSON.parse(localStorage.getItem('einsaetze') || '[]');
            const remoteTeams = JSON.parse(localStorage.getItem('remoteEinsaetze') || '[]');
            const localTeam = [...localTeams, ...remoteTeams]
              .flatMap((e: any) => e.trupps || [])
              .find((t: any) => Number(t.id) === Number(team.id));
            if (!team.outOfService) {
              if (localTeam && localTeam.createdAt) {
                // Verwende die lokale Zeit für Konsistenz
                createdAt = localTeam.createdAt;
              }
              if (localTeam && localTeam.startedAt) {
                // Verwende die lokale Zeit für Konsistenz
                startedAt = localTeam.startedAt;
              }
            }
            if (localTeam?.einsatzCount) {
              localEinsatzCount = Math.max(localEinsatzCount, Number(localTeam.einsatzCount) || 0);
            }
            const persistedCounts = readTruppEinsatzCounts(opsID);
            const persistedCount = Number(persistedCounts[String(team.id)]) || 0;
            if (persistedCount > 0) {
              localEinsatzCount = Math.max(localEinsatzCount, persistedCount);
            }
          }
          
          // Get team's start pressure, with fallback
          const teamStartPressure = team.startPressure || 300;
          
          const finalTeam = {
            id: team.id,
            aufgabe: task, // Always preserve the original task, don't change to 'Warten'
            standort: team.location || '',
            startdruck: teamStartPressure,
            currentPressure: team.currentPressure || teamStartPressure, // Neues Feld laden
            funkkanal: team.radioChannel || '',
            mitglieder: (() => {
              // Entferne Duplikate basierend auf firstName + lastName
              const uniqueMembers = new Map();
              (members || []).forEach((member: any) => {
                const key = `${member.firstName}-${member.lastName}`;
                if (!uniqueMembers.has(key)) {
                  uniqueMembers.set(key, {
              vorname: member.firstName || '',
              nachname: member.lastName || '',
              druck: teamStartPressure
                  });
                }
              });
              
              const processedMembers = Array.from(uniqueMembers.values());
              
 
              
              return processedMembers;
            })(),
            truppfuehrerIndex: (() => {
              const leaderIndex = team.teamLeaderIndex || team.truppfuehrerIndex || 0;
              const memberCount = (members || []).length;
              
              // Validierung: Truppführer-Index darf nicht größer als Mitglieder-Anzahl sein
              const validLeaderIndex = memberCount > 0 ? Math.min(leaderIndex, memberCount - 1) : 0;
              
              // Debug für Truppführer-Index
              if (einsatzleiterMode) {
                const accessToken = localStorage.getItem('accessToken');
                let currentUserID: number | null = null;
                
                if (accessToken) {
                  try {
                    const payload = JSON.parse(atob(accessToken.split('.')[1]));
                    currentUserID = payload.userID;
                  } catch (error) {
                    console.error('❌ Fehler beim Decodieren des Tokens für userID:', error);
                  }
                }
                
                const isLocalUserTeam = team.creatorID === currentUserID;
              
              }
              
              return validLeaderIndex;
            })(),
            name: team.name,
            createdAt: createdAt,
            startedAt: startedAt,
            outOfService: team.outOfService || false,
            abfrageCountdown: 300, // Immer 5 Minuten – API könnte noch alten Wert (60) liefern
            abfrageResetAt: isActive ? (team.abfrageResetAt || createdAt) : undefined,
            statusOk: team.statusOk !== false,
            mayday: team.mayday || false,
            maydayTriggeredBy: team.maydayTriggeredBy,
            maydayTriggeredAt: team.maydayTriggeredAt,
            emergencyTrupp: team.emergencyTrupp || 0,
            creator: team.creator, // Creator Objekt hinzufügen
            creatorID: team.creatorID, // WICHTIG: creatorID hinzufügen
            supervisorName: team.supervisorName, // WICHTIG: supervisorName hinzufügen
            originalAufgabe: team.originalTask || task,
            geschlossen: team.closed || false,
            einsatzCount: Math.max(localEinsatzCount, 1),
            // Pause-related fields
            pausedAt: team.pausedAt,
            pausedElapsed: team.pausedElapsed
          };
          
          
          
          return finalTeam;
        })
      );
      
      // Verarbeite Promise.allSettled Ergebnisse
      const successfulTeams: Trupp[] = [];
      const failedTeams: any[] = [];
      
      convertedTeams.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successfulTeams.push(result.value);
        } else {
          failedTeams.push({ index, error: result.reason });
          console.error(`❌ Team ${index + 1} Konvertierung fehlgeschlagen:`, result.reason);
        }
      });
      
      
      const sortedTeams = applyTruppOrder(successfulTeams, opsID);
      
      // Filter out locally deleted teams
      const locallyDeletedTeams = JSON.parse(localStorage.getItem('locallyDeletedTeams') || '[]');
      const locallyDeletedTeamIds = locallyDeletedTeams.map((team: any) => team.id);
      const filteredTeams = sortedTeams.filter(team => !locallyDeletedTeamIds.includes(team.id));
      
      
      
      // Im Trupp Übersicht Modus NICHT in localStorage speichern, nur State setzen
      if (!localEinsatzleiterMode) {
      saveTeamsToLocalStorage(filteredTeams, einsatzData);
      }
      
      // Debug: Zeige finale Trupp-Objekte
      console.log('🔍 Final filtered teams:', filteredTeams);
      filteredTeams.forEach((team: any, index: number) => {
        console.log(`🔍 Final Team ${index + 1}:`, {
          id: team.id,
          name: team.name,
          creatorID: team.creatorID,
          supervisorName: team.supervisorName,
          supervisorNameType: typeof team.supervisorName,
          allFields: Object.keys(team)
        });
      });
      
      // Update state
    
      setTrupps(filteredTeams);
      
      // Mayday-Benachrichtigungen werden von MaydayNotificationManager verwaltet
    } catch (error) {
      console.error('Error loading teams from API:', error);
      setTruppsError(error instanceof Error ? error.message : 'Unbekannter Fehler');
    } finally {
      setIsLoadingTeams(false);
      setTruppsLoading(false); // Neuen Loading-State beenden
      // Einsatzleiter-Loading beenden
      setEinsatzleiterLoading(false);
    }
  };

  // Load audit log from API
  const loadAuditLogFromAPI = async (opsID: number) => {
    
    // Check if we're online before making API calls
    if (!navigator.onLine) {
      return;
    }
    
    try {
      setIsLoadingAuditLog(true);
      const logs = await auditLogAPI.getAuditLog(opsID);
      
      console.log(`📋 Audit-Logs von API geladen: ${logs.length} Einträge`);
      if (logs.length > 0) {
        console.log('📋 Erstes API-Log (Rohformat):', JSON.stringify(logs[0], null, 2));
        console.log('📋 Verfügbare Felder im API-Log:', Object.keys(logs[0]));
      }
      
      // Convert API logs to local format
      const convertedLogs: AuditLogEntry[] = logs.map((log: any) => {
        // Handle different timestamp formats from API
        let timestamp: number;
        
        if (log.created_at) {
          if (typeof log.created_at === 'string') {
            timestamp = new Date(log.created_at).getTime();
          } else if (typeof log.created_at === 'number') {
            timestamp = log.created_at;
          } else {
            timestamp = Date.now();
          }
        } else {
          timestamp = Date.now();
        }
        
        // Handle logData field properly
        let data: any = undefined;
        if (log.logData) {
          if (typeof log.logData === 'string') {
            try {
              data = JSON.parse(log.logData);
            } catch (e) {
              console.warn('Failed to parse logData as JSON:', log.logData);
              data = { raw: log.logData };
            }
          } else {
            data = log.logData;
          }
        }
        
        // Sicherstellen, dass truppId im data-Objekt vorhanden ist
        // Die API liefert teamID als separates Feld – dieses muss in data.truppId übernommen werden
        if (log.teamID) {
          if (!data) {
            data = {};
          }
          if (!data.truppId) {
            data.truppId = log.teamID;
          }
        }
        
        return {
          timestamp,
          type: log.logType || 'Info',
          message: log.message || '',
          shortDescription: log.shortDescription,
          data: data,
          performedBy: data?.performedBy,
          performedById: data?.performedById,
        };
      });
      
      console.log(`📋 Konvertierte Logs: ${convertedLogs.length} Einträge`);
      if (convertedLogs.length > 0) {
        console.log('📋 Erstes konvertiertes Log:', JSON.stringify(convertedLogs[0], null, 2));
        const logsWithTruppId = convertedLogs.filter(l => l.data?.truppId);
        console.log(`📋 Logs mit truppId: ${logsWithTruppId.length} von ${convertedLogs.length}`);
      }
      
      // Lokale Audit-Logs prüfen: nur noch nicht synchronisierte Einträge berücksichtigen
      const localAuditLogs: any[] = JSON.parse(localStorage.getItem('auditlog') || '[]');
      const unsyncedLocalLogs = localAuditLogs.filter((log: any) => !log._synced);
      console.log(`📋 Lokale Audit-Logs: ${localAuditLogs.length} gesamt, ${unsyncedLocalLogs.length} nicht synchronisiert`);
      
      // API-Logs sind die Hauptquelle. Nur wirklich unsynced lokale Logs hinzufügen.
      const allLogs = [...convertedLogs];
      
      // Nur unsynced lokale Logs hinzufügen, die noch nicht auf der API sind
      for (const localLog of unsyncedLocalLogs) {
        const isDuplicate = convertedLogs.some(apiLog => 
          Math.abs(apiLog.timestamp - localLog.timestamp) < 2000 && // 2 Sekunden Toleranz
          apiLog.message === localLog.message &&
          apiLog.type === localLog.type
        );
        
        if (!isDuplicate) {
          allLogs.push(localLog);
        }
      }
      
      // Sort all logs by timestamp (oldest first) for consistent PDF generation
      allLogs.sort((a, b) => a.timestamp - b.timestamp);
      
      // Nach erfolgreichem API-Load: localStorage leeren (API ist Source of Truth)
      // Bereits synchronisierte Einträge brauchen wir nicht mehr lokal.
      if (convertedLogs.length > 0 && localAuditLogs.length > 0) {
        // Nur unsynced Logs behalten, die noch nicht auf der API waren
        const remainingLogs = unsyncedLocalLogs.filter(localLog => {
          const isOnApi = convertedLogs.some(apiLog => 
            Math.abs(apiLog.timestamp - localLog.timestamp) < 2000 &&
            apiLog.message === localLog.message &&
            apiLog.type === localLog.type
          );
          return !isOnApi; // Nur behalten, wenn noch nicht auf der API
        });
        
        if (remainingLogs.length > 0) {
          localStorage.setItem('auditlog', JSON.stringify(remainingLogs));
          console.log(`📋 ${remainingLogs.length} unsynced Logs verbleiben im localStorage`);
        } else {
          localStorage.removeItem('auditlog');
          console.log('🧹 localStorage-Auditlogs geleert (alle auf API vorhanden)');
        }
      }
      
      setAuditlog(allLogs);
    } catch (error) {
      console.error('❌ Error loading audit log from API:', error);
      
      // Fallback to local logs only
      const localAuditLogs = JSON.parse(localStorage.getItem('auditlog') || '[]');
      setAuditlog(localAuditLogs);
    } finally {
      setIsLoadingAuditLog(false);
    }
  };

  // Save team to API
  const saveTeamToAPI = async (trupp: Trupp) => {
    if (!einsatz) throw new Error('No operation available');
    
    // Check if we're online before making API calls
    if (!navigator.onLine) {

      // Save to localStorage and return a mock response
      const mockTeam = { ...trupp, id: Math.floor(Math.random() * 1000000) + 1000000 };
      return mockTeam;
    }
    
    // Get userID from JWT token
    const accessToken = localStorage.getItem('accessToken');
    let userID = null;
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        userID = payload.id;
      } catch (error) {
        console.error('Error parsing access token:', error);
      }
    }
    
    // Bei technischem Account: echte Person als creatorID verwenden
    let effectiveCreatorID = userID;
    if (isTechAccount) {
      const realUser = getRealUserIdentity();
      if (realUser?.id) {
        effectiveCreatorID = realUser.id;
        console.log('👤 saveTeamToAPI: Technischer Account → creatorID ist echte Person:', effectiveCreatorID);
      } else {
        console.warn('⚠️ saveTeamToAPI: Technischer Account, aber kein echter Benutzer → Fallback auf Token-User');
      }
    }
    
    // Check if userID is available (required for creatorID)
    if (!effectiveCreatorID) {
      throw new Error('User ID not available from token');
    }
    
    try {
      const teamData = {
        opsID: einsatz.id,
        name: trupp.name,
        task: trupp.aufgabe,
        location: trupp.standort,
        startPressure: trupp.startdruck,
        currentPressure: trupp.currentPressure || trupp.startdruck,
        radioChannel: trupp.funkkanal || '',
        teamLeaderIndex: trupp.truppfuehrerIndex,
        creatorID: effectiveCreatorID,
        emergencyTrupp: trupp.emergencyTrupp || 0,
        outOfService: trupp.outOfService || false,
        statusOk: trupp.statusOk,
        mayday: trupp.mayday || false,
        maydayTriggeredBy: trupp.maydayTriggeredBy,
        maydayTriggeredAt: trupp.maydayTriggeredAt,
        closed: trupp.geschlossen || false,
        // Time-related fields
        createdAt: trupp.createdAt,
        started_At: trupp.startedAt,
        abfrageCountdown: trupp.abfrageCountdown,
        abfrageResetAt: trupp.abfrageResetAt,
        // Pause-related fields
        pausedAt: trupp.pausedAt,
        pausedElapsed: trupp.pausedElapsed,
        // Original task for resume functionality
        originalTask: trupp.originalAufgabe,
        einsatzCount: trupp.einsatzCount || 1,
        // Include members data to update their pressure values
        members: trupp.mitglieder.map(member => ({
          firstName: member.vorname,
          lastName: member.nachname,
          pressure: member.druck || trupp.startdruck
        }))
      };
      
      const response = await teamAPI.createTeam(teamData);
      return response;
    } catch (error) {
      console.error('Error saving team to API:', error);
      throw error;
    }
  };

  // Update team in API
  const updateTeamInAPI = async (trupp: Trupp) => {
    // Check if we're online before making API calls
    if (!navigator.onLine) {

      return;
    }
    
    try {
      const updateData: Record<string, any> = {
        name: trupp.name,
        task: trupp.aufgabe,
        location: trupp.standort,
        startPressure: trupp.startdruck,
        currentPressure: trupp.currentPressure,
        radioChannel: trupp.funkkanal,
        teamLeaderIndex: trupp.truppfuehrerIndex,
        members: trupp.mitglieder.map(member => ({
          firstName: member.vorname,
          lastName: member.nachname,
          pressure: member.druck
        })),
        outOfService: trupp.outOfService,
        emergencyTrupp: trupp.emergencyTrupp || 0,
        started_At: trupp.startedAt,
        createdAt: trupp.createdAt, // Add createdAt to ensure time is preserved
        abfrageCountdown: trupp.abfrageCountdown,
        abfrageResetAt: trupp.abfrageResetAt,
        statusOk: trupp.statusOk,
        mayday: trupp.mayday,
        maydayTriggeredBy: trupp.maydayTriggeredBy,
        maydayTriggeredAt: trupp.maydayTriggeredAt,
        pausedAt: trupp.pausedAt,
        pausedElapsed: trupp.pausedElapsed,
        originalTask: trupp.originalAufgabe, // Fix field name to match API
        closed: trupp.geschlossen, // Fix field name to match API
        einsatzCount: trupp.einsatzCount || 1
      };
      
      // creatorID und supervisorName mitsenden (für Trupp-Übergabe)
      if (trupp.creatorID !== undefined) {
        updateData.creatorID = trupp.creatorID;
      }
      if (trupp.supervisorName !== undefined) {
        updateData.supervisorName = trupp.supervisorName;
      }
      
      await teamAPI.updateTeam(trupp.id, updateData);
    } catch (error) {
      console.error('Error updating team in API:', error);
      throw error;
    }
  };

  // Delete team from API
  const deleteTeamFromAPI = async (teamId: number) => {
    // Check if we're online before making API calls
    if (!navigator.onLine) {

      return;
    }
    
    try {
      // Check if this is a local team ID (longer than 10 digits)
      const isLocalTeamId = teamId.toString().length > 10;
      
      await teamAPI.deleteTeam(teamId);
    } catch (error) {
      console.error('Error deleting team from API:', error);
      throw error;
    }
  };

  // Ref für API-Duplikatserkennung
  const apiAuditInProgressRef = React.useRef<Set<string>>(new Set());
  
  // Save audit log entry to API
  const saveAuditLogToAPI = async (entry: Omit<AuditLogEntry, 'timestamp'>) => {
    if (!einsatz) return;
    
    // Check if we're online before making API calls
    if (!navigator.onLine) {
      return;
    }
    
    // Create unique key for this audit entry (without timestamp to catch duplicates)
    const auditKey = `${entry.type}-${entry.message}-${entry.data?.truppId || 'no-team'}`;
    
    // Check if this exact audit entry is already being sent to API
    if (apiAuditInProgressRef.current.has(auditKey)) {
      console.log('API audit call already in progress, skipping:', entry.type, entry.message);
      return;
    }
    
    // Add to in-progress set
    apiAuditInProgressRef.current.add(auditKey);
    
    try {
      const auditUser = getAuditUserInfo();
      const logData = {
        opsID: einsatz.id,
        teamID: entry.data?.truppId,
        logType: entry.type,
        message: entry.message,
        shortDescription: entry.shortDescription,
        logData: JSON.stringify({
          ...(entry.data || {}),
          performedBy: auditUser.realUser,
          performedById: auditUser.realUserId,
        })
      };
      await auditLogAPI.createAuditLog(logData);
    } catch (error) {
      throw error; // Re-throw so addAudit can handle it
    } finally {
      // Remove from in-progress set after completion (success or error)
      apiAuditInProgressRef.current.delete(auditKey);
    }
  };

  // Refs für save progress und audit logging
  const lastAuditEntryRef = React.useRef<string>('');
  const lastAuditTimestampRef = React.useRef<number>(0);

  const addAudit = async (entry: Omit<AuditLogEntry, 'timestamp'>) => {
    
    // Benutzer-Info automatisch hinzufügen
    const auditUser = getAuditUserInfo();
    
    // Create a new entry with current timestamp and user info
    const newEntry: AuditLogEntry = {
      ...entry,
      timestamp: Date.now(),
      performedBy: auditUser.realUser || entry.performedBy,
      performedById: auditUser.realUserId || entry.performedById,
    };
    
    // Check for duplicate entries using ref-based tracking (avoids stale closure on auditlog state)
    const currentTimestamp = Date.now();
    const entryKey = `${entry.type}-${entry.message}-${JSON.stringify(entry.data)}`;
    
    if (lastAuditEntryRef.current === entryKey && (currentTimestamp - lastAuditTimestampRef.current) < 5000) {
      console.log('Duplicate audit entry detected, skipping:', entry.type, entry.message);
      return;
    }
    
    // Update last audit entry tracking
    lastAuditEntryRef.current = entryKey;
    lastAuditTimestampRef.current = currentTimestamp;
    
    // Immediately add to local state for instant UI update (functional update to avoid stale closure)
    setAuditlog(prev => [...prev, newEntry]);
    
    if (navigator.onLine) {
      // Online: Zuerst in localStorage speichern (als Fallback), dann API versuchen
      const auditlogAll = JSON.parse(localStorage.getItem('auditlog') || '[]');
      const entryWithSyncFlag = { ...newEntry, _synced: false };
      auditlogAll.push(entryWithSyncFlag);
      localStorage.setItem('auditlog', JSON.stringify(auditlogAll));
      
      saveAuditLogToAPI(entry).then(() => {
        // API-Save erfolgreich → Eintrag aus localStorage entfernen (API ist Source of Truth)
        const currentLogs = JSON.parse(localStorage.getItem('auditlog') || '[]');
        const cleanedLogs = currentLogs.filter((log: any) => 
          !(log.timestamp === newEntry.timestamp && log.type === newEntry.type && log.message === newEntry.message)
        );
        localStorage.setItem('auditlog', JSON.stringify(cleanedLogs));
        console.log('✅ Audit-Log an API gesendet und aus localStorage entfernt');
      }).catch((error) => {
        console.error('❌ Failed to save audit entry to API, bleibt im localStorage für späteren Sync:', error);
        // Bleibt im localStorage → wird beim nächsten Online-Sync hochgeladen
      });
    } else {
      // Offline: Nur in localStorage speichern, wird beim Online-Sync hochgeladen
      const auditlogAll = JSON.parse(localStorage.getItem('auditlog') || '[]');
      const entryWithSyncFlag = { ...newEntry, _synced: false };
      auditlogAll.push(entryWithSyncFlag);
      localStorage.setItem('auditlog', JSON.stringify(auditlogAll));
      console.log('📴 Offline: Audit-Log nur lokal gespeichert');
    }
  };

  // Benutzer-Wechsel Handler (für technische Accounts)
  const handleUserIdentified = async (identity: RealUserIdentity) => {
    setShowUserIdentModal(false);
    
    // Vorherige Identität aus Ref verwenden (wurde VOR dem Öffnen des Modals gespeichert),
    // weil das UserIdentificationModal setRealUserIdentity() bereits aufgerufen hat
    const previousIdentity = previousIdentityRef.current;
    const previousName = previousIdentity 
      ? `${previousIdentity.firstName} ${previousIdentity.lastName}`.trim()
      : '';
    const newName = `${identity.firstName} ${identity.lastName}`.trim();
    
    console.log('👤 handleUserIdentified:', {
      previousUser: previousName,
      previousUserId: previousIdentity?.id,
      newUser: newName,
      newUserId: identity.id,
      truppsTotal: trupps.length,
      truppsDesPreviousUsers: previousIdentity?.id ? trupps.filter(t => t.creatorID === previousIdentity.id).length : 0,
      allCreatorIDs: trupps.map(t => ({ name: t.name, creatorID: t.creatorID }))
    });
    
    // Prüfe ob es Trupps gibt, die übergeben werden könnten
    // 1. Trupps des vorherigen echten Benutzers (creatorID === previousIdentity.id)
    // 2. Trupps des technischen JWT-Users (Fallback, wenn creatorID noch auf techn. User steht)
    let previousUserTrupps: typeof trupps = [];
    
    if (previousIdentity?.id) {
      previousUserTrupps = trupps.filter(t => t.creatorID === previousIdentity.id);
    }
    
    // Fallback: Wenn keine Trupps des echten Users gefunden, aber Trupps existieren,
    // prüfe ob sie dem technischen JWT-User gehören (für Altdaten/Übergangsphase)
    if (previousUserTrupps.length === 0 && trupps.length > 0 && isTechAccount) {
      try {
        const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        if (accessToken) {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          const techUserID = payload.id;
          const techUserTrupps = trupps.filter(t => t.creatorID === techUserID);
          if (techUserTrupps.length > 0) {
            console.log('🔄 Fallback: Trupps des technischen Users gefunden:', techUserTrupps.length);
            previousUserTrupps = techUserTrupps;
          }
        }
      } catch {}
    }
    
    if (previousUserTrupps.length > 0 && previousIdentity?.id !== identity.id) {
      // Es gibt Trupps vom vorherigen Benutzer → Handover-Modal anzeigen
      console.log('🔄 Benutzerwechsel mit Trupp-Übergabe:', {
        von: previousName,
        nach: newName,
        truppsZurÜbergabe: previousUserTrupps.length
      });
      setHandoverPreviousUserName(previousName);
      setHandoverNewIdentity(identity);
      setHandoverTrupps(previousUserTrupps);
      setShowHandoverModal(true);
    } else {
      // Keine Trupps zum Übergeben → direkt wechseln
      finalizeUserSwitch(identity, previousName);
    }
  };

  // Handover bestätigt: ausgewählte Trupps dem neuen Benutzer zuweisen
  const handleHandoverConfirm = async (selectedTruppIds: number[]) => {
    if (!handoverNewIdentity) return;
    
    const newName = `${handoverNewIdentity.firstName} ${handoverNewIdentity.lastName}`.trim();
    const newSupervisorName = `${handoverNewIdentity.lastName} ${handoverNewIdentity.firstName}`.trim();
    
    console.log('🔄 Trupp-Übergabe bestätigt:', {
      neuerBenutzer: newName,
      übernommeneTrupps: selectedTruppIds.length,
      verbleibendeTrupps: handoverTrupps.length - selectedTruppIds.length
    });

    // 1. Zuerst die ausgewählten Trupps in der API dem neuen Benutzer zuweisen (BLOCKIEREND warten)
    const apiUpdatePromises = selectedTruppIds
      .map(truppId => trupps.find(t => t.id === truppId))
      .filter(trupp => trupp && trupp.id < 1000000)
      .map(async (trupp) => {
        try {
          await teamAPI.updateTeam(trupp!.id, {
            creatorID: handoverNewIdentity.id,
            supervisorName: newSupervisorName
          });
          console.log(`✅ Trupp ${trupp!.name} (ID: ${trupp!.id}) → creatorID geändert zu ${handoverNewIdentity.id}`);
        } catch (error) {
          console.error(`❌ Fehler beim Aktualisieren von Trupp ${trupp!.name}:`, error);
        }
      });
    
    // Auf alle API-Updates warten, bevor wir neu laden
    await Promise.all(apiUpdatePromises);
    console.log('✅ Alle Trupp-Übergaben in API gespeichert');

    // Handover-Modal schliessen
    setShowHandoverModal(false);
    setHandoverTrupps([]);
    const savedIdentity = handoverNewIdentity;
    const savedPreviousUserName = handoverPreviousUserName;
    setHandoverNewIdentity(null);

    // Benutzerwechsel abschliessen (setzt realUserIdentity → wichtig für loadTeamsFromAPI)
    finalizeUserSwitch(savedIdentity, savedPreviousUserName, selectedTruppIds);

    // 2. Jetzt Teams komplett von der API neu laden.
    // Da finalizeUserSwitch die RealUserIdentity gesetzt hat, wird loadTeamsFromAPI
    // die supervisorID des neuen Benutzers verwenden und somit:
    // - Bens bestehende Trupps laden
    // - UND die gerade übertragenen Trupps (deren creatorID jetzt Ben ist)
    if (einsatz?.id) {
      console.log('🔄 Lade alle Trupps des neuen Benutzers von der API...');
      await loadTeamsFromAPI(einsatz.id, einsatz, einsatzleiterMode);
    }
  };

  // Handover abgebrochen → Benutzerwechsel nicht durchführen
  const handleHandoverCancel = () => {
    setShowHandoverModal(false);
    setHandoverTrupps([]);
    setHandoverNewIdentity(null);
    setHandoverPreviousUserName('');
    // Identität bleibt beim bisherigen Benutzer
  };

  // Benutzerwechsel abschliessen (Identity setzen + Auditlog)
  const finalizeUserSwitch = (identity: RealUserIdentity, previousName: string, handoverTruppIds?: number[]) => {
    setRealUserName(`${identity.firstName} ${identity.lastName}`.trim());
    
    const auditMessage = handoverTruppIds && handoverTruppIds.length > 0
      ? `<b>Benutzerwechsel</b>: ${previousName} → ${identity.firstName} ${identity.lastName}. ${handoverTruppIds.length} Trupp(s) übergeben.`
      : `<b>Benutzerwechsel</b>: Benutzer gewechselt zu ${identity.firstName} ${identity.lastName}.`;
    
    addAudit({
      type: 'Benutzerwechsel',
      message: auditMessage,
      shortDescription: handoverTruppIds ? 'Benutzerwechsel + Übergabe' : 'Benutzerwechsel',
      data: {
        previousUser: previousName,
        newUser: `${identity.firstName} ${identity.lastName}`.trim(),
        newUserId: identity.id,
        manual: identity.manual,
        einsatzId: einsatz?.id,
        handoverTruppIds: handoverTruppIds || []
      }
    });
  };

  // Helper function to save teams consistently to localStorage
  const saveTeamsToLocalStorage = (updatedTrupps: Trupp[], einsatzToSave?: any) => {
    const einsatzToUse = einsatzToSave || einsatz;
    
    if (!einsatzToUse) {
      return;
    }
    
    // Ensure all fields are properly included when saving to localStorage
    const teamsToSave = updatedTrupps.map(trupp => ({
      id: trupp.id,
      aufgabe: trupp.aufgabe,
      standort: trupp.standort,
      startdruck: trupp.startdruck,
      currentPressure: trupp.currentPressure,
      originalStartdruck: trupp.originalStartdruck,
      funkkanal: trupp.funkkanal,
      mitglieder: trupp.mitglieder,
      truppfuehrerIndex: trupp.truppfuehrerIndex,
      name: trupp.name,
      createdAt: trupp.createdAt,
      startedAt: trupp.startedAt,
      outOfService: trupp.outOfService,
      abfrageCountdown: trupp.abfrageCountdown,
      abfrageResetAt: trupp.abfrageResetAt,
      statusOk: trupp.statusOk,
      mayday: trupp.mayday,
      originalAufgabe: trupp.originalAufgabe,
      geschlossen: trupp.geschlossen,
      displayOrder: trupp.displayOrder,
      einsatzCount: trupp.einsatzCount || 1,
      // Ensure pause-related fields are always included
      pausedAt: trupp.pausedAt,
      pausedElapsed: trupp.pausedElapsed
    }));
    if (einsatzToUse?.id) {
      const counts = readTruppEinsatzCounts(einsatzToUse.id);
      updatedTrupps.forEach((trupp) => {
        counts[String(trupp.id)] = Math.max(Number(counts[String(trupp.id)]) || 1, trupp.einsatzCount || 1);
      });
      writeTruppEinsatzCounts(einsatzToUse.id, counts);
    }
    
    const einsaetze = JSON.parse(localStorage.getItem('einsaetze') || '[]');
    const remoteEinsaetze = JSON.parse(localStorage.getItem('remoteEinsaetze') || '[]');
    
    // Lokaler Einsatz
    const localIdx = einsaetze.findIndex((e: any) => String(e.id) === String(id));
    
    if (localIdx !== -1) {
      einsaetze[localIdx].trupps = teamsToSave;
      localStorage.setItem('einsaetze', JSON.stringify(einsaetze));
    }
    
    // Remote-Einsatz
    const remoteIdx = remoteEinsaetze.findIndex((e: any) => String(e.id ?? e.opsID ?? e.name) === String(id));
    
    if (remoteIdx !== -1) {
      remoteEinsaetze[remoteIdx].trupps = teamsToSave;
      localStorage.setItem('remoteEinsaetze', JSON.stringify(remoteEinsaetze));
    }
  };

  const einsatzErstelltLogged = React.useRef(false);

  // Einsatzdaten laden
  React.useEffect(() => {
    const einsaetze = JSON.parse(localStorage.getItem('einsaetze') || '[]');
    const remoteEinsaetze = JSON.parse(localStorage.getItem('remoteEinsaetze') || '[]');
    const found =
      einsaetze.find((e: any) => String(e.id) === String(id)) ||
      remoteEinsaetze.find((e: any) => String(e.id ?? e.opsID ?? e.name) === String(id));
    
    setEinsatz(found);
    setEinsatzGeschlossen(!!found?.geschlossen);
    
    // Mayday-Benachrichtigungen werden von MaydayNotificationManager verwaltet
    
    // Nur dann skippen, wenn der Einsatz nach "new=true" noch nicht im Storage existiert.
    // Sonst würden bei einem Browser-Reload vorhandene Trupps fälschlich ausgeblendet.
    if (isNewOperation && !found) {
      setTrupps([]);
      setAuditlog([]);
      setTruppsLoading(false); // Kein Loading für neue Einsätze
      return;
    }

    // new=true nur als Einmal-Flag verwenden und danach aus der URL entfernen,
    // damit Reloads nicht erneut in den "new operation"-Pfad laufen.
    if (isNewOperation) {
      window.history.replaceState(null, '', location.pathname);
    }
    
    // Load teams and audit log from API if authenticated, otherwise from localStorage

    // Async IIFE um Race Conditions zu vermeiden
    const loadData = async () => {
      if (isAuthenticatedSync() && found) {
        // Im Trupp Übersicht Modus immer API-Daten laden und Loading-Spinner anzeigen
        if (localEinsatzleiterMode) {
          setEinsatzleiterLoading(true); // Loading-Spinner aktivieren
          setTrupps([]); // Teams leeren, damit Loading-Spinner angezeigt wird
        }
        loadTeamsFromAPI(found.id, found, localEinsatzleiterMode);
        // Audit-Logs laden und WARTEN, damit der State aktuell ist bevor "Einsatz erstellt" geprüft wird
        await loadAuditLogFromAPI(found.id);
        // Mayday-Benachrichtigungen werden von MaydayNotificationManager verwaltet
      } else if (!localEinsatzleiterMode) {
        // Nur im Normal-Modus localStorage verwenden (nicht im Trupp Übersicht Modus)
        // Ensure all fields are properly loaded from localStorage
        const localStorageTeams = found?.trupps || [];
        
        const processedLocalStorageTeams = localStorageTeams.map((team: any) => {
          const processedTeam = {
          id: team.id,
          aufgabe: team.aufgabe || 'Warten',
          standort: team.standort || '',
            startdruck: team.startdruck || 300,
            currentPressure: team.currentPressure || team.startdruck || 300,
            originalStartdruck: team.originalStartdruck || team.startdruck || 300,
          funkkanal: team.funkkanal || '',
          mitglieder: team.mitglieder || [],
            truppfuehrerIndex: (() => {
              const leaderIndex = team.truppfuehrerIndex || 0;
              const memberCount = (team.mitglieder || []).length;
              
              // Validierung: Truppführer-Index darf nicht größer als Mitglieder-Anzahl sein
              const validLeaderIndex = memberCount > 0 ? Math.min(leaderIndex, memberCount - 1) : 0;
              

              return validLeaderIndex;
            })(),
          name: team.name,
          createdAt: team.createdAt,
          outOfService: team.outOfService || false,
          abfrageCountdown: 300, // Immer 5 Minuten
          abfrageResetAt: team.abfrageResetAt,
          statusOk: team.statusOk,
          mayday: team.mayday || false,
          originalAufgabe: team.originalAufgabe,
          geschlossen: team.geschlossen || false,
          displayOrder: team.displayOrder,
          einsatzCount: team.einsatzCount || 1,
          // Ensure pause-related fields are properly loaded
          pausedAt: team.pausedAt,
          pausedElapsed: team.pausedElapsed
          };
          

          
          return processedTeam;
        });
        
        const sortedTeams = applyTruppOrder(processedLocalStorageTeams, found.id);
        
        setTrupps(sortedTeams);
        const fromStorage = localStorage.getItem('auditlog');
        const localAuditLogs = fromStorage ? JSON.parse(fromStorage) : [];
        
        // Sortiere lokale Audit-Logs nach Timestamp (älteste zuerst)
        localAuditLogs.sort((a: AuditLogEntry, b: AuditLogEntry) => {
          const timeA = a.timestamp || 0;
          const timeB = b.timestamp || 0;
          return timeA - timeB;
        });
        
        setAuditlog(localAuditLogs);
      }
      
      // Auditlog: Einsatz erstellt (nur einmal pro Mount)
      // Wird NACH dem Laden der Audit-Logs geprüft, um Race Conditions zu vermeiden
      if (
        found &&
        !einsatzErstelltLogged.current
      ) {
        // Prüfung über funktionale Variante, damit der aktuelle State verwendet wird
        setAuditlog(prev => {
          const alreadyLogged = prev.some(l => l.type === 'Einsatz erstellt' && l.data?.einsatzId === found.id);
          if (!alreadyLogged && !einsatzErstelltLogged.current) {
            einsatzErstelltLogged.current = true;
            // addAudit wird asynchron aufgerufen, da wir in einer setState-Callback sind
            setTimeout(() => {
              addAudit({ type: 'Einsatz erstellt', message: `Einsatz wurde erstellt.`, data: { einsatzId: found.id, name: found.name } });
            }, 0);
          }
          return prev; // State nicht verändern, nur lesen
        });
      }
    };
    
    loadData();
  }, [id, isNewOperation]);

  // Technischer Account: Beim Öffnen eines Einsatzes fragen, wer das Gerät bedient,
  // falls die Identität noch nicht bekannt ist
  React.useEffect(() => {
    if (isTechAccount && einsatz && !getRealUserIdentity()) {
      console.log('🔧 Technischer Account ohne Identität → Frage wer das Gerät bedient');
      setShowUserIdentModal(true);
    }
  }, [isTechAccount, einsatz]);

  // Beim Öffnen der Truppüberwacher-Auswahl alle Einsatz-Mitglieder nachladen,
  // damit zugeteilte Personen nicht auswählbar sind.
  React.useEffect(() => {
    if (showUserIdentModal) {
      loadAllOperationMembers();
    }
  }, [showUserIdentModal, einsatz?.id]);

  // Auf Standort-Updates reagieren
  React.useEffect(() => {
    const handleLocationUpdate = (event: CustomEvent) => {
      const { operationId, newLocation } = event.detail;
      // Prüfe ob es der aktuelle Einsatz ist
      if (einsatz && String(einsatz.id) === String(operationId)) {
        // Einsatz-State aktualisieren
        const updatedEinsatz = {
          ...einsatz,
          ort: newLocation,
          location: newLocation,
          title: einsatz.title?.replace(/^[^:]+:/, `${newLocation}:`) || `${newLocation}: ${einsatz.category || einsatz.typ || ''}`,
          name: einsatz.name?.replace(/^[^:]+:/, `${newLocation}:`) || `${newLocation}: ${einsatz.category || einsatz.typ || ''}`
        };
        setEinsatz(updatedEinsatz);
        
        // Auch localStorage aktualisieren
        const remoteEinsaetze = JSON.parse(localStorage.getItem('remoteEinsaetze') || '[]');
        const updatedRemoteEinsaetze = remoteEinsaetze.map((e: any) => 
          String(e.id ?? e.opsID ?? e.name) === String(operationId) 
            ? { ...e, ort: newLocation, location: newLocation, title: e.title?.replace(/^[^:]+:/, `${newLocation}:`) || `${newLocation}: ${e.category || e.typ || ''}`, name: e.name?.replace(/^[^:]+:/, `${newLocation}:`) || `${newLocation}: ${e.category || e.typ || ''}` }
            : e
        );
        localStorage.setItem('remoteEinsaetze', JSON.stringify(updatedRemoteEinsaetze));
      }
    };

    window.addEventListener('operationLocationUpdated', handleLocationUpdate as EventListener);
    
    return () => {
      window.removeEventListener('operationLocationUpdated', handleLocationUpdate as EventListener);
    };
  }, [einsatz, id]);

  // Edit-Modal Standort aktualisieren, wenn Einsatz-State aktualisiert wird
  React.useEffect(() => {
    if (einsatzEditModalOpen && einsatz) {
      // Auch aus localStorage laden, falls der Standort im Hintergrund aktualisiert wurde
      const einsaetze = JSON.parse(localStorage.getItem('einsaetze') || '[]');
      const remoteEinsaetze = JSON.parse(localStorage.getItem('remoteEinsaetze') || '[]');
      const currentEinsatz = 
        einsaetze.find((e: any) => String(e.id) === String(id)) ||
        remoteEinsaetze.find((e: any) => String(e.id ?? e.opsID ?? e.name) === String(id));
      
      const einsatzToUse = currentEinsatz || einsatz;
      const currentOrt = einsatzToUse?.ort || einsatzToUse?.location || '';
      
      // Aktualisiere nur, wenn ein echter Standort vorhanden ist (nicht "Unbekannt")
      if (currentOrt && currentOrt !== 'Unbekannt') {
        setEditEinsatzOrt(currentOrt);
        // Aktualisiere auch den Einsatz-State, falls nötig
        if (einsatz.ort === 'Unbekannt' || einsatz.location === 'Unbekannt') {
          setEinsatz(einsatzToUse);
        }
      }
    }
  }, [einsatz, einsatzEditModalOpen, id]);

  // Trupps im LocalStorage speichern
  // REMOVED: Automatic saving for local einsätze to prevent race conditions
  // Local einsätze are now saved manually in specific operations (create, update, delete, etc.)
  // Remote einsätze are handled by their specific functions

  // Trupp erstellen
  // Funktion zum Öffnen des Trupp-Erstellungs-Modals mit Loading aller Standard-Werte
  const openTruppModal = async () => {
    // Hard limit: prevent opening when maximum number of teams is reached
    if (trupps.length >= maxTrupps) {
      return;
    }
    // Alle Standard-Werte laden und State aktualisieren
    await Promise.all([
      loadTasks(),
      loadStandorte(),
      loadFunkkanaele(),
      loadFeedbacks()
    ]);
    // Aktuelle Einstellung bevorzugt aus API laden (Fallback: localStorage)
    const currentQuickTruppCreation = await loadQuickTruppCreationSetting();
    await loadTruppNamingModeSetting();
    setQuickTruppCreation(currentQuickTruppCreation);
    setQuickTruppCreationForModal(currentQuickTruppCreation);
    console.log('⚙️ QUICK-TRUPP MODAL OPEN: Effective setting for dialog', {
      currentQuickTruppCreation,
    });
    
    setModalOpen(true);
  };

  // Funktion zum Zurücksetzen des Trupp-Erstellungs-Modals
  const resetTruppModal = () => {
    setModalOpen(false);
    setStep(1);
    setAufgabe('');
    setIsEmergencyTrupp(false);
    setStandort('');
    setStartdruck(300);
    setFunkkanal('');
    setFunkkanalSelected(false);
    setMitglieder([]);
    setTruppfuehrerIndex(null);
    setMitgliedVorname('');
    setMitgliedNachname('');
    setQuickTruppCreationForModal(false);
  };

  // Refs für save progress und audit logging außerhalb der Funktion
  const saveInProgressRef = React.useRef(false);
  const auditLoggedRef = React.useRef(false);
  const lastTruppNameRef = React.useRef<string>('');
  const lastTruppTimestampRef = React.useRef<number>(0);
  
  const handleSaveTrupp = async (forcedLeaderIndex?: number) => {
    // Enforce max trupps before saving
    if (trupps.length >= maxTrupps) {
      setModalOpen(false);
      return;
    }
    const leaderIndex =
      typeof forcedLeaderIndex === 'number'
        ? forcedLeaderIndex
        : (truppfuehrerIndex !== null ? truppfuehrerIndex : (mitglieder.length === 1 ? 0 : null));
    if (leaderIndex === null || !mitglieder[leaderIndex]) return;
    if (truppfuehrerIndex !== leaderIndex) setTruppfuehrerIndex(leaderIndex);

    // Vor JEDEM Speichern die aktuelle Einstellung erneut aus API laden
    // (wichtig für Accounts ohne Zugriff auf das Settings-Modal)
    const effectiveQuickTruppCreation = await loadQuickTruppCreationSetting();
    const effectiveTruppNamingMode = await loadTruppNamingModeSetting();
    const { shortName: effectiveShortName, prefixEnabled: effectivePrefixEnabled } = await loadOrgPrefixSettings();
    setQuickTruppCreation(effectiveQuickTruppCreation);
    setQuickTruppCreationForModal(effectiveQuickTruppCreation);
    console.log('⚙️ QUICK-TRUPP SAVE: Effective setting before create', {
      effectiveQuickTruppCreation,
      funkkanalSelected,
      effectiveShortName,
      effectivePrefixEnabled,
      leaderIndex,
    });
    
    // Bei Schnell-Erstellung sind Funkkanal-Validierungen nicht nötig
    if (!effectiveQuickTruppCreation && !funkkanalSelected) return;
    
    const truppfuehrer = mitglieder[leaderIndex];
    const nextTruppNumber = await getNextOperationTruppNumber();
    const paddedTruppNumber = String(nextTruppNumber).padStart(2, '0');
    const baseName = isEmergencyTrupp
      ? 'Notfalltrupp'
      : effectiveTruppNamingMode === 'number'
        ? `Trupp ${paddedTruppNumber}`
        : `Trupp ${truppfuehrer.nachname}`;
    const name = buildPrefixedTruppName(baseName, effectiveShortName, effectivePrefixEnabled);
    const currentTimestamp = Date.now();
    
    // Verhindere mehrfache Ausführung und doppelte Trupp-Erstellung
    if (saveInProgressRef.current) {
      console.log('Save already in progress, skipping duplicate call');
      return;
    }
    
    // Verhindere doppelte Trupp-Erstellung mit gleichem Namen innerhalb von 5 Sekunden
    if (lastTruppNameRef.current === name && (currentTimestamp - lastTruppTimestampRef.current) < 5000) {
      console.log('Duplicate trupp creation detected, skipping');
      return;
    }
    
    saveInProgressRef.current = true;
    auditLoggedRef.current = false; // Reset audit logged flag
    lastTruppNameRef.current = name;
    lastTruppTimestampRef.current = currentTimestamp;
    // Get current user ID for creatorID
    // Bei technischen Accounts wird die echte Person (RealUserIdentity) als Ersteller verwendet
    const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    let creatorID: number | undefined = undefined;
    let supervisorName: string | undefined = undefined;
    
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const tokenUserID = payload.id;
        console.log('👤 Token User ID:', tokenUserID, '| isTechnicalAccount:', isTechAccount);
        
        // Bei technischem Account: echte Person als Ersteller verwenden
        if (isTechAccount) {
          const realUser = getRealUserIdentity();
          if (realUser?.id) {
            creatorID = realUser.id;
            supervisorName = `${realUser.lastName} ${realUser.firstName}`.trim();
            console.log('👤 Technischer Account → Verwende echte Person als Ersteller:', {
              creatorID,
              supervisorName,
              realUser
            });
          } else {
            // Fallback: Kein echter Benutzer gewählt → technischen User verwenden
            console.warn('⚠️ Technischer Account, aber kein echter Benutzer identifiziert – verwende Token-User als Fallback');
            creatorID = tokenUserID;
          }
        } else {
          // Normaler Benutzer: wie bisher aus Token
          creatorID = tokenUserID;
          console.log('👤 Normaler Account → creatorID aus Token:', creatorID);
        }
        
        // supervisorName aus lokaler User-Liste auflösen (falls noch nicht gesetzt)
        if (!supervisorName && creatorID) {
          let orgID = 0;
          try {
            orgID = payload.orgID;
          } catch {}
          
          if (orgID > 0) {
            const localUsers = JSON.parse(localStorage.getItem(`activeUsers_${orgID}`) || '[]');
            const currentUser = localUsers.find((user: any) => user.id === creatorID);
            if (currentUser) {
              supervisorName = `${currentUser.lastName} ${currentUser.firstName}`;
              console.log('👤 Found supervisorName from local users:', supervisorName);
            } else {
              console.log('❌ Current user not found in local users, trying to load users...');
              try {
                await syncUsers();
                const updatedUsers = JSON.parse(localStorage.getItem(`activeUsers_${orgID}`) || '[]');
                const updatedCurrentUser = updatedUsers.find((user: any) => user.id === creatorID);
                if (updatedCurrentUser) {
                  supervisorName = `${updatedCurrentUser.lastName} ${updatedCurrentUser.firstName}`;
                  console.log('👤 Found supervisorName after sync:', supervisorName);
                }
              } catch (error) {
                console.warn('Failed to sync users for supervisorName:', error);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error parsing access token for creatorID:', error);
      }
    } else {
      console.log('❌ No access token found for creatorID');
    }

    // Trupp startet im Status "Warten" (Aufgabe=Warten, Status=Warten), keine Zeit läuft
    const newTrupp: Trupp = {
      id: Math.floor(Math.random() * 1000000) + 1000000,
      aufgabe: effectiveQuickTruppCreation ? 'Warten' : 'Warten', // Bei Schnell-Erstellung auch Warten
      standort: effectiveQuickTruppCreation ? '' : standort, // Bei Schnell-Erstellung leer
      startdruck: startdruck, // Startdruck wird immer übernommen
      funkkanal: effectiveQuickTruppCreation ? '' : funkkanal, // Bei Schnell-Erstellung leer
      mitglieder: mitglieder.map(m => ({ ...m, druck: startdruck })),
      truppfuehrerIndex: leaderIndex,
      name,
      createdAt: Date.now(), // Erstellungszeit
      startedAt: undefined, // Trupp wartet auf Start - use undefined to send NULL to database
      creatorID, // ID des Benutzers, der den Trupp erstellt hat
      supervisorName, // Name des Truppüberwachers (sofort gesetzt)
      abfrageCountdown: 300,
      abfrageResetAt: undefined,
      statusOk: undefined, // explizit Warten
      mayday: false,
      emergencyTrupp: isEmergencyTrupp ? 1 : 0,
      originalAufgabe: effectiveQuickTruppCreation ? '' : aufgabe, // Bei Schnell-Erstellung leer
      // Pause-related fields for new teams (undefined/null)
      pausedAt: undefined,
      pausedElapsed: undefined,
      einsatzCount: 1,
    };
    
    const updatedTrupps = [...trupps, newTrupp];
    setTrupps(updatedTrupps);
    appendTruppOrderId(einsatz.id, newTrupp.id);
    
    // Save to API if authenticated, otherwise to localStorage
    if (isAuthenticatedSync()) {
      try {
        const savedTeam = await saveTeamToAPI(newTrupp);
        // Update the trupp with the real ID from API
        const updatedTruppsWithRealId = updatedTrupps.map(t => 
          t.id === newTrupp.id ? { ...t, id: savedTeam.id } : t
        );
        replaceTruppOrderId(einsatz.id, newTrupp.id, savedTeam.id);
        setTrupps(updatedTruppsWithRealId);
        // Auditlog-Eintrag mit echter ID
        if (!auditLoggedRef.current) {
          addAudit({
            type: 'Trupp erstellt',
            message: `Trupp '${name}' wurde erstellt (Status: Warten).`,
            shortDescription: 'Trupp erstellt',
            data: { name, mitglieder: mitglieder.map(m => ({ ...m, druck: startdruck })), aufgabe: 'Warten', standort, bar: startdruck, funkkanal: funkkanal, einsatzId: einsatz.id, truppId: savedTeam.id }
          });
          auditLoggedRef.current = true;
        }
      } catch (error) {
        console.error('Error saving team to API:', error);
        // Fallback to localStorage
        saveTeamsToLocalStorage(updatedTrupps);
        // Auditlog mit temporärer ID
        if (!auditLoggedRef.current) {
          addAudit({
            type: 'Trupp erstellt',
            message: `Trupp '${name}' wurde erstellt (Status: Warten).`,
            shortDescription: 'Trupp erstellt',
            data: { name, mitglieder: mitglieder.map(m => ({ ...m, druck: startdruck })), aufgabe: 'Warten', standort, bar: startdruck, funkkanal: funkkanal, einsatzId: einsatz.id, truppId: newTrupp.id }
          });
          auditLoggedRef.current = true;
        }
      }
    } else {
      saveTeamsToLocalStorage(updatedTrupps);
      // NEU: Einsatzobjekt im LocalStorage aktualisieren
      const einsaetze = JSON.parse(localStorage.getItem('einsaetze') || '[]');
      const localIdx = einsaetze.findIndex((e: any) => String(e.id) === String(einsatz.id));
      if (localIdx !== -1) {
        einsaetze[localIdx].trupps = updatedTrupps;
        localStorage.setItem('einsaetze', JSON.stringify(einsaetze));
        // Einsatzobjekt im State aktualisieren
        setEinsatz(einsaetze[localIdx]);
      }
      const remoteEinsaetze = JSON.parse(localStorage.getItem('remoteEinsaetze') || '[]');
      const remoteIdx = remoteEinsaetze.findIndex((e: any) => String(e.id ?? e.opsID ?? e.name) === String(einsatz.id));
      if (remoteIdx !== -1) {
        remoteEinsaetze[remoteIdx].trupps = updatedTrupps;
        localStorage.setItem('remoteEinsaetze', JSON.stringify(remoteEinsaetze));
        // Remote-Einsatzobjekt im State aktualisieren
        setEinsatz(remoteEinsaetze[remoteIdx]);
      }
      // Auditlog mit temporärer ID
      if (!auditLoggedRef.current) {
        addAudit({
          type: 'Trupp erstellt',
          message: `Trupp '${name}' wurde erstellt (Status: Warten).`,
          shortDescription: 'Trupp erstellt',
          data: { name, mitglieder: mitglieder.map(m => ({ ...m, druck: startdruck })), aufgabe: 'Warten', standort, bar: startdruck, funkkanal: funkkanal, einsatzId: einsatz.id, truppId: newTrupp.id }
        });
        auditLoggedRef.current = true;
      }
    }
    
    // Reset save progress flag
    saveInProgressRef.current = false;
    
    setModalOpen(false);
    setStep(1);
    setAufgabe('');
    setIsEmergencyTrupp(false);
    setStandort('');
    setStartdruck(300);
    setFunkkanal('');
    setFunkkanalSelected(false);
    setMitglieder([]);
    setTruppfuehrerIndex(null);
    setMitgliedVorname('');
    setMitgliedNachname('');
    setAutocompleteValue('');
    setAutocompleteSuggestions([]);
    setShowSuggestions(false);
    setNfcStatus('checking');
    setIsScanning(false);
  };

  // NFC functionality
  const checkNFCStatus = () => {
    if (isNFCAvailable()) {
      setNfcStatus('available');
      // Start continuous scan when step 6 (Mitglieder) opens
      if (step === 6) {
        startContinuousNFCScanning('create');
      }
    } else {
      setNfcStatus('unavailable');
    }
  };

  const startContinuousNFCScanning = (mode: 'create' | 'edit' = 'create') => {
    if (!isNFCAvailable()) {
      return;
    }

    if (continuousNFCStop) {
      continuousNFCStop();
      setContinuousNFCStop(null);
    }
    
    // Gescannte Tags zurücksetzen
    setScannedTags(new Set());
    
    const stopScan = startContinuousNFCScan(
      (user) => {
        const memberList = mode === 'edit' ? editMitglieder : mitglieder;
        
        // Prüfe ob dieser Tag bereits gescannt wurde
        if (scannedTags.has(user.hashKey)) {
          setNfcScanResult({
            success: false,
            message: `${user.firstName} ${user.lastName} wurde bereits gescannt`
          });
          return;
        }
        
        // Prüfe ob Benutzer der eingeloggte/ausgewählte Benutzer (Truppüberwacher) ist
        const currentUserKey = getCurrentUserKey();
        const userKey = `${user.firstName.toLowerCase()}|${user.lastName.toLowerCase()}`;
        if (currentUserKey && userKey === currentUserKey) {
          setNfcScanResult({
            success: false,
            message: `${user.firstName} ${user.lastName} ist der aktuelle Truppüberwacher und kann nicht als Truppmitglied hinzugefügt werden`
          });
          return;
        }
        
        // Prüfe ob Benutzer bereits im aktuellen Trupp ist
        const isAlreadyInTeam = memberList.some(m => 
          m.vorname === user.firstName && m.nachname === user.lastName
        );
        
        if (isAlreadyInTeam) {
          setNfcScanResult({
            success: false,
            message: `${user.firstName} ${user.lastName} ist bereits im Trupp`
          });
          return;
        }
        
        // Prüfe ob Benutzer bereits in einem anderen Trupp des Einsatzes ist
        const currentTruppId = mode === 'edit' ? mitgliederDialog.trupp?.id : undefined;
        const usersInTrupps = getUsersAlreadyInTrupps(currentTruppId);
        if (usersInTrupps.has(userKey)) {
          setNfcScanResult({
            success: false,
            message: `${user.firstName} ${user.lastName} ist bereits in einem anderen Trupp`
          });
          return;
        }
        
        // Füge Tag zur gescannten Liste hinzu
        setScannedTags(prev => new Set([...Array.from(prev), user.hashKey]));
        
        // Füge Benutzer direkt zur Mitgliederliste hinzu
        if (mode === 'edit') {
          setEditMitglieder(prevMitglieder => [...prevMitglieder, { 
            vorname: user.firstName, 
            nachname: user.lastName 
          }]);
        } else {
          setMitglieder(prevMitglieder => [...prevMitglieder, { 
            vorname: user.firstName, 
            nachname: user.lastName 
          }]);
        }
        
        setNfcScanResult({
          success: true,
          message: `${user.firstName} ${user.lastName} erfolgreich hinzugefügt`
        });
      },
      (error) => {
        console.error('NFC scan error:', error);
        setNfcScanResult({
          success: false,
          message: error
        });
      }
    );
    
    setContinuousNFCStop(() => stopScan);
  };

  // Lädt ALLE Mitglieder aller Trupps im gesamten Einsatz (auch von anderen Benutzern)
  // Damit kann geprüft werden, ob eine Person bereits in irgendeinem Trupp des Einsatzes ist
  const loadAllOperationMembers = async () => {
    try {
      if (!navigator.onLine || !einsatz?.id) return;
      
      const accessToken = await getAccessTokenAsync();
      if (!accessToken) return;
      
      let orgID = 0;
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        orgID = payload.orgID;
      } catch {}
      
      if (!orgID) return;
      
      const allTeams = await teamAPI.getTeamsByOperation(orgID, einsatz.id);
      const teamsArray = Array.isArray(allTeams) ? allTeams : Object.values(allTeams);
      
      const membersSet = new Set<string>();
      const supervisorIdsSet = new Set<number>();
      const supervisorKeysSet = new Set<string>();
      teamsArray.forEach((team: any) => {
        const members = team.members || team.mitglieder || [];
        members.forEach((m: any) => {
          const firstName = (m.firstName || m.vorname || '').toLowerCase();
          const lastName = (m.lastName || m.nachname || '').toLowerCase();
          if (firstName && lastName) {
            membersSet.add(`${firstName}|${lastName}`);
          }
        });

        // Truppüberwacher via ID erfassen
        if (typeof team.creatorID === 'number') {
          supervisorIdsSet.add(team.creatorID);
        }
        if (team.creator && typeof team.creator.id === 'number') {
          supervisorIdsSet.add(team.creator.id);
        }

        // Truppüberwacher via Name erfassen (für manuelle Eingaben)
        const creatorFirstName = (team.creator?.firstName || '').toLowerCase();
        const creatorLastName = (team.creator?.lastName || '').toLowerCase();
        if (creatorFirstName && creatorLastName) {
          supervisorKeysSet.add(`${creatorFirstName}|${creatorLastName}`);
        }

        // Fallback: supervisorName ("Nachname Vorname") gegen lokale User-Liste auflösen
        const supervisorName = (team.supervisorName || '').toString().trim().toLowerCase();
        if (supervisorName) {
          const matchedUser = localUsers.find(u =>
            `${u.lastName} ${u.firstName}`.toLowerCase() === supervisorName
          );
          if (matchedUser) {
            supervisorIdsSet.add(matchedUser.id);
            supervisorKeysSet.add(`${matchedUser.firstName.toLowerCase()}|${matchedUser.lastName.toLowerCase()}`);
          }
        }
      });
      
      console.log(`👥 Alle Mitglieder im Einsatz geladen: ${membersSet.size} Personen aus ${teamsArray.length} Trupps`);
      console.log(`👤 Alle Truppüberwacher im Einsatz geladen: ${supervisorIdsSet.size} IDs, ${supervisorKeysSet.size} Namen`);
      setAllOperationMembers(membersSet);
      setAllOperationSupervisorIds(supervisorIdsSet);
      setAllOperationSupervisorKeys(supervisorKeysSet);
    } catch (error) {
      console.error('❌ Fehler beim Laden aller Einsatz-Mitglieder:', error);
      // Bei Fehler: Fallback auf lokale Trupps (kein setAllOperationMembers, behalte bisherigen State)
    }
  };

  // Hilfsfunktion: Gibt den Key (vorname|nachname) des aktuell eingeloggten/ausgewählten Benutzers zurück
  // Bei normalem Account: JWT-User, bei technischem Account: der ausgewählte echte Benutzer
  const getCurrentUserKey = (): string | null => {
    if (isTechAccount) {
      const realUser = getRealUserIdentity();
      if (realUser) {
        return `${realUser.firstName.toLowerCase()}|${realUser.lastName.toLowerCase()}`;
      }
      return null;
    }
    // Normaler Account: aus JWT-Payload lesen
    try {
      const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      if (!accessToken) return null;
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      if (payload.firstName && payload.lastName) {
        return `${payload.firstName.toLowerCase()}|${payload.lastName.toLowerCase()}`;
      }
      // Fallback: aus lokaler User-Liste anhand der User-ID
      const orgID = payload.orgID;
      if (orgID) {
        const localUsers = JSON.parse(localStorage.getItem(`activeUsers_${orgID}`) || '[]');
        const currentUser = localUsers.find((u: any) => u.id === payload.id);
        if (currentUser) {
          return `${currentUser.firstName.toLowerCase()}|${currentUser.lastName.toLowerCase()}`;
        }
      }
    } catch {}
    return null;
  };

  // Hilfsfunktion: Prüft ob ein Benutzer als Truppüberwacher gesperrt ist
  // (aktueller User ODER Truppüberwacher aus dem Einsatz, ggf. auf anderem Gerät)
  const isBlockedSupervisorUser = (user: User): boolean => {
    const userKey = `${user.firstName.toLowerCase()}|${user.lastName.toLowerCase()}`;
    const currentUserKey = getCurrentUserKey();
    if (currentUserKey && userKey === currentUserKey) return true;
    if (allOperationSupervisorIds.has(user.id)) return true;
    if (allOperationSupervisorKeys.has(userKey)) return true;
    return false;
  };

  // Hilfsfunktion: Sammelt alle Benutzer, die bereits in einem Trupp des Einsatzes sind
  // Berücksichtigt sowohl die lokal geladenen Trupps als auch ALLE Trupps des Einsatzes (von der API)
  const getUsersAlreadyInTrupps = (excludeTruppId?: number): Set<string> => {
    const usersInTrupps = new Set<string>();
    
    // 1. Lokale Trupps (des aktuellen Benutzers)
    trupps.forEach(trupp => {
      // Optional: Trupp ausschließen (z.B. beim Bearbeiten eines Trupps)
      if (excludeTruppId !== undefined && trupp.id === excludeTruppId) return;
      
      // Alle Trupps berücksichtigen (auch "außer Dienst")
      trupp.mitglieder.forEach(m => {
        // Eindeutiger Schlüssel: Vorname + Nachname (lowercase für Vergleich)
        const key = `${m.vorname.toLowerCase()}|${m.nachname.toLowerCase()}`;
        usersInTrupps.add(key);
      });
    });
    
    // 2. Alle Mitglieder des gesamten Einsatzes (auch Trupps anderer Benutzer)
    allOperationMembers.forEach(memberKey => {
      usersInTrupps.add(memberKey);
    });
    
    return usersInTrupps;
  };

  const handleAutocompleteChange = (value: string) => {
    setAutocompleteValue(value);
    
    if (value.trim()) {
      const searchTerm = value.toLowerCase().trim();
      
      // Benutzer, die bereits in einem gespeicherten Trupp sind, ausschließen
      const usersInTrupps = getUsersAlreadyInTrupps();
      
      // Benutzer, die bereits im aktuell erstellten Trupp sind, ausschließen
      const usersInCurrentTrupp = new Set(
        mitglieder.map(m => `${m.vorname.toLowerCase()}|${m.nachname.toLowerCase()}`)
      );
      
      const filtered = localUsers.filter(user => {
        const userKey = `${user.firstName.toLowerCase()}|${user.lastName.toLowerCase()}`;
        // Truppüberwacher (lokal + andere Geräte) dürfen nicht Truppmitglied sein
        if (isBlockedSupervisorUser(user)) return false;
        // Prüfe, ob Benutzer bereits in einem gespeicherten Trupp ist
        if (usersInTrupps.has(userKey)) return false;
        // Prüfe, ob Benutzer bereits im aktuellen Trupp ist
        if (usersInCurrentTrupp.has(userKey)) return false;
        
        // Suchfilter anwenden
        return user.firstName.toLowerCase().includes(searchTerm) ||
        user.lastName.toLowerCase().includes(searchTerm) ||
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm) ||
          `${user.lastName} ${user.firstName}`.toLowerCase().includes(searchTerm);
      });
      
      setAutocompleteSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setAutocompleteSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleAutocompleteSelect = (user: User) => {
    const userKey = `${user.firstName.toLowerCase()}|${user.lastName.toLowerCase()}`;
    if (isBlockedSupervisorUser(user)) return;
    
    // Check if user is already in the current team
    const isAlreadyInTeam = mitglieder.some(m => 
      m.vorname === user.firstName && m.nachname === user.lastName
    );
    
    // Check if user is already in another trupp
    const usersInTrupps = getUsersAlreadyInTrupps();
    const isInOtherTrupp = usersInTrupps.has(userKey);
    
    if (!isAlreadyInTeam && !isInOtherTrupp) {
      setMitglieder([...mitglieder, { 
        vorname: user.firstName, 
        nachname: user.lastName 
      }]);
    }
    
    setAutocompleteValue('');
    setAutocompleteSuggestions([]);
    setShowSuggestions(false);
  };

  const handleAutocompleteAdd = () => {
    const trimmedValue = autocompleteValue.trim();
    if (!trimmedValue) return;
    
    // Check if it's a valid name (at least two parts)
    const nameParts = trimmedValue.split(' ').filter(part => part.length > 0);
    if (nameParts.length < 2) return;
    
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');
    
    const userKey = `${firstName.toLowerCase()}|${lastName.toLowerCase()}`;
    const currentUserKey = getCurrentUserKey();
    if (currentUserKey && userKey === currentUserKey) return;
    if (allOperationSupervisorKeys.has(userKey)) return;
    
    // Check if user is already in the current team
    const isAlreadyInTeam = mitglieder.some(m => 
      m.vorname === firstName && m.nachname === lastName
    );
    
    // Check if user is already in another trupp
    const usersInTrupps = getUsersAlreadyInTrupps();
    const isInOtherTrupp = usersInTrupps.has(userKey);
    
    if (!isAlreadyInTeam && !isInOtherTrupp) {
      setMitglieder([...mitglieder, { 
        vorname: firstName, 
        nachname: lastName 
      }]);
    }
    
    setAutocompleteValue('');
    setAutocompleteSuggestions([]);
    setShowSuggestions(false);
  };

  const handleAutocompleteKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (autocompleteSuggestions.length > 0) {
        handleAutocompleteSelect(autocompleteSuggestions[0]);
      } else {
        handleAutocompleteAdd();
      }
    }
  };

  // Load local users when step 6 (Mitglieder) opens
  React.useEffect(() => {
    if (step === 6) {
      const loadLocalUsers = async () => {
        try {
          // Check if we're online before making API calls
          if (!navigator.onLine) {
        
            const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
            let orgID = 0;
            
            if (accessToken) {
              try {
                const payload = JSON.parse(atob(accessToken.split('.')[1]));
                orgID = payload.orgID;
              } catch {}
            }
            
            const users = JSON.parse(localStorage.getItem(`activeUsers_${orgID}`) || '[]');
            setLocalUsers(users);
            return;
          }
          
          await syncUsers();
          // Get orgID from token
          const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
          let orgID = 0;
          
          if (accessToken) {
            try {
              const payload = JSON.parse(atob(accessToken.split('.')[1]));
              orgID = payload.orgID;
            } catch {}
          }
          
          // Use the correct localStorage key with orgID
          const users = JSON.parse(localStorage.getItem(`activeUsers_${orgID}`) || '[]');
          setLocalUsers(users);
        } catch (error) {
          console.error('Error loading local users:', error);
        }
      };
      
      loadLocalUsers();
      checkNFCStatus();
      // Alle Mitglieder des Einsatzes laden (auch von anderen Benutzern)
      loadAllOperationMembers();
    }
  }, [step]);

  // Load local users when Mitglieder-Dialog opens
  React.useEffect(() => {
    if (mitgliederDialog.open) {
      const loadLocalUsers = async () => {
        try {
          // Check if we're online before making API calls
          if (!navigator.onLine) {
        
            const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
            let orgID = 0;
            
            if (accessToken) {
              try {
                const payload = JSON.parse(atob(accessToken.split('.')[1]));
                orgID = payload.orgID;
              } catch {}
            }
            
            const users = JSON.parse(localStorage.getItem(`activeUsers_${orgID}`) || '[]');
            setLocalUsers(users);
            return;
          }
          
          await syncUsers();
          // Get orgID from token
          const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
          let orgID = 0;
          
          if (accessToken) {
            try {
              const payload = JSON.parse(atob(accessToken.split('.')[1]));
              orgID = payload.orgID;
            } catch {}
          }
          
          // Use the correct localStorage key with orgID
          const users = JSON.parse(localStorage.getItem(`activeUsers_${orgID}`) || '[]');
          setLocalUsers(users);
        } catch (error) {
          console.error('Error loading local users for Mitglieder-Dialog:', error);
        }
      };
      
      loadLocalUsers();
      if (!einsatzleiterMode) {
        startContinuousNFCScanning('edit');
      }
      // Alle Mitglieder des Einsatzes laden (auch von anderen Benutzern)
      loadAllOperationMembers();
    } else if (continuousNFCStop) {
      continuousNFCStop();
      setContinuousNFCStop(null);
    }
  }, [mitgliederDialog.open, einsatzleiterMode]);

  // Clear NFC scan result after 5 seconds
  React.useEffect(() => {
    if (nfcScanResult) {
      const timer = setTimeout(() => {
        setNfcScanResult(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [nfcScanResult]);

  // Stop continuous NFC scan when leaving step 6
  React.useEffect(() => {
    if (step !== 6 && continuousNFCStop && !mitgliederDialog.open) {
      continuousNFCStop();
      setContinuousNFCStop(null);
    }
  }, [step, continuousNFCStop, mitgliederDialog.open]);

  // Manual refresh function for users
  const refreshUsers = async () => {
    try {
      // Check if we're online before making API calls
      if (!navigator.onLine) {
    
        const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
        let orgID = 0;
        
        if (accessToken) {
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            orgID = payload.orgID;
          } catch {}
        }
        
        const users = JSON.parse(localStorage.getItem(`activeUsers_${orgID}`) || '[]');
        setLocalUsers(users);
        return;
      }
      
      await syncUsers();
      const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      let orgID = 0;
      
      if (accessToken) {
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          orgID = payload.orgID;
        } catch {}
      }
      
      const users = JSON.parse(localStorage.getItem(`activeUsers_${orgID}`) || '[]');
      setLocalUsers(users);
    } catch (error) {
      console.error('Error refreshing users:', error);
    }
  };

  // Trupp löschen
  const handleDeleteTrupp = async (id: number) => {
    const trupp = trupps.find(t => t.id === id);
    const updatedTrupps = trupps.filter(t => t.id !== id);
    setTrupps(updatedTrupps);
    if (einsatz?.id) writeTruppOrder(einsatz.id, updatedTrupps.map(t => t.id));
    
    // Track locally deleted teams for later sync
    if (trupp) {
      const isLocalTeam = trupp.id.toString().length > 10;
      
      if (!isLocalTeam) {
        // This is a server team that was deleted locally
    
        const locallyDeletedTeams = JSON.parse(localStorage.getItem('locallyDeletedTeams') || '[]');
        locallyDeletedTeams.push({
          id: trupp.id,
          name: trupp.name,
          deletedAt: Date.now()
        });
        localStorage.setItem('locallyDeletedTeams', JSON.stringify(locallyDeletedTeams));
      }
      
      addAudit({ type: 'Trupp gelöscht', message: `Trupp '${trupp.name}' wurde gelöscht.`, shortDescription: 'Trupp gelöscht', data: { name: trupp.name, einsatzId: einsatz.id } });
    }
    
    // Delete from API if authenticated and online, otherwise just save to localStorage
    if (isAuthenticatedSync() && navigator.onLine) {
      try {
        await deleteTeamFromAPI(id);
        // Always save to localStorage after successful API deletion
        saveTeamsToLocalStorage(updatedTrupps);
      } catch (error) {
        console.error('Error deleting team from API:', error);
        // Fallback to localStorage
        saveTeamsToLocalStorage(updatedTrupps);
      }
    } else {
      saveTeamsToLocalStorage(updatedTrupps);
    }
  };

  const handleStatus = async (trupp: Trupp, resetAbfrage?: () => void) => {
    
    // Wenn Trupp im Status 'Warten' und NEU erstellt (ohne Pause-Felder), direkt starten
    // Neue Trupps haben: aufgabe='Warten', !outOfService, alle Pause-Felder sind null/undefined
    // Reaktivierte Trupps haben: aufgabe='Warten', !outOfService, mindestens ein Pause-Feld ist gesetzt
    const isNewTeam = trupp.aufgabe === 'Warten' && 
                     !trupp.outOfService && 
                     (trupp.pausedAt === null || trupp.pausedAt === undefined) &&
                     (trupp.pausedElapsed === null || trupp.pausedElapsed === undefined);
    
    // Bei Quick-Erstellung: Wenn Daten fehlen, öffne Status-Modal
    if (isNewTeam && hasMissingData(trupp)) {
      const isReactivation = Boolean(trupp.outOfService || (trupp.originalAufgabe && trupp.aufgabe === 'Warten'));
      
      // Feedbacks + aktuelle Screen-Reihenfolge vor dem Öffnen laden
      loadFeedbacks();
      const latestScreens = await loadStatusUpdateScreensSetting();
      
      setStatusDialog({ open: true, trupp, resetAbfrage, isReactivation, isQuickTrupp: true });
      setStatusStep(getInitialStatusStep(trupp, true, latestScreens)); // skipFeedback = true für Quick-Trupps
      setStatusOk(trupp.statusOk !== false);
      setEditStandort(trupp.standort);
      setEditAufgabe(trupp.originalAufgabe || trupp.aufgabe || 'Einsatz');
      setEditDruck(trupp.currentPressure || trupp.startdruck);
      setFunkkanal(trupp.funkkanal || '');
      setFunkkanalSelected(!!trupp.funkkanal);
      setIsBearbeitenDialog(false);
      return;
    }
    
    if (isNewTeam) {
      const now = Date.now();
      
      // Verwende den ursprünglich gewählten Task aus originalAufgabe, falls vorhanden
      const taskToUse = trupp.originalAufgabe || 'Sicherung';
      
      const updatedTrupps = trupps.map(t =>
        t.id === trupp.id
          ? {
              ...t,
              aufgabe: taskToUse, // Verwende den ursprünglich gewählten Task
              createdAt: now, // Für neue Trupps: aktuelle Zeit
            startedAt: now, // Einsatzzeit beginnt jetzt
              abfrageResetAt: now,
              statusOk: true,
              originalAufgabe: taskToUse, // Speichere auch als originalAufgabe
            }
          : t
      );
      
      setTrupps(updatedTrupps);
      
      // Sofort in localStorage speichern
      if (einsatz) {
        const einsaetze = JSON.parse(localStorage.getItem('einsaetze') || '[]');
        const remoteEinsaetze = JSON.parse(localStorage.getItem('remoteEinsaetze') || '[]');
        
        // Lokaler Einsatz
        const localIdx = einsaetze.findIndex((e: any) => String(e.id) === String(id));
        if (localIdx !== -1) {
          einsaetze[localIdx].trupps = updatedTrupps;
          localStorage.setItem('einsaetze', JSON.stringify(einsaetze));
        }
        
        // Remote-Einsatz
        const remoteIdx = remoteEinsaetze.findIndex((e: any) => String(e.id ?? e.opsID ?? e.name) === String(id));
        if (remoteIdx !== -1) {
          remoteEinsaetze[remoteIdx].trupps = updatedTrupps;
          localStorage.setItem('remoteEinsaetze', JSON.stringify(remoteEinsaetze));
        }
      }
      
      saveTeamsToLocalStorage(updatedTrupps);
      
      // Update API
      if (isAuthenticatedSync()) {
        try {
          await updateTeamInAPI(updatedTrupps.find(t => t.id === trupp.id)!);
        } catch (error) {
          console.error('Error updating team in API:', error);
        }
      }
      
      addAudit({
        type: 'Trupp gestartet',
        message: `Trupp '${trupp.name}' wurde in den Einsatz geschickt (Status: ${taskToUse}).`,
        shortDescription: 'In Einsatz geschickt',
        data: {
          name: trupp.name,
          statusOk: true,
          aufgabe: taskToUse,
          standort: trupp.standort,
          bar: trupp.startdruck,
          abfrageCountdown: 300,
          einsatzId: einsatz.id,
          truppId: trupp.id,
          funkkanal: trupp.funkkanal,
          alt: {
            aufgabe: trupp.aufgabe,
            standort: trupp.standort,
            bar: trupp.startdruck,
            funkkanal: trupp.funkkanal
          },
          neu: {
            aufgabe: taskToUse,
            standort: trupp.standort,
            bar: trupp.startdruck,
            funkkanal: trupp.funkkanal
          }
        }
      });
      return;
    }
    
    // Für alle anderen Fälle (inkl. reaktivierte Trupps im 'Warten' Status) Status-Dialog öffnen
    // Prüfe ob es sich um eine Reaktivierung handelt
    const isReactivation = Boolean(trupp.outOfService || (trupp.originalAufgabe && trupp.aufgabe === 'Warten'));
    
    // Feedbacks + aktuelle Screen-Reihenfolge vor dem Öffnen laden (ohne F5)
    loadFeedbacks();
    const latestScreens = await loadStatusUpdateScreensSetting();
    
    setStatusDialog({ open: true, trupp, resetAbfrage, isReactivation });
    setStatusStep(getInitialStatusStep(trupp, false, latestScreens));
    setStatusOk(trupp.statusOk !== false);
    setEditStandort(trupp.standort);
    // Für reaktivierte Trupps den ursprünglichen Task vorauswählen, sonst den aktuellen
    setEditAufgabe(trupp.originalAufgabe || trupp.aufgabe);
    setEditDruck(trupp.currentPressure || trupp.startdruck);
    setFunkkanal(trupp.funkkanal || '');
    setFunkkanalSelected(!!trupp.funkkanal);
    setIsBearbeitenDialog(false);
  };
  const handleStatusSave = async () => {
    if (!statusDialog.trupp) return;
    
    // Bei Schnell-Erstellung sind Funkkanal-Validierungen nicht zwingend
    const isQuickTrupp = statusDialog.isQuickTrupp || false;
    if (!isQuickTrupp && !funkkanalSelected) return;
    const now = Date.now();
    const truppAlt = statusDialog.trupp;
    
    // Wenn auf Warten gesetzt wird (editAufgabe === 'Warten' und vorher nicht Warten): Pausieren
    if (editAufgabe === 'Warten' && truppAlt.aufgabe !== 'Warten') {
      // Zeit und Abfrage pausieren
      // Zeit: elapsed = jetzt - startedAt (oder createdAt als Fallback)
      const startTime = (truppAlt.startedAt && truppAlt.startedAt > 0) ? truppAlt.startedAt : truppAlt.createdAt;
      const elapsed = startTime ? Math.floor((now - startTime) / 1000) : 0;
      const updatedTrupps = trupps.map(t =>
        t.id === truppAlt.id
          ? {
              ...t,
              standort: editStandort,
              aufgabe: 'Warten',
              currentPressure: editDruck, // Nur aktuellen Druck setzen, startdruck unverändert lassen
              mitglieder: t.mitglieder.map(m => ({ ...m, druck: editDruck })),
              abfrageCountdown: 300,
              abfrageResetAt: now,
              // Zeit pausieren
              pausedAt: now,
              pausedElapsed: elapsed,
              statusOk,
              mayday: false,
              funkkanal: funkkanal,
            }
          : t
      );
      setTrupps(updatedTrupps);
      
      // Update API
      if (isAuthenticatedSync()) {
        try {
          await updateTeamInAPI(updatedTrupps.find(t => t.id === truppAlt.id)!);
        } catch (error) {
          console.error('Error updating team in API:', error);
        }
      }
      
      saveTeamsToLocalStorage(updatedTrupps);
      addAudit({
        type: isBearbeitenDialog ? 'Bearbeiten' : 'Status geändert',
        message: isBearbeitenDialog
          ? `Bearbeiten: Trupp '${statusDialog.trupp.name}' wurde bearbeitet.`
          : `Status geändert: Trupp '${statusDialog.trupp.name}' Status wurde geändert: Warten (pausiert).`,
        shortDescription: isBearbeitenDialog ? 'Bearbeitung' : 'Statusupdate',
        data: { name: statusDialog.trupp.name, statusOk, aufgabe: 'Warten', standort: editStandort, bar: editDruck, abfrageCountdown: 300, einsatzId: einsatz.id, truppId: statusDialog.trupp.id, funkkanal: funkkanal }
      });
      setStatusDialog({ open: false, trupp: null });
      setStatusStep(getFirstConfiguredStatusStep());
      setIsBearbeitenDialog(false);
      return;
    }
    
    // Wenn von Warten auf aktiv gewechselt wird: Fortsetzen oder neu starten
    // Für Quick-Trupps: Verwende die gewählte Aufgabe oder eine Standard-Aufgabe
    const taskToUse = isQuickTrupp && editAufgabe === 'Warten' ? 'Einsatz' : editAufgabe;
    
    if (truppAlt.aufgabe === 'Warten' && taskToUse !== 'Warten') {
      // Prüfen ob es ein reaktivierter Trupp ist (hat originalAufgabe aber keine pausedAt oder war outOfService)
      const isReactivated = truppAlt.pausedAt || truppAlt.outOfService;
      
      if (isReactivated) {
        // Reaktivierter Trupp: Komplett neu starten - BEIDE Druck-Felder setzen
        const updatedTrupps = trupps.map(t =>
          t.id === truppAlt.id
          ? {
              ...t,
              standort: editStandort,
              aufgabe: taskToUse,
              startdruck: editDruck, // BEIDE Felder setzen bei Reaktivierung
              currentPressure: editDruck, // BEIDE Felder setzen bei Reaktivierung
              mitglieder: t.mitglieder.map(m => ({ ...m, druck: editDruck })),
              abfrageCountdown: 300,
              abfrageResetAt: now,
              createdAt: now, // Neue Startzeit
              startedAt: now, // Einsatzzeit beginnt jetzt
              pausedAt: undefined,
              pausedElapsed: undefined,
              outOfService: false, // Mark as active again
              originalAufgabe: undefined, // Clear original task
              statusOk,
              mayday: false,
              funkkanal: funkkanal,
            }
            : t
        );
        setTrupps(updatedTrupps);
        
        // Update API
        if (isAuthenticatedSync()) {
          try {
            await updateTeamInAPI(updatedTrupps.find(t => t.id === truppAlt.id)!);
          } catch (error) {
            console.error('Error updating team in API:', error);
          }
        }
        
        saveTeamsToLocalStorage(updatedTrupps);
        addAudit({
          type: 'Trupp gestartet',
          message: `Trupp '${statusDialog.trupp.name}' wurde in den Einsatz geschickt (Status: ${taskToUse}).`,
          shortDescription: 'In Einsatz geschickt',
          data: {
            name: statusDialog.trupp.name,
            statusOk,
            aufgabe: taskToUse,
            standort: editStandort,
            bar: editDruck,
            abfrageCountdown: 300,
            einsatzId: einsatz.id,
            truppId: statusDialog.trupp.id,
            funkkanal: funkkanal,
            alt: {
              aufgabe: truppAlt.aufgabe,
              standort: truppAlt.standort,
              bar: truppAlt.startdruck,
              funkkanal: truppAlt.funkkanal
            },
            neu: {
              aufgabe: taskToUse,
              standort: editStandort,
              bar: editDruck,
              funkkanal: funkkanal
            }
          }
        });
        setStatusDialog({ open: false, trupp: null });
        setStatusStep(getFirstConfiguredStatusStep());
        setIsBearbeitenDialog(false);
        return;
      } else {
        // Pausierter Trupp: Fortsetzen - BEIDE Druck-Felder setzen
      const pausedElapsed = truppAlt.pausedElapsed || 0;
      const newCreatedAt = now - pausedElapsed * 1000;
      const newStartedAt = now - pausedElapsed * 1000;
        const updatedTrupps = trupps.map(t =>
        t.id === truppAlt.id
        ? {
            ...t,
            standort: editStandort,
            aufgabe: taskToUse,
            startdruck: editDruck, // BEIDE Felder setzen bei Fortsetzung
            currentPressure: editDruck, // BEIDE Felder setzen bei Fortsetzung
            mitglieder: t.mitglieder.map(m => ({ ...m, druck: editDruck })),
              abfrageCountdown: 300,
            abfrageResetAt: now,
              createdAt: newCreatedAt,
            startedAt: newStartedAt,
              pausedAt: undefined,
              pausedElapsed: undefined,
            statusOk,
              mayday: false,
              funkkanal: funkkanal,
            }
          : t
        );
        setTrupps(updatedTrupps);
        
        // Update API
        if (isAuthenticatedSync()) {
          try {
            await updateTeamInAPI(updatedTrupps.find(t => t.id === truppAlt.id)!);
          } catch (error) {
            console.error('Error updating team in API:', error);
          }
        }
        
        saveTeamsToLocalStorage(updatedTrupps);
      addAudit({
        type: 'Trupp gestartet',
          message: `Trupp '${statusDialog.trupp.name}' wurde in den Einsatz geschickt (Status: ${taskToUse}).`,
        shortDescription: 'In Einsatz geschickt',
        data: {
          name: statusDialog.trupp.name,
          statusOk,
              aufgabe: taskToUse,
          standort: editStandort,
          bar: editDruck,
          abfrageCountdown: 300,
          einsatzId: einsatz.id,
          truppId: statusDialog.trupp.id,
          funkkanal: funkkanal,
          alt: {
            aufgabe: truppAlt.aufgabe,
            standort: truppAlt.standort,
            bar: truppAlt.startdruck,
            funkkanal: truppAlt.funkkanal
          },
          neu: {
            aufgabe: taskToUse,
            standort: editStandort,
            bar: editDruck,
            funkkanal: funkkanal
          }
        }
      });
      setStatusDialog({ open: false, trupp: null });
      setStatusStep(getFirstConfiguredStatusStep());
      setIsBearbeitenDialog(false);
      return;
      }
    }
    
    // Wenn es ein Bearbeitungsdialog ist und der Trupp bereits im Wartemodus ist, 
    // dann nur die Daten aktualisieren ohne den Status zu ändern
    if (isBearbeitenDialog && truppAlt.aufgabe === 'Warten') {
  
      const updatedTrupps = trupps.map(t =>
        t.id === truppAlt.id
          ? {
              ...t,
              standort: editStandort,
              aufgabe: 'Warten', // Behalte Warten-Status - IGNORIERE editAufgabe
              startdruck: editDruck, // BEIDE Felder setzen bei Bearbeitung
              currentPressure: editDruck, // BEIDE Felder setzen bei Bearbeitung
              mitglieder: t.mitglieder.map(m => ({ ...m, druck: editDruck })),
              funkkanal: funkkanal,
              // WICHTIG: Behalte alle Timer-Werte unverändert und stelle sicher, dass der Trupp im Wartemodus bleibt
              abfrageCountdown: t.abfrageCountdown,
              abfrageResetAt: t.abfrageResetAt,
              createdAt: undefined, // Keine Startzeit setzen = Wartemodus
              startedAt: undefined, // Keine Einsatzzeit = Wartemodus - use undefined to send NULL to database
              pausedAt: t.pausedAt,
              pausedElapsed: t.pausedElapsed,
              statusOk: undefined, // undefined = Wartemodus
              mayday: t.mayday,
              outOfService: t.outOfService, // Behalte outOfService-Status
            }
          : t
      );
      setTrupps(updatedTrupps);
      
      // Update API
      if (isAuthenticatedSync()) {
        try {
          await updateTeamInAPI(updatedTrupps.find(t => t.id === truppAlt.id)!);
        } catch (error) {
          console.error('Error updating team in API:', error);
        }
      }
      
      saveTeamsToLocalStorage(updatedTrupps);
      addAudit({
        type: 'Bearbeiten',
        message: `Bearbeiten: Trupp '${statusDialog.trupp.name}' wurde bearbeitet (bleibt im Wartemodus).`,
        shortDescription: 'Bearbeitung',
        data: { 
          name: statusDialog.trupp.name, 
          aufgabe: 'Warten', 
          standort: editStandort, 
          bar: editDruck, 
          einsatzId: einsatz.id, 
          truppId: statusDialog.trupp.id, 
          funkkanal: funkkanal 
        }
      });
      setStatusDialog({ open: false, trupp: null });
      setStatusStep(getFirstConfiguredStatusStep());
      setIsBearbeitenDialog(false);
      return;
    }
    
    // Wenn von Warten auf aktiv gewechselt wird: Fortsetzen oder neu starten
    // ABER NUR wenn es KEIN Bearbeitungsdialog ist
    if (!isBearbeitenDialog && truppAlt.aufgabe === 'Warten' && taskToUse !== 'Warten') {
      // Prüfen ob es ein reaktivierter Trupp ist (hat originalAufgabe aber keine pausedAt oder war outOfService)
      const isReactivated = truppAlt.pausedAt || truppAlt.outOfService;
      
      if (isReactivated) {
        // Reaktivierter Trupp: Komplett neu starten - BEIDE Druck-Felder setzen
        const updatedTrupps = trupps.map(t =>
          t.id === truppAlt.id
          ? {
              ...t,
              standort: editStandort,
              aufgabe: taskToUse,
              startdruck: editDruck, // BEIDE Felder setzen bei Reaktivierung
              currentPressure: editDruck, // BEIDE Felder setzen bei Reaktivierung
              mitglieder: t.mitglieder.map(m => ({ ...m, druck: editDruck })),
              abfrageCountdown: 300,
              abfrageResetAt: now,
              createdAt: now, // Neue Startzeit
              startedAt: now, // Einsatzzeit beginnt jetzt
              pausedAt: undefined,
              pausedElapsed: undefined,
              outOfService: false, // Mark as active again
              originalAufgabe: undefined, // Clear original task
              statusOk,
              mayday: false,
              funkkanal: funkkanal,
            }
            : t
        );
        setTrupps(updatedTrupps);
        
        // Update API
        if (isAuthenticatedSync()) {
          try {
            await updateTeamInAPI(updatedTrupps.find(t => t.id === truppAlt.id)!);
          } catch (error) {
            console.error('Error updating team in API:', error);
          }
        }
        
        saveTeamsToLocalStorage(updatedTrupps);
        addAudit({
          type: 'Trupp gestartet',
          message: `Trupp '${statusDialog.trupp.name}' wurde in den Einsatz geschickt (Status: ${taskToUse}).`,
          shortDescription: 'In Einsatz geschickt',
          data: {
            name: statusDialog.trupp.name,
            statusOk,
            aufgabe: taskToUse,
            standort: editStandort,
            bar: editDruck,
            abfrageCountdown: 300,
            einsatzId: einsatz.id,
            truppId: statusDialog.trupp.id,
            funkkanal: funkkanal,
            alt: {
              aufgabe: truppAlt.aufgabe,
              standort: truppAlt.standort,
              bar: truppAlt.startdruck,
              funkkanal: truppAlt.funkkanal
            },
            neu: {
              aufgabe: taskToUse,
              standort: editStandort,
              bar: editDruck,
              funkkanal: funkkanal
            }
          }
        });
        setStatusDialog({ open: false, trupp: null });
        setStatusStep(getFirstConfiguredStatusStep());
        setIsBearbeitenDialog(false);
        return;
      } else {
        // Pausierter Trupp: Fortsetzen - BEIDE Druck-Felder setzen
      const pausedElapsed = truppAlt.pausedElapsed || 0;
      const newCreatedAt = now - pausedElapsed * 1000;
      const newStartedAt = now - pausedElapsed * 1000;
        const updatedTrupps = trupps.map(t =>
        t.id === truppAlt.id
        ? {
            ...t,
            standort: editStandort,
            aufgabe: taskToUse,
            startdruck: editDruck, // BEIDE Felder setzen bei Fortsetzung
            currentPressure: editDruck, // BEIDE Felder setzen bei Fortsetzung
            mitglieder: t.mitglieder.map(m => ({ ...m, druck: editDruck })),
              abfrageCountdown: 300,
            abfrageResetAt: now,
              createdAt: newCreatedAt,
            startedAt: newStartedAt,
              pausedAt: undefined,
              pausedElapsed: undefined,
            statusOk,
              mayday: false,
              funkkanal: funkkanal,
            }
          : t
        );
        setTrupps(updatedTrupps);
        
        // Update API
        if (isAuthenticatedSync()) {
          try {
            await updateTeamInAPI(updatedTrupps.find(t => t.id === truppAlt.id)!);
          } catch (error) {
            console.error('Error updating team in API:', error);
          }
        }
        
        saveTeamsToLocalStorage(updatedTrupps);
      addAudit({
        type: 'Trupp gestartet',
          message: `Trupp '${statusDialog.trupp.name}' wurde in den Einsatz geschickt (Status: ${taskToUse}).`,
        shortDescription: 'In Einsatz geschickt',
        data: {
          name: statusDialog.trupp.name,
          statusOk,
              aufgabe: taskToUse,
          standort: editStandort,
          bar: editDruck,
          abfrageCountdown: 300,
          einsatzId: einsatz.id,
          truppId: statusDialog.trupp.id,
          funkkanal: funkkanal,
          alt: {
            aufgabe: truppAlt.aufgabe,
            standort: truppAlt.standort,
            bar: truppAlt.startdruck,
            funkkanal: truppAlt.funkkanal
          },
          neu: {
            aufgabe: taskToUse,
            standort: editStandort,
            bar: editDruck,
            funkkanal: funkkanal
          }
        }
      });
        setStatusDialog({ open: false, trupp: null });
        setStatusStep(getFirstConfiguredStatusStep());
        setIsBearbeitenDialog(false);
        return;
      }
    }
    
    // Standard-Statusupdate
    const updatedTrupps = trupps.map(t =>
      t.id === statusDialog.trupp!.id
        ? {
            ...t,
            standort: editStandort,
            aufgabe: taskToUse,
            currentPressure: editDruck, // Aktuellen Druck auch setzen
            mitglieder: t.mitglieder.map(m => ({ ...m, druck: editDruck })),
            // Abfrage-Timer korrekt zurücksetzen: abfrageResetAt auf jetzt setzen, abfrageCountdown bleibt 300
            abfrageCountdown: 300,
            abfrageResetAt: now,
            statusOk,
            mayday: false,
            funkkanal: funkkanal,
          }
        : t
    );
    setTrupps(updatedTrupps);
    
    // Update API
    if (isAuthenticatedSync()) {
      try {
        await updateTeamInAPI(updatedTrupps.find(t => t.id === statusDialog.trupp!.id)!);
      } catch (error) {
        console.error('Error updating team in API:', error);
      }
    }
    
    saveTeamsToLocalStorage(updatedTrupps);
    // Verbleibende Zeit berechnen
    const abfrageResetAt = statusDialog.trupp.abfrageResetAt || statusDialog.trupp.createdAt || now;
    const abfrageStart = typeof statusDialog.trupp.abfrageCountdown === 'number' ? statusDialog.trupp.abfrageCountdown : 300;
    const abfrageCountdown = abfrageStart - Math.floor((now - abfrageResetAt) / 1000);
    addAudit({
      type: isBearbeitenDialog ? 'Bearbeiten' : 'Status geändert',
      message: isBearbeitenDialog
        ? `Bearbeiten: Trupp '${statusDialog.trupp.name}' wurde bearbeitet.`
        : `Status geändert: Trupp '${statusDialog.trupp.name}' Status wurde geändert: ${statusOk ? 'OK' : 'Nicht OK'}.`,
      shortDescription: isBearbeitenDialog ? 'Bearbeitung' : 'Statusupdate',
      data: {
        name: statusDialog.trupp.name,
        statusOk,
              aufgabe: taskToUse,
        standort: editStandort,
        bar: editDruck,
        abfrageCountdown,
        einsatzId: einsatz.id,
        truppId: statusDialog.trupp.id,
        funkkanal: funkkanal,
        alt: {
          aufgabe: statusDialog.trupp.aufgabe,
          standort: statusDialog.trupp.standort,
          bar: statusDialog.trupp.startdruck,
          funkkanal: statusDialog.trupp.funkkanal
        },
        neu: {
              aufgabe: taskToUse,
          standort: editStandort,
          bar: editDruck,
          funkkanal: funkkanal
        }
      }
    });
    
    // Füge Feedback als separaten Auditlog-Eintrag hinzu, wenn eines ausgewählt wurde
    if (selectedFeedback) {
      addAudit({
        type: 'Rückmeldung',
        message: selectedFeedback,
        shortDescription: 'Rückmeldung',
        data: {
          name: statusDialog.trupp.name,
          feedback: selectedFeedback,
          einsatzId: einsatz.id,
          truppId: statusDialog.trupp.id
        }
      });
    }
    
    if (statusDialog.resetAbfrage) statusDialog.resetAbfrage();
    setStatusDialog({ open: false, trupp: null });
    setStatusStep(getFirstConfiguredStatusStep());
    setIsBearbeitenDialog(false);
    setSelectedFeedback(''); // Reset feedback selection
  };

  // Trupp als aus dem Einsatz markieren
  const handleOutOfService = (id: number) => {
    const trupp = trupps.find(t => t.id === id);
    if (trupp) {
      setFinalPressure(trupp.currentPressure || trupp.startdruck);
      setShowPressureAdjustModal({ open: true, trupp, mode: 'out' });
    }
  };

  const handlePressureAdjust = (trupp: Trupp) => {
    const isImEinsatz = !trupp.outOfService && trupp.aufgabe !== 'Warten' && !!(trupp.createdAt || trupp.startedAt);
    if (!isImEinsatz) return;
    setFinalPressure(trupp.currentPressure || trupp.startdruck);
    setShowPressureAdjustModal({ open: true, trupp, mode: 'adjust' });
  };

  const reorderTrupps = (fromId: number, toId: number) => {
    if (fromId === toId) return;
    const current = truppsRef.current;
    const fromIndex = current.findIndex((t) => t.id === fromId);
    const toIndex = current.findIndex((t) => t.id === toId);
    if (fromIndex < 0 || toIndex < 0) return;
    const next = [...current];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    const ordered = next.map((t, index) => ({ ...t, displayOrder: index }));
    setTrupps(ordered);
    if (einsatz?.id) writeTruppOrder(einsatz.id, ordered.map((t) => t.id));
    saveTeamsToLocalStorage(ordered);
    addAudit({
      type: 'Trupp verschoben',
      message: `Trupp '${moved.name}' an Position ${toIndex + 1} verschoben.`,
      shortDescription: 'Trupp verschoben',
      data: { name: moved.name, truppId: moved.id, einsatzId: einsatz?.id, fromIndex: fromIndex + 1, toIndex: toIndex + 1 }
    });
  };

  const handleReorderPointerDown = (truppId: number, e: React.PointerEvent) => {
    if (localEinsatzleiterMode || einsatzGeschlossen) return;
    const pointerId = e.pointerId;
    const startX = e.clientX;
    const startY = e.clientY;
    let active = false;
    const handle = e.currentTarget as HTMLElement;
    handle.setPointerCapture(pointerId);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';

    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      const distance = Math.hypot(ev.clientX - startX, ev.clientY - startY);
      if (!active && distance < 8) return;
      if (!active) {
        active = true;
        setDraggingTruppId(truppId);
      }
      ev.preventDefault();
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const tile = el?.closest('[data-trupp-id]') as HTMLElement | null;
      const overId = tile ? Number(tile.dataset.truppId) : NaN;
      setDropTargetTruppId(Number.isFinite(overId) && overId !== truppId ? overId : null);
    };

    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      try { handle.releasePointerCapture(pointerId); } catch {}
      if (active) {
        const el = document.elementFromPoint(ev.clientX, ev.clientY);
        const tile = el?.closest('[data-trupp-id]') as HTMLElement | null;
        const toId = tile ? Number(tile.dataset.truppId) : NaN;
        if (Number.isFinite(toId)) reorderTrupps(truppId, toId);
      }
      setDraggingTruppId(null);
      setDropTargetTruppId(null);
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  // Trupp reaktivieren
  const handleReactivateTrupp = async (id: number) => {
    const trupp = trupps.find(t => t.id === id);

    const updatedTrupps = trupps.map(t =>
      t.id === id
        ? {
            ...t,
            outOfService: false, // Mark as active again
            // Set back to waiting status
            aufgabe: 'Warten',
            // Reset currentPressure to original startdruck when reactivating
            currentPressure: t.originalStartdruck || t.startdruck,
            // Reset timer to start at 00:00 when reactivating
            createdAt: Date.now(), // Set to current time when reactivating
            startedAt: undefined, // Reset startedAt - will be set when trupp starts again
            einsatzCount: (t.einsatzCount || 1) + 1,
            abfrageCountdown: 300,
            abfrageResetAt: Date.now(),
            statusOk: undefined, // Reset to undefined (Warten status)
            mayday: false,
          }
        : t
    );
    const reactivated = updatedTrupps.find(t => t.id === id);
    if (einsatz?.id && reactivated) {
      upsertTruppEinsatzCount(einsatz.id, reactivated.id, reactivated.einsatzCount || 1);
    }
    setTrupps(updatedTrupps);
    
    // Update API immediately to ensure createdAt is synchronized
    if (isAuthenticatedSync()) {
      try {
        await updateTeamInAPI(updatedTrupps.find(t => t.id === id)!);

      } catch (error) {
        console.error('❌ Fehler beim Senden der Reaktivierung an API:', error);
      }
    }
    
    saveTeamsToLocalStorage(updatedTrupps);
    if (trupp) addAudit({
      type: 'Trupp reaktiviert',
      message: `Trupp '${trupp.name}' wurde reaktiviert und wartet auf Start.`,
      shortDescription: 'Trupp reaktiviert',
      data: {
        name: trupp.name,
        einsatzId: einsatz.id,
        truppId: trupp.id,
        einsatzCount: (trupp.einsatzCount || 1) + 1,
        alt: {
          aufgabe: trupp.aufgabe,
          standort: trupp.standort,
          bar: trupp.startdruck,
          funkkanal: trupp.funkkanal
        }
      }
    });
  };

  const handleEmergencyChange = (trupp: Trupp, emergency: boolean) => {
    if (trupp.outOfService) return;
    const updatedTrupps = trupps.map(t =>
      t.id === trupp.id ? { ...t, emergencyTrupp: emergency ? 1 : 0 } : t
    );
    setTrupps(updatedTrupps);
    saveTeamsToLocalStorage(updatedTrupps);
    const updated = updatedTrupps.find(t => t.id === trupp.id);
    if (updated && isAuthenticatedSync()) {
      updateTeamInAPI(updated).catch(error => {
        console.error('Error updating emergency trupp:', error);
      });
    }
    addAudit({
      type: emergency ? 'Notfalltrupp' : 'Normaltrupp',
      message: emergency
        ? `Trupp '${trupp.name}' wurde als Notfalltrupp markiert.`
        : `Trupp '${trupp.name}' wurde als Normaltrupp markiert.`,
      shortDescription: emergency ? 'Notfalltrupp' : 'Normaltrupp',
      data: {
        name: trupp.name,
        truppId: trupp.id,
        einsatzId: einsatz?.id,
        emergencyTrupp: emergency ? 1 : 0
      }
    });
  };

  // Alarm stummschalten/aktivieren
  const handleMuteToggle = (id: number) => {
    const trupp = trupps.find(t => t.id === id);
    const newMutedState = !trupp?.alarmMuted;
    
    const updatedTrupps = trupps.map(t =>
      t.id === id
        ? { ...t, alarmMuted: newMutedState, alarmMutedAt: newMutedState ? Date.now() : undefined }
        : t
    );
    setTrupps(updatedTrupps);
    saveTeamsToLocalStorage(updatedTrupps);
    
    if (trupp) {
      addAudit({
        type: newMutedState ? 'Alarm stummgeschaltet' : 'Alarm aktiviert',
        message: newMutedState ? '<b>Alarm stummgeschaltet</b>: Alarm wurde manuell stummgeschaltet.' : '<b>Alarm aktiviert</b>: Alarm wurde manuell aktiviert.',
        shortDescription: newMutedState ? 'Alarm stumm' : 'Alarm aktiv',
        data: {
          name: trupp.name,
          einsatzId: einsatz?.id,
          truppId: trupp.id
        }
      });
    }
  };

  // Countdown-Änderungen speichern
  const handleCountdownChange = (id: number, abfrage: number) => {
    setTrupps(trupps => {
      const now = Date.now();
      const updated = trupps.map(t => {
        if (t.id === id) {
          let abfrageResetAt = t.abfrageResetAt;
          // Wenn zurückgesetzt wird, auch Reset-Timestamp aktualisieren
          if (abfrage === 300 && t.abfrageCountdown !== 300) abfrageResetAt = now;
          return { ...t, abfrageCountdown: abfrage, abfrageResetAt };
        }
        return t;
      });
      saveTeamsToLocalStorage(updated);
      return updated;
    });
  };

  const handleMitglieder = (trupp: Trupp) => {
    // Validierung: Truppführer-Index darf nicht größer als Mitglieder-Anzahl sein
    const validTruppfuehrerIndex = trupp.mitglieder.length > 0 
      ? Math.min(trupp.truppfuehrerIndex || 0, trupp.mitglieder.length - 1) 
      : 0;
    

    
    setMitgliederDialog({ open: true, trupp });
    setEditMitglieder(trupp.mitglieder);
    setEditTruppfuehrerIndex(validTruppfuehrerIndex);
    // Initialisiere Autocomplete-State
    setMitgliederAutocompleteValue('');
    setMitgliederAutocompleteUsers([]);
    setMitgliederAutocompleteShowList(false);
    setMitgliederAutocompleteSelectedUser(null);
  };

  const handleMitgliedRemove = (idx: number) => {
    setEditMitglieder(editMitglieder.filter((_, i) => i !== idx));
    if (editTruppfuehrerIndex === idx) setEditTruppfuehrerIndex(null);
    else if (editTruppfuehrerIndex !== null && editTruppfuehrerIndex > idx) setEditTruppfuehrerIndex(editTruppfuehrerIndex - 1);
  };

  // Autocomplete-Funktionen für Mitglieder-Dialog
  const handleMitgliederAutocompleteChange = (value: string) => {
    setMitgliederAutocompleteValue(value);
    
    if (value.trim()) {
      const searchTerm = value.toLowerCase().trim();
      
      // Benutzer, die bereits in einem ANDEREN Trupp sind, ausschließen
      // Der aktuell bearbeitete Trupp wird ausgeschlossen (mitgliederModal.trupp?.id)
      const currentTruppId = mitgliederModal.trupp?.id;
      const usersInOtherTrupps = getUsersAlreadyInTrupps(currentTruppId);
      
      // Benutzer, die bereits im aktuell bearbeiteten Trupp sind, ausschließen
      const usersInCurrentEdit = new Set(
        editMitglieder.map(m => `${m.vorname.toLowerCase()}|${m.nachname.toLowerCase()}`)
      );
      
      const filtered = localUsers.filter(user => {
        const userKey = `${user.firstName.toLowerCase()}|${user.lastName.toLowerCase()}`;
        // Truppüberwacher (lokal + andere Geräte) dürfen nicht Truppmitglied sein
        if (isBlockedSupervisorUser(user)) return false;
        // Prüfe, ob Benutzer bereits in einem anderen Trupp ist
        if (usersInOtherTrupps.has(userKey)) return false;
        // Prüfe, ob Benutzer bereits im aktuell bearbeiteten Trupp ist
        if (usersInCurrentEdit.has(userKey)) return false;
        
        // Suchfilter anwenden
        return user.firstName.toLowerCase().includes(searchTerm) ||
        user.lastName.toLowerCase().includes(searchTerm) ||
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm) ||
          `${user.lastName} ${user.firstName}`.toLowerCase().includes(searchTerm);
      });
      
      setMitgliederAutocompleteUsers(filtered);
      setMitgliederAutocompleteShowList(filtered.length > 0);
    } else {
      setMitgliederAutocompleteUsers([]);
      setMitgliederAutocompleteShowList(false);
    }
  };

  const handleMitgliederAutocompleteSelect = (user: User) => {
    const userKey = `${user.firstName.toLowerCase()}|${user.lastName.toLowerCase()}`;
    if (isBlockedSupervisorUser(user)) return;
    
    // Check if user is already in the current team
    const isAlreadyInTeam = editMitglieder.some(m => 
      m.vorname === user.firstName && m.nachname === user.lastName
    );
    
    // Check if user is already in another trupp
    const currentTruppId = mitgliederModal.trupp?.id;
    const usersInOtherTrupps = getUsersAlreadyInTrupps(currentTruppId);
    const isInOtherTrupp = usersInOtherTrupps.has(userKey);
    
    if (!isAlreadyInTeam && !isInOtherTrupp) {
      setEditMitglieder([...editMitglieder, { 
        vorname: user.firstName, 
        nachname: user.lastName 
      }]);
    }
    
    setMitgliederAutocompleteValue('');
    setMitgliederAutocompleteUsers([]);
    setMitgliederAutocompleteShowList(false);
    setMitgliederAutocompleteSelectedUser(null);
  };

  const handleMitgliederAutocompleteAdd = () => {
    const trimmedValue = mitgliederAutocompleteValue.trim();
    if (!trimmedValue) return;
    
    // Check if it's a valid name (at least two parts)
    const nameParts = trimmedValue.split(' ').filter(part => part.length > 0);
    if (nameParts.length < 2) return;
    
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');
    
    const userKey = `${firstName.toLowerCase()}|${lastName.toLowerCase()}`;
    const currentUserKey = getCurrentUserKey();
    if (currentUserKey && userKey === currentUserKey) return;
    if (allOperationSupervisorKeys.has(userKey)) return;
    
    // Check if user is already in the current team
    const isAlreadyInTeam = editMitglieder.some(m => 
      m.vorname === firstName && m.nachname === lastName
    );
    
    // Check if user is already in another trupp
    const currentTruppId = mitgliederModal.trupp?.id;
    const usersInOtherTrupps = getUsersAlreadyInTrupps(currentTruppId);
    const isInOtherTrupp = usersInOtherTrupps.has(userKey);
    
    if (!isAlreadyInTeam && !isInOtherTrupp) {
      setEditMitglieder([...editMitglieder, { 
        vorname: firstName, 
        nachname: lastName 
      }]);
    }
    
    setMitgliederAutocompleteValue('');
    setMitgliederAutocompleteUsers([]);
    setMitgliederAutocompleteShowList(false);
    setMitgliederAutocompleteSelectedUser(null);
  };

  const handleMitgliederAutocompleteKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (mitgliederAutocompleteUsers.length > 0) {
        handleMitgliederAutocompleteSelect(mitgliederAutocompleteUsers[0]);
      } else {
        handleMitgliederAutocompleteAdd();
      }
    }
  };

  const handleMitgliederSave = () => {
    if (!mitgliederDialog.trupp || editTruppfuehrerIndex === null) return;
    const tf = editMitglieder[editTruppfuehrerIndex];
    const newName = truppNamingMode === 'leader'
      ? (tf ? buildPrefixedTruppName(`Trupp ${tf.nachname}`) : mitgliederDialog.trupp.name)
      : mitgliederDialog.trupp.name;
    const oldMitglieder = mitgliederDialog.trupp.mitglieder;
    const oldTF = mitgliederDialog.trupp.truppfuehrerIndex;
    // Druck für alle Mitglieder setzen, falls nicht vorhanden
    const truppStartdruck = mitgliederDialog.trupp.startdruck || 300;
    const editMitgliederMitDruck = editMitglieder.map(m => ({ ...m, druck: typeof m.druck === 'number' ? m.druck : truppStartdruck }));
    const updatedTrupps = trupps.map(t =>
      t.id === mitgliederDialog.trupp!.id
        ? { ...t, mitglieder: editMitgliederMitDruck, truppfuehrerIndex: editTruppfuehrerIndex, name: newName }
        : t
    );
    setTrupps(updatedTrupps);
    saveTeamsToLocalStorage(updatedTrupps);
    
    // Update API with member changes
    if (isAuthenticatedSync()) {
      const updatedTrupp = updatedTrupps.find(t => t.id === mitgliederDialog.trupp!.id);
      if (updatedTrupp) {
        updateTeamInAPI(updatedTrupp).catch(error => {
          console.error('Error updating team members in API:', error);
        });
      }
    }
    
    // Auditlog: Mitglieder geändert
    const removed = oldMitglieder.filter((m, i) => !editMitglieder.some((em) => em.vorname === m.vorname && em.nachname === m.nachname));
    const added = editMitglieder.filter((m) => !oldMitglieder.some((om) => om.vorname === m.vorname && om.nachname === m.nachname));
    if (removed.length > 0 || added.length > 0) {
      addAudit({
        type: 'Mitglieder geändert',
        message: `Mitglieder von '${mitgliederDialog.trupp.name}' geändert. Hinzugefügt: ${added.map(m => m.vorname + ' ' + m.nachname).join(', ') || '-'}, Entfernt: ${removed.map(m => m.vorname + ' ' + m.nachname).join(', ') || '-'}.`,
        data: { name: mitgliederDialog.trupp.name, added, removed, einsatzId: einsatz.id }
      });
    }
    // Auditlog: Truppführer geändert
    if (oldTF !== editTruppfuehrerIndex) {
      addAudit({
        type: 'Truppführer geändert',
        message: `Truppführer von '${mitgliederDialog.trupp.name}' geändert.`,
        data: { name: mitgliederDialog.trupp.name, alterTF: oldMitglieder[oldTF], neuerTF: editMitglieder[editTruppfuehrerIndex], einsatzId: einsatz.id }
      });
    }
    setMitgliederDialog({ open: false, trupp: null });
  };

  // Einsatz bearbeiten
  const handleEditEinsatz = async () => {
    // Operations Types neu laden, um sicherzustellen, dass die aktuellsten Daten verfügbar sind
    try {
      const types = await syncOperationsTypes();
      if (types) {
        setOperationsTypes(types);
      }
    } catch (error) {
      console.warn('Failed to reload operations types:', error);
    }
    
    // Einsatz aus localStorage neu laden, um sicherzustellen, dass die neuesten Daten verwendet werden
    // (wichtig, falls der Standort im Hintergrund aktualisiert wurde)
    const einsaetze = JSON.parse(localStorage.getItem('einsaetze') || '[]');
    const remoteEinsaetze = JSON.parse(localStorage.getItem('remoteEinsaetze') || '[]');
    const currentEinsatz = 
      einsaetze.find((e: any) => String(e.id) === String(id)) ||
      remoteEinsaetze.find((e: any) => String(e.id ?? e.opsID ?? e.name) === String(id));
    
    // Einsatz-State aktualisieren, falls neuerer Standort gefunden wurde
    if (currentEinsatz && currentEinsatz.ort && currentEinsatz.ort !== 'Unbekannt') {
      if (!einsatz || einsatz.ort === 'Unbekannt' || einsatz.location === 'Unbekannt') {
        setEinsatz(currentEinsatz);
      }
    }
    
    // Verwende den aktuellsten Einsatz (aus State oder localStorage)
    const einsatzToUse = currentEinsatz || einsatz;
    
    // Korrekte Zuordnung der Einsatztyp-Felder
    const currentCategoryID = einsatzToUse?.categoryID || einsatzToUse?.type || null;
    const currentCategoryName = einsatzToUse?.category || einsatzToUse?.typ || '';
    
    // Wenn wir eine categoryID haben, den entsprechenden Typ-Namen finden
    let correctTypName = currentCategoryName;
    if (currentCategoryID && operationsTypes.length > 0) {
      const matchingType = operationsTypes.find(t => t.id === currentCategoryID);
      if (matchingType) {
        correctTypName = matchingType.name;
      }
    }
    
    setEditEinsatzTyp(correctTypName);
    // Verwende den aktuellsten Standort
    const currentOrt = einsatzToUse?.ort || einsatzToUse?.location || '';
    setEditEinsatzOrt(currentOrt);
    setEditEinsatzCategoryID(currentCategoryID);
    setEinsatzEditModalOpen(true);
  };

  // Einsatz bearbeiten speichern
  const handleSaveEinsatzEdit = async () => {
    if (!einsatz) return;

    try {
      const updatedTitle = `${editEinsatzOrt}: ${editEinsatzTyp}`;
      // Lokal speichern
      const updatedEinsatz = {
        ...einsatz,
        typ: editEinsatzTyp,
        ort: editEinsatzOrt,
        location: editEinsatzOrt,
        title: updatedTitle,
        name: updatedTitle,
        alertTitle: editEinsatzTyp,
        categoryID: editEinsatzCategoryID,
        category: editEinsatzTyp // Verwende den tatsächlichen Typ-Namen
      };

      // Lokale Einsätze aktualisieren
      const einsaetze = JSON.parse(localStorage.getItem('einsaetze') || '[]');
      const updatedEinsaetze = einsaetze.map((e: any) => 
        String(e.id) === String(id) ? updatedEinsatz : e
      );
      localStorage.setItem('einsaetze', JSON.stringify(updatedEinsaetze));

      // Remote-Einsätze aktualisieren
      const remoteEinsaetze = JSON.parse(localStorage.getItem('remoteEinsaetze') || '[]');
      const updatedRemoteEinsaetze = remoteEinsaetze.map((e: any) => 
        String(e.id ?? e.opsID ?? e.name) === String(id) ? updatedEinsatz : e
      );
      localStorage.setItem('remoteEinsaetze', JSON.stringify(updatedRemoteEinsaetze));

      // State aktualisieren
      setEinsatz(updatedEinsatz);

      // API Update (falls online und Remote-Einsatz)
      if (navigator.onLine && einsatz.remote !== false) {
        const accessToken = await getAccessTokenAsync();
        if (accessToken) {
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            const orgID = payload.orgID;

            const requestBody = {
              orgID,
              title: `${editEinsatzOrt}: ${editEinsatzTyp}`,
              location: editEinsatzOrt,
              category: editEinsatzTyp,
              alertTitle: editEinsatzTyp,
              type: editEinsatzCategoryID || editEinsatzTyp,
              description: `${editEinsatzTyp} an ${editEinsatzOrt}`
            };

            const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.OPERATIONS_UPDATE(einsatz.id)), {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(requestBody)
            });

            if (response.ok) {
        
            } else {
              const errorText = await response.text();
              console.warn('⚠️ API Update fehlgeschlagen, aber lokal gespeichert:', response.status, errorText);
            }
          } catch (error) {
            console.warn('⚠️ API Update fehlgeschlagen, aber lokal gespeichert:', error);
          }
        }
      }

      // Audit-Log hinzufügen
      addAudit({
        type: 'Einsatz bearbeitet',
        message: `Einsatz wurde bearbeitet.`,
        data: {
          einsatzId: einsatz.id,
          name: einsatz.name,
          oldTyp: einsatz.typ,
          newTyp: editEinsatzTyp,
          oldOrt: einsatz.ort,
          newOrt: editEinsatzOrt
        }
      });

      setEinsatzEditModalOpen(false);
    } catch (error) {
      console.error('Fehler beim Bearbeiten des Einsatzes:', error);
    }
  };

  // Funktion zum Zurücksetzen des Trupp Übersicht Modus
  const resetEinsatzleiterMode = () => {
    setEinsatzleiterMode(false);
    setLocalEinsatzleiterMode(false);
    console.log('🔄 Trupp Übersicht Modus zurückgesetzt');
  };

  // Einsatz löschen
  const handleDeleteEinsatz = () => {
    setShowDeleteEinsatzModal(true);
  };
  // Wirklich löschen (aus Modal)
  const reallyDeleteEinsatz = () => {
    // 1. Einsatz aus lokalen Listen entfernen
    const einsaetze = JSON.parse(localStorage.getItem('einsaetze') || '[]').filter((e: any) => String(e.id) !== String(id));
    localStorage.setItem('einsaetze', JSON.stringify(einsaetze));
    const remoteEinsaetze = JSON.parse(localStorage.getItem('remoteEinsaetze') || '[]').filter((e: any) => String(e.id ?? e.opsID ?? e.name) !== String(id));
    localStorage.setItem('remoteEinsaetze', JSON.stringify(remoteEinsaetze));

    // 2. Auditlog zu diesem Einsatz entfernen (wie bisher)
    const auditlogAll = JSON.parse(localStorage.getItem('auditlog') || '[]');
    const auditlogFiltered = auditlogAll.filter((entry: any) => entry.data?.einsatzId !== einsatz?.id);
    localStorage.setItem('auditlog', JSON.stringify(auditlogFiltered));

    // 3. Alle lokalen Daten zu diesem Einsatz entfernen
    Object.keys(localStorage).forEach(key => {
      if (key.includes(String(id))) {
        localStorage.removeItem(key);
      }
    });

    // 4. Trupp Übersicht Modus zurücksetzen
    resetEinsatzleiterMode();

    // 5. Navigation zurück
    navigate('/dashboard');
  };

  // State: Zeige Mitglieder oder nur Anzahl?
  const [showMitglieder, setShowMitglieder] = React.useState(() => window.innerWidth > 600);
  const truppsContainerRef = React.useRef<HTMLDivElement>(null);
  const [twoRows, setTwoRows] = React.useState(false);
  const truppRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  React.useEffect(() => {
    const container = truppsContainerRef.current;
    if (!container) return;

    // Wenn mehr als 4 Trupps (also mehr als eine Zeile), dann kompakte Ansicht
    if (trupps.length > 4) {
      setShowMitglieder(false);
      setTwoRows(true);
    } else {
      setShowMitglieder(true);
      setTwoRows(false);
    }
  }, [trupps.length]);

  // Desktop-Grid (≤4): gleiche Höhe. Mobile: nie fixe Höhen – sonst überdeckt Expand die Nachbarn.
  React.useEffect(() => {
    const resetHeights = () => {
      const container = truppsContainerRef.current;
      if (!container) return;
      Array.from(container.children).forEach((child) => {
        const el = child as HTMLElement;
        el.style.height = 'auto';
        el.style.maxHeight = 'none';
        el.style.minHeight = '0';
      });
    };

    const equalizeHeights = () => {
      const container = truppsContainerRef.current;
      if (!container) return;
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      resetHeights();
      if (isMobile || trupps.length === 0 || trupps.length > 4) return;

      const children = Array.from(container.children).filter(
        (child) => (child as HTMLElement).offsetParent !== null
      ) as HTMLElement[];
      if (children.length === 0) return;
      const maxHeight = Math.max(...children.map((child) => child.offsetHeight));
      children.forEach((child) => { child.style.height = `${maxHeight}px`; });
    };

    const timer = window.setTimeout(equalizeHeights, 50);
    const onExpand = () => {
      // Nach Expand/Collapse: natürliche Höhe, Layout neu berechnen
      resetHeights();
      window.requestAnimationFrame(() => resetHeights());
    };
    const onResize = () => { window.setTimeout(equalizeHeights, 50); };
    window.addEventListener('truppMobileExpand', onExpand as EventListener);
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('truppMobileExpand', onExpand as EventListener);
      window.removeEventListener('resize', onResize);
    };
  }, [trupps.length, showMitglieder, trupps]);

  // Modal für Mitgliederanzeige in kompakter Ansicht
  const [mitgliederModal, setMitgliederModal] = React.useState<{ open: boolean; trupp: Trupp | null }>({ open: false, trupp: null });

  // Button-Style für alle Modals - Tutorial-Stil
  const modalButtonStyle = {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: '#6c757d',
    color: 'white'
  };
  const modalButtonPrimaryStyle = {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: '#c00',
    color: 'white'
  };
  
  // Button-Style für deaktivierte Primary-Buttons
  const modalButtonPrimaryDisabledStyle = {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 500,
    cursor: 'not-allowed',
    transition: 'all 0.2s ease',
    background: '#ccc',
    color: '#666'
  };

  // Kein Login-Check mehr nötig - der Interceptor handhabt alles automatisch

  // State für Bearbeiten-Dialog
  const [isBearbeitenDialog, setIsBearbeitenDialog] = React.useState(false);

  // Funktion für Bearbeiten im Wartemodus (öffnet Statusdialog, aber Auditlog-Eintrag ist 'Bearbeiten')
  const handleBearbeitenTrupp = async (trupp: Trupp) => {
    // Feedbacks + aktuelle Screen-Reihenfolge vor dem Öffnen laden
    loadFeedbacks();
    const latestScreens = await loadStatusUpdateScreensSetting();
    
    setStatusDialog({ open: true, trupp });
    setStatusStep(getInitialStatusStep(trupp, false, latestScreens));
    setStatusOk(trupp.statusOk !== false);
    setEditStandort(trupp.standort);
    setEditAufgabe(trupp.aufgabe);
    setEditDruck(trupp.currentPressure || trupp.startdruck);
    setFunkkanal(trupp.funkkanal || '');
    setFunkkanalSelected(!!trupp.funkkanal);
    setIsBearbeitenDialog(true);
  };

  const handleOpenRenameTrupp = (trupp: Trupp) => {
    setRenameTruppModal({ open: true, trupp });
    setRenameTruppValue(trupp.name || '');
  };

  const handleSaveTruppRename = async () => {
    if (!renameTruppModal.trupp) return;
    const newName = renameTruppValue.trim();
    if (!newName) return;
    const trupp = renameTruppModal.trupp;
    const updatedTrupps = trupps.map(t => t.id === trupp.id ? { ...t, name: newName } : t);
    setTrupps(updatedTrupps);
    saveTeamsToLocalStorage(updatedTrupps);
    if (isAuthenticatedSync()) {
      try {
        await updateTeamInAPI(updatedTrupps.find(t => t.id === trupp.id)!);
      } catch (error) {
        console.error('Error updating team name in API:', error);
      }
    }
    addAudit({
      type: 'Trupp umbenannt',
      message: `Trupp '${trupp.name}' wurde umbenannt zu '${newName}'.`,
      shortDescription: 'Trupp umbenannt',
      data: { truppId: trupp.id, alt: trupp.name, neu: newName, einsatzId: einsatz.id }
    });
    setRenameTruppModal({ open: false, trupp: null });
    setRenameTruppValue('');
  };

  // PDF Export Funktion (identisch mit Web-Version)
  const handleExportPDF = () => {
    // Hilfsfunktion für deutsches Datums- und Zeitformat
    const formatDateTimeDE = (date: Date | null) => {
      if (!date) return '-';
      const d = date.getDate().toString().padStart(2, '0');
      const m = (date.getMonth() + 1).toString().padStart(2, '0');
      const y = date.getFullYear();
      const h = date.getHours().toString().padStart(2, '0');
      const min = date.getMinutes().toString().padStart(2, '0');
      const s = date.getSeconds().toString().padStart(2, '0');
      return `${d}.${m}.${y} ${h}:${min}:${s}`;
    };

    // Einsatzdaten mit Fallbacks
    const einsatzName = einsatz.name || einsatz.title || `${einsatz.ort || einsatz.location}: ${einsatz.alertTitle || ''}`.trim() || 'Unbekannter Einsatz';
    const einsatzOrt = einsatz.ort || einsatz.location || 'Unbekannt';

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    let y = 20;
    
    // Farben
    const red = '#c00';
    const gray = '#e0e0e0';
    
    // Titel
    doc.setFontSize(22);
    doc.setTextColor(red);
    doc.text('Einsatzbericht', 148.5, y, { align: 'center' }); // Mitte für landscape
    y += 12;
    
    doc.setDrawColor(gray);
    doc.setLineWidth(0.5);
    doc.line(20, y, 277, y); // volle Breite landscape
    y += 6;
    
    // Einsatzdaten
    doc.setFontSize(13);
    doc.setTextColor(0, 0, 0);
    doc.text(`Einsatzname:`, 20, y); 
    doc.setFont('helvetica', 'bold'); 
    doc.text(`${einsatzName}`, 60, y); 
    doc.setFont('helvetica', 'normal'); 
    y += 7;
    
    doc.text(`Ort:`, 20, y); 
    doc.setFont('helvetica', 'bold'); 
    doc.text(`${einsatzOrt}`, 60, y); 
    doc.setFont('helvetica', 'normal'); 
    y += 7;
    
    // Einsatz Start = frühester "Trupp gestartet" Zeitpunkt
    const truppGestartetTimestamps = auditlog
      .filter((l: AuditLogEntry) => l.type === 'Trupp gestartet' && l.timestamp)
      .map((l: AuditLogEntry) => l.timestamp);
    
    const einsatzStart = truppGestartetTimestamps.length > 0 
      ? new Date(Math.min(...truppGestartetTimestamps)) 
      : null;
    
    // Einsatz Ende = spätester Audit-Log-Eintrag
    const alleTimestamps = auditlog
      .filter((l: AuditLogEntry) => l.timestamp)
      .map((l: AuditLogEntry) => l.timestamp);
    
    const einsatzEnde = alleTimestamps.length > 0 ? new Date(Math.max(...alleTimestamps)) : null;
    
    doc.text(`Einsatz Start:`, 20, y); 
    doc.setFont('helvetica', 'bold'); 
    doc.text(`${formatDateTimeDE(einsatzStart)}`, 60, y); 
    doc.setFont('helvetica', 'normal'); 
    y += 7;
    
    doc.text(`Einsatz Ende:`, 20, y); 
    doc.setFont('helvetica', 'bold'); 
    doc.text(`${formatDateTimeDE(einsatzEnde)}`, 60, y); 
    doc.setFont('helvetica', 'normal'); 
    y += 7;
    
    doc.text(`Anzahl Trupps:`, 20, y); 
    doc.setFont('helvetica', 'bold'); 
    doc.text(`${trupps.length}`, 60, y); 
    doc.setFont('helvetica', 'normal'); 
    y += 10;
    
    // Trennlinie
    doc.setDrawColor(gray); 
    doc.line(20, y, 277, y); 
    y += 6;
    
    // Trupps
    trupps.forEach((trupp, idx) => {
      // Trupp-Logs filtern und sortieren
      const truppLogs = auditlog
        .filter((l: AuditLogEntry) => 
          l.data?.truppId === trupp.id && 
          l.type !== 'Abfrage Alarm' && 
          l.type !== 'Restzeit Alarm'
        )
        .sort((a: AuditLogEntry, b: AuditLogEntry) => (a.timestamp || 0) - (b.timestamp || 0));
      
      // Platzbedarf für einen Trupp-Eintrag abschätzen
      const baseHeight = 8 + 6 + 6 + 8 + 8 + 10; // Kopf, Mitglieder, Start, Dauer, Truppüberwacher, Abstand
      const statusTableHead = truppLogs.length > 0 ? 6 : 0;
      const statusTableRows = truppLogs.length * 8 + (truppLogs.length > 0 ? 4 : 0);
      const trennlinie = 8;
      const truppBlockHeight = baseHeight + statusTableHead + statusTableRows + trennlinie;
      
      if (y + truppBlockHeight > 190) { 
        doc.addPage(); 
        y = 20; 
      }
      
      const isEmergency = trupp.emergencyTrupp === 1;
      const truppDisplayName = isEmergency ? 'Notfalltrupp' : trupp.name;
      
      doc.setFontSize(15); 
      doc.setTextColor(red); 
      doc.text(`Trupp ${idx + 1}: ${truppDisplayName}`, 20, y); 
      y += 8;
      
      doc.setFontSize(11); 
      doc.setTextColor(0, 0, 0);
      
      // Mitglieder
      const mitgliederText = trupp.mitglieder.map(m => `${m.vorname} ${m.nachname}`).join(', ') || 'Keine Mitglieder';
      doc.text(`Mitglieder:`, 22, y); 
      doc.setFont('helvetica', 'bold'); 
      doc.text(mitgliederText, 60, y); 
      doc.setFont('helvetica', 'normal'); 
      y += 6;
      
      // Einsatzstart pro Trupp = frühester "Trupp gestartet" Log-Eintrag
      const startLog = truppLogs.find((l: AuditLogEntry) => 
        l.type === 'Trupp gestartet' || 
        l.shortDescription === 'Trupp gestartet' ||
        l.message?.includes('Trupp gestartet')
      );
      const truppStart = startLog?.timestamp || null;
      
      // Start
      doc.text(`Start:`, 22, y); 
      doc.setFont('helvetica', 'bold'); 
      doc.text(truppStart ? formatDateTimeDE(new Date(truppStart)) : '-', 60, y); 
      doc.setFont('helvetica', 'normal'); 
      y += 6;
      let truppEnd = null;
      
      if (truppLogs.length > 0) {
        const teamTimestamps = truppLogs
          .filter((l: AuditLogEntry) => l.timestamp)
          .map((l: AuditLogEntry) => l.timestamp);
        
        if (teamTimestamps.length > 0) {
          truppEnd = Math.max(...teamTimestamps);
        }
      }
      
      doc.text(`Einsatzdauer:`, 22, y); 
      doc.setFont('helvetica', 'bold');
      if (truppStart && truppEnd && truppEnd > truppStart) {
        const dauer = Math.floor((truppEnd - truppStart) / 1000);
        const min = Math.floor(dauer / 60).toString().padStart(2, '0');
        const sec = (dauer % 60).toString().padStart(2, '0');
        doc.text(`${min}:${sec} min`, 60, y); 
      } else {
        doc.text(`00:00 min`, 60, y); 
      }
      doc.setFont('helvetica', 'normal'); 
      y += 6;
      
      // Truppüberwacher – aus Logs und Truppdaten extrahieren (unique) mit Kürzeln
      const resolveLogSupervisorName = (log: AuditLogEntry): string | null => {
        if (typeof log.data?.performedBy === 'string' && log.data.performedBy.trim() !== '') {
          return log.data.performedBy.trim();
        }
        if (typeof log.performedBy === 'string' && log.performedBy.trim() !== '') {
          return log.performedBy.trim();
        }
        return null;
      };

      let truppDefaultSupervisor: string | null = null;
      if (typeof trupp.supervisorName === 'string' && trupp.supervisorName.trim() !== '') {
        truppDefaultSupervisor = trupp.supervisorName.trim();
      } else if (trupp.creator?.firstName && trupp.creator?.lastName) {
        truppDefaultSupervisor = `${trupp.creator.firstName} ${trupp.creator.lastName}`.trim();
      } else if (typeof trupp.creatorID === 'number') {
        const creatorUser = localUsers.find(u => u.id === trupp.creatorID);
        if (creatorUser) {
          truppDefaultSupervisor = `${creatorUser.firstName} ${creatorUser.lastName}`.trim();
        }
      }

      const supervisorMap = new Map<string, string>(); // name → TU01, TU02, ...
      truppLogs.forEach((log: AuditLogEntry) => {
        const name = resolveLogSupervisorName(log);
        if (name && !supervisorMap.has(name)) {
          const nr = (supervisorMap.size + 1).toString().padStart(2, '0');
          supervisorMap.set(name, `TU${nr}`);
        }
      });

      // Fallback: wenn Logs keinen Benutzer enthalten, Trupp-Creator als Überwacher verwenden
      if (supervisorMap.size === 0 && truppDefaultSupervisor) {
        supervisorMap.set(truppDefaultSupervisor, 'TU01');
      }
      
      if (supervisorMap.size > 0) {
        const legendParts = Array.from(supervisorMap.entries()).map(([name, code]) => `${name} (${code})`);
        doc.text(`Truppüberwacher:`, 22, y);
        doc.setFont('helvetica', 'bold');
        doc.text(legendParts.join(', '), 60, y, { maxWidth: 210 });
        doc.setFont('helvetica', 'normal');
        y += 6;
      }
      
      y += 4; // Kleiner Abstand vor der Tabelle
      
      // Statusupdates als Tabelle
      if (truppLogs.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.text('Zeit', 22, y);
        doc.text('Änderung', 64, y);
        doc.text('Standort', 112, y);
        doc.text('Aufgabe', 148, y);
        doc.text('Druck', 184, y);
        doc.text('Funkkanal', 204, y);
        doc.text('TU', 240, y);
        doc.setFont('helvetica', 'normal');
        y += 6;
        
        // Für Druck, Funkkanal & Standort: letzten bekannten Wert merken
        let lastDruck = typeof trupp.startdruck === 'number' ? trupp.startdruck : undefined;
        let lastFunkkanal = trupp.funkkanal || undefined;
        let lastStandort = trupp.standort || '-';
        let lastAufgabe = '-';
        
        truppLogs.forEach((log: AuditLogEntry) => {
          if (y > 190) { 
            doc.addPage(); 
            y = 20; 
          }
          
          // logData (in der PWA ist log.data bereits ein Objekt)
          const parsedLogData = log.data || null;
          
          // Zeit
          const zeit = log.timestamp ? formatDateTimeDE(new Date(log.timestamp)) : '-';
          doc.text(zeit, 22, y, { maxWidth: 40 });
          
          // Änderungstext im PDF: logType verwenden
          const logType = log.type || '';
          doc.text(logType.charAt(0).toUpperCase() + logType.slice(1), 64, y, { maxWidth: 46 });
          
          // Standort
          let standort = lastStandort;
          if (parsedLogData) {
            if (typeof parsedLogData.standort === 'string' && parsedLogData.standort.trim() !== '') {
              standort = parsedLogData.standort;
            } else if (typeof parsedLogData.location === 'string' && parsedLogData.location.trim() !== '') {
              standort = parsedLogData.location;
            }
          }
          lastStandort = standort;
          doc.text(standort, 112, y, { maxWidth: 34 });
          
          // Aufgabe
          let aufgabe = lastAufgabe;
          if (parsedLogData) {
            if (typeof parsedLogData.aufgabe === 'string' && parsedLogData.aufgabe.trim() !== '') {
              aufgabe = parsedLogData.aufgabe;
            } else if (typeof parsedLogData.task === 'string' && parsedLogData.task.trim() !== '') {
              aufgabe = parsedLogData.task;
            }
          }
          lastAufgabe = aufgabe;
          doc.text(`${aufgabe || '-'}`, 148, y, { maxWidth: 34 });
          
          // Druck
          let druck = lastDruck;
          if (parsedLogData) {
            if (typeof parsedLogData.bar === 'number') {
              druck = parsedLogData.bar;
            } else if (typeof parsedLogData.finalPressure === 'number') {
              druck = parsedLogData.finalPressure;
            }
          }
          lastDruck = druck;
          doc.text((typeof druck === 'number' ? druck + ' Bar' : '-'), 184, y, { maxWidth: 18 });
          
          // Funkkanal
          let funkkanal = lastFunkkanal;
          if (parsedLogData) {
            if (typeof parsedLogData.funkkanal === 'string' && parsedLogData.funkkanal.trim() !== '') {
              funkkanal = parsedLogData.funkkanal;
            } else if (typeof parsedLogData.funkkanal === 'number') {
              funkkanal = String(parsedLogData.funkkanal);
            }
          }
          lastFunkkanal = funkkanal;
          doc.text((typeof funkkanal === 'string' && funkkanal.trim() !== '' ? funkkanal : '-'), 204, y, { maxWidth: 34 });
          
          // Überwacher (performedBy → Kürzel TU01, TU02, ...)
          let tuCode = '-';
          const supervisorName = resolveLogSupervisorName(log) || truppDefaultSupervisor;
          if (supervisorName) {
            tuCode = supervisorMap.get(supervisorName) || '-';
          }
          doc.text(tuCode, 240, y, { maxWidth: 18 });
          y += 6;
          
          // Feedback Sub-Zeile (falls vorhanden)
          let feedback = '';
          if (parsedLogData) {
            if (typeof parsedLogData.feedback === 'string' && parsedLogData.feedback.trim() !== '') {
              feedback = parsedLogData.feedback.trim();
            } else if (typeof parsedLogData.Feedback === 'string' && parsedLogData.Feedback.trim() !== '') {
              feedback = parsedLogData.Feedback.trim();
            }
          }
          if (feedback) {
            if (y > 190) { doc.addPage(); y = 20; }
            // Kleine Linie als Verbindung zum Eintrag darüber
            doc.setDrawColor(180, 180, 180);
            doc.setLineWidth(0.3);
            doc.line(26, y - 3.5, 26, y - 1);
            doc.line(26, y - 1, 30, y - 1);
            doc.setFontSize(9);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(120, 120, 120);
            doc.text(`Feedback: ${feedback}`, 32, y, { maxWidth: 228 });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(0, 0, 0);
            doc.setDrawColor(0, 0, 0);
            y += 7;
          }
        });
        y += 4;
      }
      
      // Trennlinie
      doc.setDrawColor(gray); 
      doc.line(22, y, 270, y); 
      y += 8;
    });
    
    // Footer auf allen Seiten
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      
      // Footer-Linie
      doc.setDrawColor(gray);
      doc.setLineWidth(0.2);
      doc.line(20, pageHeight - 20, pageWidth - 20, pageHeight - 20);
      
      // Footer-Text
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      
      // Links: "Generiert vom ASmonitor"
      doc.text('Generiert vom ASmonitor', 20, pageHeight - 12);
      
      // Mitte: Aktuelles Datum
      const now = new Date();
      const currentDate = `${now.getDate().toString().padStart(2, '0')}.${(now.getMonth() + 1).toString().padStart(2, '0')}.${now.getFullYear()}`;
      doc.text(currentDate, pageWidth / 2, pageHeight - 12, { align: 'center' });
      
      // Rechts: Seitenzahl
      doc.text(`Seite ${i} von ${pageCount}`, pageWidth - 20, pageHeight - 12, { align: 'right' });
    }
    
    doc.save(`Einsatzbericht_${einsatzName.replace(/\s+/g, '_')}.pdf`);
  };

  // 1. State für Einsatz geschlossen und Modal
  const [einsatzGeschlossen, setEinsatzGeschlossen] = React.useState(false);
  const [showEinsatzBeendenModal, setShowEinsatzBeendenModal] = React.useState(false);

  // Globale Einstellung: Maximale Trupps pro Einsatz
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [maxTrupps, setMaxTrupps] = React.useState(() => {
    // Bevorzugt den Key aus dem Settings-Modal ('maxTrupps'), fallback auf alten Key
    const v = Number(localStorage.getItem('maxTrupps')) || Number(localStorage.getItem('maxTruppsProEinsatz'));
    return v > 0 ? v : 8;
  });
  React.useEffect(() => {
    // Schreibe beide Keys, um Kompatibilität zu wahren
    localStorage.setItem('maxTrupps', String(maxTrupps));
    localStorage.setItem('maxTruppsProEinsatz', String(maxTrupps));
  }, [maxTrupps]);
  React.useEffect(() => {
    // Reagiere auf Änderungen aus dem Settings-Modal (auch in anderen Tabs)
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'maxTrupps' || e.key === 'maxTruppsProEinsatz') {
        const v = Number(localStorage.getItem('maxTrupps')) || Number(localStorage.getItem('maxTruppsProEinsatz'));
        if (v > 0 && v !== maxTrupps) setMaxTrupps(v);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [maxTrupps]);
  const truppLimitReached = trupps.length >= maxTrupps;

  // 1. State für Delete-Modal
  const [showDeleteEinsatzModal, setShowDeleteEinsatzModal] = React.useState(false);

  // State für Trupp-Lösch-Modal
  const [showDeleteTruppModal, setShowDeleteTruppModal] = React.useState<{ open: boolean, trupp: Trupp | null }>({ open: false, trupp: null });
  const [showTruppTypeModal, setShowTruppTypeModal] = React.useState<{ open: boolean; trupp: Trupp | null }>({ open: false, trupp: null });

  const reallyDeleteTrupp = () => {
    if (showDeleteTruppModal.trupp) {
      handleDeleteTrupp(showDeleteTruppModal.trupp.id);
      setShowDeleteTruppModal({ open: false, trupp: null });
    }
  };

  // State für Tasks
  const [availableTasks, setAvailableTasks] = React.useState<string[]>(['Warten']);

  // Tasks laden und State aktualisieren
  const loadTasks = async (): Promise<void> => {
    try {
      // Zuerst aus localStorage versuchen
      const standardTasks = JSON.parse(localStorage.getItem('standardTasks') || '[]');
      if (standardTasks.length > 0) {
        setAvailableTasks([...standardTasks, 'Warten']);
        return;
      }

      // Falls keine Tasks im localStorage, von API laden
      const tasks = await syncTasks();
      if (tasks && tasks.length > 0) {
        const taskNames = tasks.map((t: any) => t.name);
        setAvailableTasks([...taskNames, 'Warten']);
        console.log('Tasks loaded from API:', taskNames.length);
      } else {
        setAvailableTasks(['Warten']);
      }
    } catch (error) {
      console.warn('Failed to load tasks:', error);
      setAvailableTasks(['Warten']);
    }
  };

  // Dynamische Tasks aus State laden (reaktiv)
  const getAufgaben = (): string[] => {
    return availableTasks;
  };

  // State für Standorte
  const [availableStandorte, setAvailableStandorte] = React.useState<string[]>([]);

  // State für Funkkanäle
  const [availableFunkkanaele, setAvailableFunkkanaele] = React.useState<string[]>([]);

  // State für Feedbacks
  const [availableFeedbacks, setAvailableFeedbacks] = React.useState<string[]>([]);

  // Standorte laden und State aktualisieren
  const loadStandorte = async (): Promise<void> => {
    try {
      // Zuerst aus localStorage versuchen
      const standardStandorte = JSON.parse(localStorage.getItem('standardStandorte') || '[]');
      if (standardStandorte.length > 0) {
        setAvailableStandorte(standardStandorte);
        return;
      }

      // Falls keine Standorte im localStorage, von API laden
      const locations = await syncLocations();
      if (locations && locations.length > 0) {
        const locationNames = locations.map((l: any) => l.name);
        setAvailableStandorte(locationNames);
        console.log('Standorte loaded from API:', locationNames.length);
      } else {
        setAvailableStandorte([]);
      }
    } catch (error) {
      console.warn('Failed to load standorte:', error);
      setAvailableStandorte([]);
    }
  };

  // Funkkanäle laden und State aktualisieren
  const loadFunkkanaele = async (): Promise<void> => {
    try {
      // Zuerst aus localStorage versuchen
      const standardFunkkanaele = JSON.parse(localStorage.getItem('standardFunkkanaele') || '[]');
      if (standardFunkkanaele.length > 0) {
        setAvailableFunkkanaele(standardFunkkanaele);
        return;
      }

      // Falls keine Funkkanäle im localStorage, von API laden
      const channels = await syncRadioChannels();
      if (channels && channels.length > 0) {
        const channelNames = channels.map((c: any) => c.name);
        setAvailableFunkkanaele(channelNames);
        console.log('Funkkanäle loaded from API:', channelNames.length);
      } else {
        setAvailableFunkkanaele([]);
      }
    } catch (error) {
      console.warn('Failed to load funkkanaele:', error);
      setAvailableFunkkanaele([]);
    }
  };

  // Dynamische Standorte aus State laden (reaktiv)
  const getStandorte = (): string[] => {
    return availableStandorte;
  };

  // Dynamische Funkkanäle aus State laden (reaktiv)
  const getFunkkanaele = (): string[] => {
    return availableFunkkanaele;
  };

  // Dynamische Feedbacks aus State laden (reaktiv)
  const getFeedbacks = (): string[] => {
    return availableFeedbacks;
  };

  // Feedbacks laden und State aktualisieren
  const loadFeedbacks = async (): Promise<void> => {
    try {
      // Zuerst aus localStorage versuchen
      const standardFeedbacks = JSON.parse(localStorage.getItem('standardFeedbacks') || '[]');
      if (standardFeedbacks.length > 0) {
        setAvailableFeedbacks(standardFeedbacks);
        return;
      }

      // Falls keine Feedbacks im localStorage, von API laden
      const feedbacks = await syncFeedbacks();
      if (feedbacks && feedbacks.length > 0) {
        const feedbackTexts = feedbacks.map((f: any) => f.text);
        setAvailableFeedbacks(feedbackTexts);
        console.log('Feedbacks loaded from API:', feedbackTexts.length);
      } else {
        setAvailableFeedbacks([]);
      }
    } catch (error) {
      console.warn('Failed to load feedbacks:', error);
      setAvailableFeedbacks([]);
    }
  };

  // OperationsTypes und Tasks laden
  React.useEffect(() => {
    const loadOperationsTypes = async () => {
      try {
        const types = await syncOperationsTypes();
        const allTypes = await syncAllOperationsTypes();
        if (types) {
          setOperationsTypes(types);
        }
        if (allTypes) {
          setAllOperationsTypes(allTypes);
        }
      } catch (error) {
        console.warn('Failed to load operations types:', error);
      }
    };
    loadOperationsTypes();
    
    // Alle Standard-Werte beim ersten Laden laden
    loadTasks();
    loadStandorte();
    loadFunkkanaele();
    loadFeedbacks();
  }, []);

  // Lokaler Trupp Übersicht Modus State (synchronisiert mit globalem)
  const [localEinsatzleiterMode, setLocalEinsatzleiterMode] = React.useState(false);
  const [einsatzleiterLoading, setEinsatzleiterLoading] = React.useState(false);
  const [truppsLoading, setTruppsLoading] = React.useState(true); // Neuer Loading-State für normale Benutzer
  const [truppsError, setTruppsError] = React.useState<string | null>(null); // Neuer Error-State

  // Alle verfügbaren Einsatztypen (für Einstellungen)
  const [allOperationsTypes, setAllOperationsTypes] = React.useState<any[]>([]);

  // Permission states
  const [canAccessSettingsState, setCanAccessSettingsState] = React.useState(false);
  const [canManageReportsState, setCanManageReportsState] = React.useState(false);
  const [canManageOperationsState, setCanManageOperationsState] = React.useState(false);
  const [canManageTeamsState, setCanManageTeamsState] = React.useState(false);


  // Helper function to get Truppüberwacher name by supervisorName
  const getTruppueberwacherName = (supervisorName?: string): string => {
    console.log('🔍 getTruppueberwacherName called with supervisorName:', supervisorName);
    
    if (!supervisorName) {
      console.log('❌ No supervisorName provided');
      return 'Unbekannt';
    }
    
    console.log('✅ Using supervisorName directly:', supervisorName);
    return supervisorName;
  };

  // Wrapper function for TruppKachel (converts creatorID to supervisorName for compatibility)
  const getTruppueberwacherNameForKachel = (creatorID?: number): string => {
    const trupp = trupps.find(t => t.creatorID === creatorID && t.supervisorName?.trim());
    if (trupp?.supervisorName?.trim()) return trupp.supervisorName.trim();
    if (creatorID == null) return 'Unbekannt';
    return `ID: ${creatorID}`;
  };

  const resolveTruppSupervisorName = (trupp: Trupp): string => {
    const direct = (trupp.supervisorName || '').trim();
    if (direct) return direct;
    if (trupp.creator?.firstName || trupp.creator?.lastName) {
      return `${trupp.creator.lastName || ''} ${trupp.creator.firstName || ''}`.trim();
    }
    return getTruppueberwacherNameForKachel(trupp.creatorID);
  };

  // Stabile, eindeutige Farben pro Truppüberwacher im aktuellen Einsatz
  const supervisorColorIndexByName = React.useMemo(() => {
    const unique = Array.from(
      new Set(trupps.map((t) => resolveTruppSupervisorName(t)).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, 'de'));
    const map = new Map<string, number>();
    unique.forEach((name, index) => map.set(name, index));
    return map;
  }, [trupps]);

  const getSupervisorColor = (supervisorName: string): { background: string; border: string; text: string } => {
    const colors = [
      { background: '#e8f5e8', border: '#4caf50', text: '#2e7d32' }, // Grün
      { background: '#e3f2fd', border: '#2196f3', text: '#1976d2' }, // Blau
      { background: '#fff3e0', border: '#ff5722', text: '#d84315' }, // Orange
      { background: '#f3e5f5', border: '#9c27b0', text: '#7b1fa2' }, // Violett
      { background: '#e0f2f1', border: '#009688', text: '#00695c' }, // Türkis
      { background: '#fce4ec', border: '#e91e63', text: '#c2185b' }, // Pink
      { background: '#fff8e1', border: '#ff9800', text: '#f57c00' }, // Amber
      { background: '#e8eaf6', border: '#3f51b5', text: '#303f9f' }, // Indigo
      { background: '#f1f8e9', border: '#8bc34a', text: '#689f38' }, // Hellgrün
      { background: '#efebe9', border: '#795548', text: '#5d4037' }, // Braun
    ];
    const index = supervisorColorIndexByName.get(supervisorName) ?? 0;
    return colors[index % colors.length];
  };

  // Synchronisiere lokalen State mit globalem
  React.useEffect(() => {
    setLocalEinsatzleiterMode(einsatzleiterMode);
  }, [einsatzleiterMode]);

  // Check permissions
  React.useEffect(() => {
    const checkPermissions = async () => {
      console.log('🔐 EinsatzDetail: Checking permissions...');
      const [settingsPermission, reportsPermission, operationsPermission, teamsPermission] = await Promise.all([
        canAccessSettings(),
        canManageReports(),
        canManageOperations(),
        canManageTeams()
      ]);
      console.log('🔐 EinsatzDetail: Settings permission:', settingsPermission);
      console.log('🔐 EinsatzDetail: Reports permission:', reportsPermission);
      console.log('🔐 EinsatzDetail: Operations permission:', operationsPermission);
      console.log('🔐 EinsatzDetail: Teams permission:', teamsPermission);
      setCanAccessSettingsState(settingsPermission);
      setCanManageReportsState(reportsPermission);
      setCanManageOperationsState(operationsPermission);
      setCanManageTeamsState(teamsPermission);
    };
    
    checkPermissions();
  }, []);

  // Handler für Trupp Übersicht Modus Änderungen
  const handleEinsatzleiterModeChange = (newMode: boolean) => {
    if (newMode) {
      // Beim Wechsel in Trupp Übersicht Modus Loading anzeigen
      setEinsatzleiterLoading(true);
    }
    setLocalEinsatzleiterMode(newMode);
    setEinsatzleiterMode(newMode);
  };

  // Mayday-Benachrichtigungen werden von MaydayNotificationManager verwaltet

  // Teams neu laden wenn sich der Trupp Übersicht Modus ändert
  React.useEffect(() => {
    if (einsatz && isAuthenticatedSync()) {
      // Im Trupp Übersicht Modus Loading-Spinner aktivieren
      if (localEinsatzleiterMode) {
        setEinsatzleiterLoading(true);
      }
      loadTeamsFromAPI(einsatz.id, einsatz, localEinsatzleiterMode);
    }
  }, [localEinsatzleiterMode]);

  // Im Trupp Übersicht Modus alle 15 Sekunden Teams von der API laden
  React.useEffect(() => {
    if (!localEinsatzleiterMode || !einsatz || !isAuthenticatedSync()) {
      return;
    }


    
    // Sofort laden
    loadTeamsFromAPI(einsatz.id, einsatz, localEinsatzleiterMode);
    
    // Dann alle 15 Sekunden
    const interval = setInterval(() => {
  
      loadTeamsFromAPI(einsatz.id, einsatz, localEinsatzleiterMode);
    }, 15000);

    return () => {
  
      clearInterval(interval);
    };
  }, [localEinsatzleiterMode, einsatz?.id]);

  // Mayday-Benachrichtigungen verwalten
  // Mayday-Benachrichtigungen werden von MaydayNotificationManager verwaltet

  // Hilfsfunktion: Mayday-Status beenden
  const endMaydayStatus = async (trupp: Trupp) => {
    try {
      
      const updatedTrupp = {
        ...trupp,
        mayday: false,
        maydayTriggeredBy: undefined,
        maydayTriggeredAt: undefined
      };
      
      // Lokalen State aktualisieren
      setTrupps(prev => prev.map(t => t.id === trupp.id ? updatedTrupp : t));
      
      // An API senden
      await updateTeamInAPI(updatedTrupp);
      
      addAudit({ 
        type: 'Mayday', 
        message: `Mayday-Alarm für '${trupp.name}' beendet.`, 
        shortDescription: 'Mayday Alarm beendet', 
        data: { truppId: trupp.id, name: trupp.name, einsatzId: einsatz.id } 
      });
      
    } catch (error) {
      console.error('❌ Fehler beim Beenden des Mayday-Status:', error);
    }
  };

  const getCurrentSupervisorCreatorId = (): number | undefined => {
    if (isTechAccount) {
      const realUser = getRealUserIdentity();
      if (realUser?.id) return realUser.id;
    }
    const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    if (!accessToken) return undefined;
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      return typeof payload.id === 'number' ? payload.id : undefined;
    } catch {
      return undefined;
    }
  };

  const getMyActiveTruppsForBulkChannel = (): Trupp[] => {
    const myId = getCurrentSupervisorCreatorId();
    return trupps.filter((t) => {
      if (t.outOfService) return false;
      if (myId !== undefined && typeof t.creatorID === 'number') {
        return t.creatorID === myId;
      }
      // Fallback im Normalmodus: geladene Trupps gehören dem aktuellen Überwacher
      return !localEinsatzleiterMode;
    });
  };

  const handleBulkFunkkanalChange = async (selectedTruppIds: number[], newChannel: string) => {
    if (!newChannel.trim() || selectedTruppIds.length === 0 || !einsatz) return;

    setBulkFunkkanalSaving(true);
    try {
      const selectedSet = new Set(selectedTruppIds);
      const updatedTrupps = trupps.map((t) =>
        selectedSet.has(t.id) ? { ...t, funkkanal: newChannel.trim() } : t
      );
      setTrupps(updatedTrupps);
      saveTeamsToLocalStorage(updatedTrupps);

      for (const trupp of updatedTrupps.filter((t) => selectedSet.has(t.id))) {
        if (isAuthenticatedSync()) {
          try {
            await updateTeamInAPI(trupp);
          } catch (error) {
            console.error(`Fehler beim Aktualisieren des Funkkanals für ${trupp.name}:`, error);
          }
        }
        addAudit({
          type: 'Funkkanal',
          message: `Funkkanal von Trupp '${trupp.name}' auf '${newChannel.trim()}' geändert.`,
          shortDescription: 'Funkkanal gewechselt',
          data: {
            name: trupp.name,
            truppId: trupp.id,
            einsatzId: einsatz.id,
            funkkanal: newChannel.trim(),
          },
        });
      }

      setShowBulkFunkkanalModal(false);
    } finally {
      setBulkFunkkanalSaving(false);
    }
  };

  // Hilfsfunktion: Remote-Einsatz schliessen
  const closeRemoteEinsatz = async () => {
    console.log('🔄 closeRemoteEinsatz called');
    const accessToken = await getAccessTokenAsync();
    let orgID = null;
    let userID = null;
    
    // Debug: Token prüfen
    if (!accessToken) {
      console.error('closeRemoteEinsatz: Kein accessToken gefunden');
      alert('Kein Access Token gefunden. Bitte melden Sie sich erneut an.');
      return false;
    }
    
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      orgID = payload.orgID;
      userID = payload.id;
    } catch (error) {
      console.error('closeRemoteEinsatz: Fehler beim Decodieren des Tokens:', error);
      alert('Ungültiger Access Token. Bitte melden Sie sich erneut an.');
      return false;
    }
    
    if (!orgID || !userID || !einsatz?.id) {
      console.error('closeRemoteEinsatz: Fehlende Parameter:', { orgID, userID, einsatzId: einsatz?.id });
      alert('Fehlende Parameter für das Schliessen des Einsatzes.');
      return false;
    }
    
    try {
      const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.OPERATIONS_CLOSE), {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          orgID, 
          opsID: einsatz.id, 
          userID,
          closeDate: new Date().toISOString().split('T')[0] // Send current date in YYYY-MM-DD format
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('closeRemoteEinsatz: Response error:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      return true;
    } catch (e) {
      console.error('closeRemoteEinsatz: Exception:', e);
      const errorMessage = e instanceof Error ? e.message : 'Unbekannter Fehler';
      alert(`Fehler beim Schliessen des Remote-Einsatzes: ${errorMessage}`);
      return false;
    }
  };

  // State für Druckanpassung beim Aus-Einsatz-Nehmen
  const [showPressureAdjustModal, setShowPressureAdjustModal] = React.useState<{ open: boolean; trupp: Trupp | null; mode?: 'out' | 'adjust' }>({ open: false, trupp: null, mode: 'out' });
  const [finalPressure, setFinalPressure] = React.useState(300);

  // Mayday Dialog
  const [maydayOpen, setMaydayOpen] = React.useState(false);
  const [maydayStep, setMaydayStep] = React.useState(1);
  const [maydayTrupp, setMaydayTrupp] = React.useState<Trupp | null>(null);
  const [isMobileLayout, setIsMobileLayout] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = () => setIsMobileLayout(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Online/Offline status listener
  useEffect(() => {
    const handleOnline = async () => {
      console.log('🌐 App is back online, syncing local data first...');
      if (einsatz && isAuthenticatedSync()) {
        try {
        // First, sync ALL teams that have been modified locally (both new local teams and existing API teams)
        // We need to sync both:
        // 1. New local teams (ID >= 1000000)
        // 2. Existing API teams that were modified offline (ID < 1000000 but have local changes)
        
        // Get all teams that need syncing
        const teamsToSync = trupps.filter(team => {
          // Always sync new local teams
          if (team.id >= 1000000) return true;
          
          // For existing API teams, check if they have been modified locally
          // by looking for audit logs that reference this team
          const auditLogs = JSON.parse(localStorage.getItem('auditlog') || '[]');
          const hasLocalChanges = auditLogs.some((log: any) => log.data?.truppId === team.id);
          return hasLocalChanges;
        });
        
        console.log(`🔄 Syncing ${teamsToSync.length} teams to server (${teamsToSync.filter(t => t.id >= 1000000).length} new local, ${teamsToSync.filter(t => t.id < 1000000).length} existing API with changes)...`);
        
        for (const teamToSync of teamsToSync) {
                      try {
              console.log(`📤 Syncing team "${teamToSync.name}" (ID: ${teamToSync.id}) to server...`);
              console.log(`📋 Team data to sync:`, {
                name: teamToSync.name,
                aufgabe: teamToSync.aufgabe,
                startedAt: teamToSync.startedAt,
                createdAt: teamToSync.createdAt,
                currentPressure: teamToSync.currentPressure,
                mayday: teamToSync.mayday,
                outOfService: teamToSync.outOfService
              });
              
              // Check if this team already exists on the server (has been synced before)
              // If it has a remote ID (stored in localStorage), use updateTeamInAPI
              // Otherwise, use saveTeamToAPI for new teams
              let syncedTeam: { id: number };
              const existingRemoteId = localStorage.getItem(`team_remote_id_${teamToSync.id}`);
              
              if (existingRemoteId) {
                console.log(`🔄 Updating existing team "${teamToSync.name}" with remote ID: ${existingRemoteId}`);
                await updateTeamInAPI({ ...teamToSync, id: parseInt(existingRemoteId) });
                syncedTeam = { id: parseInt(existingRemoteId) };
              } else if (teamToSync.id >= 1000000) {
                // New local team - create on server
                console.log(`🆕 Creating new team "${teamToSync.name}" on server`);
                syncedTeam = await saveTeamToAPI(teamToSync);
                // Store the mapping from local ID to remote ID
                localStorage.setItem(`team_remote_id_${teamToSync.id}`, syncedTeam.id.toString());
              } else {
                // Existing API team with local changes - update on server
                console.log(`🔄 Updating existing API team "${teamToSync.name}" (ID: ${teamToSync.id})`);
                await updateTeamInAPI(teamToSync);
                syncedTeam = { id: teamToSync.id };
              }
              
              console.log(`✅ Team "${teamToSync.name}" synced successfully with API ID: ${syncedTeam.id}`);
              
              // Update the team ID in the state and localStorage (only for new local teams)
              if (teamToSync.id >= 1000000) {
                const updatedTrupps = trupps.map(team => 
                  team.id === teamToSync.id ? { ...team, id: syncedTeam.id } : team
                );
                setTrupps(updatedTrupps);
                saveTeamsToLocalStorage(updatedTrupps);
                
                // Update audit logs with new team ID
                const auditLogs = JSON.parse(localStorage.getItem('auditlog') || '[]');
                const updatedAuditLogs = auditLogs.map((log: any) => {
                  if (log.data?.truppId === teamToSync.id) {
                    return {
                      ...log,
                      data: {
                        ...log.data,
                        truppId: syncedTeam.id
                      }
                    };
                  }
                  return log;
                });
                localStorage.setItem('auditlog', JSON.stringify(updatedAuditLogs));
              }
              
            } catch (error) {
              console.error(`❌ Failed to sync team "${teamToSync.name}":`, error);
            }
          }
          
          // Second, sync only truly unsynced audit logs from localStorage
          // Einträge die bereits erfolgreich an die API gesendet wurden, sind nicht mehr
          // im localStorage (werden in addAudit nach erfolgreichem API-Save entfernt).
          // Hier sind nur Einträge die offline erstellt wurden oder deren API-Save fehlschlug.
          const allLocalAuditLogs = JSON.parse(localStorage.getItem('auditlog') || '[]');
          const unsyncedAuditLogs = allLocalAuditLogs.filter((log: any) => !log._synced);
          console.log(`📝 ${allLocalAuditLogs.length} lokale Audit-Logs, davon ${unsyncedAuditLogs.length} noch nicht synchronisiert`);
          
          let syncSuccessCount = 0;
          for (const auditEntry of unsyncedAuditLogs) {
            try {
              console.log(`📤 Syncing audit log "${auditEntry.type}" to server...`);
              
              // Daten ohne interne Flags senden
              const cleanData = { ...(auditEntry.data || {}) };
              delete cleanData._synced;
              
              const logData = {
                opsID: einsatz.id,
                teamID: auditEntry.data?.truppId,
                logType: auditEntry.type,
                message: auditEntry.message,
                shortDescription: auditEntry.shortDescription,
                logData: JSON.stringify(cleanData)
              };
              
              await auditLogAPI.createAuditLog(logData);
              syncSuccessCount++;
              console.log(`✅ Audit log "${auditEntry.type}" synced successfully`);
            } catch (error) {
              console.error(`❌ Failed to sync audit log "${auditEntry.type}":`, error);
            }
          }
          
          // Nach dem Sync: localStorage komplett leeren (API ist Source of Truth)
          if (allLocalAuditLogs.length > 0) {
            localStorage.removeItem('auditlog');
            console.log(`🧹 localStorage-Auditlogs geleert (${syncSuccessCount}/${unsyncedAuditLogs.length} synchronisiert)`);
          }
          
          // Third, delete any locally deleted teams from the server
          const locallyDeletedTeams = JSON.parse(localStorage.getItem('locallyDeletedTeams') || '[]');
          console.log(`🗑️ Syncing ${locallyDeletedTeams.length} locally deleted teams to server...`);
          
          for (const deletedTeam of locallyDeletedTeams) {
            try {
              console.log(`🗑️ Deleting team "${deletedTeam.name}" (ID: ${deletedTeam.id}) from server...`);
              await deleteTeamFromAPI(deletedTeam.id);
              console.log(`✅ Team "${deletedTeam.name}" deleted from server successfully`);
            } catch (error) {
              console.error(`❌ Failed to delete team "${deletedTeam.name}" from server:`, error);
            }
          }
          
          // Clear the locally deleted teams list after successful sync
          if (locallyDeletedTeams.length > 0) {
            localStorage.removeItem('locallyDeletedTeams');
            console.log('🧹 Cleared locally deleted teams list');
          }
          
                  // Now reload all teams from API to get the latest state
          console.log('🔄 Reloading teams from API...');
        try {
          await loadTeamsFromAPI(einsatz.id, einsatz, localEinsatzleiterMode);
        } catch (error) {
          console.warn('⚠️ Failed to reload teams from API, keeping local state:', error);
        }
        
        try {
          await loadAuditLogFromAPI(einsatz.id);
        } catch (error) {
          console.warn('⚠️ Failed to reload audit log from API, keeping local state:', error);
        }
        
        // Clean up the team ID mappings after successful sync
        teamsToSync.forEach(teamToSync => {
          localStorage.removeItem(`team_remote_id_${teamToSync.id}`);
        });
        } catch (error) {
          console.error('Error during online sync:', error);
        }
      }
    };

    const handleOffline = () => {
      console.log('📡 App went offline, switching to offline mode');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [einsatz, trupps]);

  if (!einsatz) {
    return (
      <div style={{ padding: 32 }}>
        <button onClick={() => { resetEinsatzleiterMode(); navigate('/dashboard'); }} style={{ marginBottom: 16, background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px' }}>Zurück</button>
        <h2>Einsatz nicht gefunden</h2>
      </div>
    );
  }

  const headerOrt = einsatz.ort || einsatz.location || 'Unbekannt';
  const headerTyp = einsatz.typ || einsatz.category || 'Unbekannt';
  const primaryHeaderTitle = (einsatz.name || einsatz.title || '').toString().trim();
  const myActiveTruppsForBulkChannel = getMyActiveTruppsForBulkChannel();
  const canBulkChangeFunkkanal = !einsatzGeschlossen && myActiveTruppsForBulkChannel.length > 0;

  const excludedSupervisorSelectionKeys = Array.from(getUsersAlreadyInTrupps());

  return (
    <div className="einsatzdetail-outer" style={{ padding: '20px 32px 24px', marginTop: 64 }}>
      {/* Mobile: Zurück absolut links; Titel + Actions volle Breite und horizontal zentriert */}
      <div className="einsatzdetail-header-mobile" style={{ display: 'none', marginBottom: 20, position: 'relative' }}>
        <div className="einsatzdetail-header-back" style={{ position: 'absolute', left: 0, top: 0, zIndex: 2 }}>
          <button onClick={() => { resetEinsatzleiterMode(); navigate('/dashboard'); }} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            <ArrowBackIcon style={{ fontSize: 32, color: '#c00' }} />
          </button>
        </div>
        <div className="einsatzdetail-header-block" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', minWidth: 0, paddingLeft: 36, paddingRight: 36, boxSizing: 'border-box' }}>
          <div className="einsatzdetail-header-title" style={{ fontWeight: 700, fontSize: 18, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
            {primaryHeaderTitle}
          </div>
          <div style={{ fontSize: 20, color: '#444', textAlign: 'center', marginTop: 2, marginBottom: 2 }}>
            <b>Ort:</b> {headerOrt} &nbsp;|&nbsp; <b>Typ:</b> {headerTyp}
          </div>

          {/* Benutzer-Anzeige für technische Accounts (mobile) */}
          {isTechAccount && realUserName && (
            <div 
              onClick={() => { previousIdentityRef.current = getRealUserIdentity(); setShowUserIdentModal(true); }} 
              style={{ 
                display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 2, marginBottom: 2,
                cursor: 'pointer', fontSize: 12, color: '#666', 
                background: '#f0f0f0', borderRadius: 12, padding: '2px 10px 2px 8px',
                border: '1px solid #ddd'
              }}
              title="Benutzer ändern"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#888"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>
              <span style={{ fontWeight: 600 }}>{realUserName}</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="#aaa"><path d="M7 10l5 5 5-5z"/></svg>
            </div>
          )}

          <div className="einsatzdetail-header-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'center', margin: '4px 0 0 0', width: '100%' }}>
              {!localEinsatzleiterMode && (
              <button onClick={openTruppModal} title="Trupp erstellen" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, opacity: einsatzGeschlossen || truppLimitReached ? 0.5 : 1, cursor: einsatzGeschlossen || truppLimitReached ? 'not-allowed' : 'pointer' }} disabled={einsatzGeschlossen || truppLimitReached}><AddIcon /></button>
              )}
              {!localEinsatzleiterMode && (
              <button
                onClick={() => setShowBulkFunkkanalModal(true)}
                title="Funkkanal wechseln"
                style={{
                  background: canBulkChangeFunkkanal ? '#c00' : '#999',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  width: 36,
                  height: 36,
                  opacity: canBulkChangeFunkkanal ? 1 : 0.5,
                  cursor: canBulkChangeFunkkanal ? 'pointer' : 'not-allowed',
                }}
                disabled={!canBulkChangeFunkkanal}
              >
                <CellTowerIcon style={{ fontSize: 22 }} />
              </button>
              )}
              {!localEinsatzleiterMode && (
              <button onClick={() => setShowTruppAuditlog(true)} title="Trupp-Journal" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, cursor: 'pointer' }}><InfoIcon /></button>
              )}
              <button onClick={handleEditEinsatz} title="Einsatz bearbeiten" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, marginLeft: 2 }}><EditIcon /></button>
              {canAccessSettingsState && (
                <button onClick={() => setSettingsOpen(true)} title="Einstellungen" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, marginLeft: 2 }}><SettingsIcon style={{ color: '#fff' }} /></button>
              )}
              <button onClick={handleDeleteEinsatz} title="Einsatz löschen" style={{ background: '#c00', color: 'none', borderRadius: 8, padding: 6, display: 'none', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, marginLeft: 2 }}><DeleteIcon /></button>
              {canManageReportsState && (
                <button onClick={handleExportPDF} title="PDF Export" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, marginLeft: 2 }}><FileDownloadIcon /></button>
              )}
              {(canManageTeamsState || canManageOperationsState) && (
                <div className="einsatzdetail-header-admin-row" style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center', gap: 8, flexBasis: '100%' }}>
                  {canManageTeamsState && (
                    <button 
                      onClick={() => handleEinsatzleiterModeChange(!localEinsatzleiterMode)} 
                      title={localEinsatzleiterMode ? "Normal-Modus" : "Trupp Übersicht Modus"} 
                      style={{ 
                        background: localEinsatzleiterMode ? '#c00' : '#1976d2', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 8, 
                        padding: 6, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: 36,
                        height: 36
                      }}
                    >
                      {localEinsatzleiterMode ? <PersonIcon style={{ fontSize: 20 }} /> : <SupervisorAccountIcon style={{ fontSize: 20 }} />}
                    </button>
                  )}
                  {canManageOperationsState && (
                    <button onClick={() => setShowEinsatzBeendenModal(true)} title="Einsatz beenden" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36 }}><StopIcon /></button>
                  )}
                </div>
              )}

          </div>
        </div>
      </div>
      {/* Desktop: bisheriger Header */}
      <div className="einsatzdetail-header-row" style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 22, flexWrap: 'wrap', position: 'relative', paddingTop: 4, paddingBottom: 4 }}>
        <button onClick={() => { resetEinsatzleiterMode(); navigate('/dashboard'); }} style={{ background: 'none', border: 'none', padding: 0, marginRight: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', zIndex: 10 }}>
          <ArrowBackIcon style={{ fontSize: 32, color: '#c00' }} />
        </button>
        
        {/* Titel oben ausgerichtet – Warnung wächst nach unten, nicht in beide Richtungen */}
        <div className="einsatzdetail-header-title" style={{ 
          position: 'absolute', 
          left: '50%', 
          top: 0, 
          transform: 'translateX(-50%)', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          minWidth: 0,
          zIndex: 5,
          pointerEvents: 'none',
        }}>
          <div style={{ fontWeight: 700, fontSize: 18, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.25 }}>
            {primaryHeaderTitle}
          </div>
          <div style={{ fontSize: 13, color: '#444', textAlign: 'center', marginTop: 1, lineHeight: 1.25 }}>
            <b>Ort:</b> {headerOrt} &nbsp;|&nbsp; <b>Typ:</b> {headerTyp}
          </div>

          {/* Benutzer-Anzeige für technische Accounts */}
          {isTechAccount && realUserName && (
            <div 
              onClick={() => { previousIdentityRef.current = getRealUserIdentity(); setShowUserIdentModal(true); }} 
              style={{ 
                display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4, 
                cursor: 'pointer', fontSize: 12, color: '#666', 
                background: '#f0f0f0', borderRadius: 12, padding: '2px 10px 2px 8px',
                border: '1px solid #ddd', transition: 'background 0.15s',
                pointerEvents: 'auto',
              }}
              title="Benutzer ändern"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#888"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>
              <span style={{ fontWeight: 600 }}>{realUserName}</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="#aaa"><path d="M7 10l5 5 5-5z"/></svg>
            </div>
          )}

          {!localEinsatzleiterMode && truppLimitReached && (
            <div style={{ color: '#c00', fontWeight: 700, fontSize: 12, marginTop: 4, textAlign: 'center', lineHeight: 1.2 }}>
              Maximale Anzahl Trupps erreicht ({maxTrupps})
            </div>
          )}
        </div>
        
        <div className="einsatzdetail-header-actions" style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', margin: '0', flex: 1, zIndex: 10 }}>
          {!localEinsatzleiterMode && (
            <button onClick={openTruppModal} title="Trupp erstellen" style={{ background: (einsatzGeschlossen || truppLimitReached) ? '#999' : '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, opacity: einsatzGeschlossen || truppLimitReached ? 0.5 : 1, cursor: einsatzGeschlossen || truppLimitReached ? 'not-allowed' : 'pointer' }} disabled={einsatzGeschlossen || truppLimitReached}><AddIcon /></button>
          )}
          {!localEinsatzleiterMode && (
            <button
              onClick={() => setShowBulkFunkkanalModal(true)}
              title="Funkkanal wechseln"
              style={{
                background: canBulkChangeFunkkanal ? '#c00' : '#999',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                width: 36,
                height: 36,
                opacity: canBulkChangeFunkkanal ? 1 : 0.5,
                cursor: canBulkChangeFunkkanal ? 'pointer' : 'not-allowed',
              }}
              disabled={!canBulkChangeFunkkanal}
            >
              <CellTowerIcon style={{ fontSize: 22 }} />
            </button>
          )}
          {!localEinsatzleiterMode && (
            <button onClick={() => setShowTruppAuditlog(true)} title="Trupp-Journal" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, cursor: 'pointer' }}><InfoIcon /></button>
          )}
          <button onClick={handleEditEinsatz} title="Einsatz bearbeiten" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, marginLeft: 2 }}><EditIcon /></button>
          {canAccessSettingsState && (
            <button onClick={() => setSettingsOpen(true)} title="Einstellungen" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, marginLeft: 2 }}><SettingsIcon style={{ color: '#fff' }} /></button>
          )}
          <button onClick={handleDeleteEinsatz} title="Einsatz löschen" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'none', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, marginLeft: 2 }}><DeleteIcon /></button>
          {canManageReportsState && (
            <button onClick={handleExportPDF} title="PDF Export" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36, marginLeft: 2 }}><FileDownloadIcon /></button>
          )}
          {(canManageTeamsState || canManageOperationsState) && (
            <div style={{margin: '0 8px', color: '#ccc', fontSize: 24, fontWeight: 400}}>|</div>
          )}
          {canManageTeamsState && (
            <button 
              onClick={() => handleEinsatzleiterModeChange(!localEinsatzleiterMode)} 
              title={localEinsatzleiterMode ? "Normal-Modus" : "Trupp Übersicht Modus"} 
              style={{ 
                background: localEinsatzleiterMode ? '#c00' : '#1976d2', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 8, 
                padding: 6, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: 36,
                height: 36
              }}
            >
              {localEinsatzleiterMode ? <PersonIcon style={{ fontSize: 20 }} /> : <SupervisorAccountIcon style={{ fontSize: 20 }} />}
            </button>
          )}
          {canManageOperationsState && (
            <button onClick={() => setShowEinsatzBeendenModal(true)} title="Einsatz beenden" style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, width: 36, height: 36 }}><StopIcon /></button>
          )}

        </div>
      </div>



      {/* Mayday-Benachrichtigungen werden von MaydayNotificationManager verwaltet */}

      {(trupps.length === 0 && !truppsLoading && !truppsError) || (localEinsatzleiterMode && einsatzleiterLoading) || truppsLoading ? (
        <div className="einsatzdetail-empty-placeholder" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: '60vh',
          textAlign: 'center'
        }}>
          {(einsatzleiterLoading || truppsLoading) ? (
            <>
              <div style={{
                width: '32px',
                height: '32px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #1976d2',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: '16px'
              }}></div>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
              <div style={{ 
                fontSize: 18, 
                color: '#1976d2', 
                fontWeight: 600, 
                textAlign: 'center',
                maxWidth: '100%'
              }}>
                Trupps werden geladen...
              </div>
            </>
          ) : truppsError ? (
            <>
              <div style={{ 
                fontSize: 22, 
                color: '#db2828', 
                marginBottom: 24, 
                fontWeight: 500, 
                textAlign: 'center',
                maxWidth: '100%'
              }}>
                Fehler beim Laden der Trupps: {truppsError}
              </div>
              <button 
                onClick={() => loadTeamsFromAPI(einsatz.id, einsatz, localEinsatzleiterMode)} 
                style={{ 
                  background: '#c00', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 12, 
                  padding: '18px 40px', 
                  fontWeight: 700, 
                  fontSize: 20, 
                  boxShadow: '0 2px 8px #c002', 
                  cursor: 'pointer', 
                  letterSpacing: 1,
                  display: 'block',
                  margin: '0 auto'
                }}
              >
                Erneut versuchen
              </button>
            </>
          ) : (
            <>
              <div style={{ 
                fontSize: 22, 
                color: '#888', 
                marginBottom: 24, 
                fontWeight: 500, 
                textAlign: 'center',
                maxWidth: '100%'
              }}>
                Es gibt noch keine Trupps
              </div>
              {!localEinsatzleiterMode && (
                <button 
                  onClick={openTruppModal} 
                  style={{ 
                    background: trupps.length >= maxTrupps ? '#999' : '#c00', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: 12, 
                    padding: '18px 40px', 
                    fontWeight: 700, 
                    fontSize: 20, 
                    boxShadow: '0 2px 8px #c002', 
                    cursor: trupps.length >= maxTrupps ? 'not-allowed' : 'pointer', 
                    letterSpacing: 1,
                    display: 'block',
                    margin: '0 auto'
                  }}
                  disabled={trupps.length >= maxTrupps}
                >
                  Trupp erstellen
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          
          <div
            ref={truppsContainerRef}
            className={`einsatzdetail-trupps-row${twoRows ? ' two-rows' : ''}`}
            style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            width: '100%',
            alignItems: 'stretch',
            paddingBottom: 120,
            }}
          >
            {(localEinsatzleiterMode
              ? [...trupps].sort((a, b) =>
                  resolveTruppSupervisorName(a).localeCompare(resolveTruppSupervisorName(b), 'de')
                )
              : trupps
            ).map((trupp: Trupp, idx: number) => {
              const supervisorName = resolveTruppSupervisorName(trupp);
              const supervisorColors = localEinsatzleiterMode
                ? getSupervisorColor(supervisorName)
                : null;
              const canReorder = !localEinsatzleiterMode && !einsatzGeschlossen && trupps.length > 1;

              return (
                <div
                  key={`trupp-container-${trupp.id}-${idx}`}
                  data-trupp-id={trupp.id}
                  ref={el => { truppRefs.current[idx] = el; }}
                  style={{
                    height: 'auto',
                    minHeight: 0,
                    overflow: 'visible',
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    opacity: draggingTruppId === trupp.id ? 0.45 : 1,
                    outline: dropTargetTruppId === trupp.id ? '3px solid #1976d2' : '3px solid transparent',
                    outlineOffset: 0,
                    borderRadius: 22,
                    transition: 'opacity 0.15s, outline-color 0.15s',
                  }}
                >
                  <TruppKachel
                    key={`${trupp.id}-${trupp.outOfService ? 'out' : 'active'}-${trupp.createdAt || 'no-timer'}`}
                    trupp={trupp}
                    onDelete={handleDeleteTrupp}
                    onStatus={(t, resetAbfrage) => handleStatus(t, resetAbfrage)}
                    onOut={handleOutOfService}
                    onReactivate={handleReactivateTrupp}
                    onCountdownChange={handleCountdownChange}
                    onMitglieder={handleMitglieder}
                    onBearbeiten={handleBearbeitenTrupp}
                    onRename={!localEinsatzleiterMode ? handleOpenRenameTrupp : undefined}
                    onShowAuditlog={() => setShowTruppAuditlog(true)}
                    onEndMayday={endMaydayStatus}
                    onMuteToggle={handleMuteToggle}
                    onEmergencyChange={handleEmergencyChange}
                    onRequestTypeChange={(t) => setShowTruppTypeModal({ open: true, trupp: t })}
                    onPressureAdjust={handlePressureAdjust}
                    showMitglieder={window.innerWidth > 600 && showMitglieder}
                    setMitgliederModal={setMitgliederModal}
                    addAudit={addAudit}
                    auditlog={auditlog}
                    einsatzGeschlossen={einsatzGeschlossen}
                    setShowDeleteTruppModal={setShowDeleteTruppModal}
                    einsatzleiterMode={localEinsatzleiterMode}
                    getTruppueberwacherName={getTruppueberwacherNameForKachel}
                    hasSupervisorHeader={localEinsatzleiterMode}
                    supervisorBorderColor={supervisorColors?.border}
                    supervisorHeader={localEinsatzleiterMode && supervisorColors ? {
                      name: supervisorName,
                      background: supervisorColors.background,
                      border: supervisorColors.border,
                      text: supervisorColors.text,
                    } : undefined}
                    canReorder={canReorder}
                    isDragging={draggingTruppId === trupp.id}
                    onReorderPointerDown={(e) => handleReorderPointerDown(trupp.id, e)}
                  />
                </div>
              );
            })}
        </div>
        </>
      )}
      {modalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className={window.innerWidth <= 600 ? 'einsatzdetail-modal-mobile' : ''} style={{ background: '#fff', borderRadius: 16, padding: 0, minWidth: 600, maxWidth: 600, minHeight: 500, position: 'relative', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 32px #0002' }}>
            {/* X-Button oben rechts */}
            <button 
              onClick={resetTruppModal}
              style={{
                position: 'absolute',
                top: 15,
                right: 15,
                background: 'transparent',
                border: 'none',
                fontSize: 24,
                color: '#666',
                cursor: 'pointer',
                width: 30,
                height: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'background-color 0.2s',
                zIndex: 10
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Schliessen"
            >
              ×
            </button>
            {/* Titelbereich oben - wie Status-Modal */}
            <div style={{ 
              textAlign: 'center', 
              fontWeight: 700, 
              fontSize: 22, 
              padding: '20px 20px 16px 20px',
              background: '#f8f9fa',
              borderBottom: '1px solid #e0e0e0',
              letterSpacing: 1,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16
            }}>
              {getStepInfo(step).title}
            </div>
            {/* Mittlerer Bereich - dynamisch wachsend und vertikal zentriert */}
            <div id="modal-middle" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', flex: '1 1 auto', padding: '24px 40px' }}>
              <div style={{ pointerEvents: 'auto', width: '100%' }}>
                {step === 1 && !effectiveQuickTruppCreation && (() => {
                  const aufgaben = getAufgaben().filter(a => a !== 'Warten');
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 16 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', width: '100%', maxWidth: 600 }}>
                      {aufgaben.map(a => (
                          <button 
                            key={a} 
                            onClick={() => setAufgabe(a)} 
                            style={{ 
                              background: aufgabe === a ? '#c00' : '#f8f9fa', 
                              color: aufgabe === a ? '#fff' : '#333', 
                              borderRadius: '8px', 
                              padding: '12px 16px', 
                              cursor: 'pointer', 
                              minWidth: '100px', 
                              textAlign: 'center', 
                              fontWeight: '500', 
                              fontSize: '14px', 
                              boxShadow: aufgabe === a ? '0 4px 12px rgba(204, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)', 
                              border: aufgabe === a ? '2px solid #c00' : '2px solid #e0e0e0',
                              transition: 'all 0.2s ease'
                            }}
                          >{a}</button>
                        ))}
                      </div>
                      {/* Freitextfeld für Aufgabe */}
                      <div style={{ width: '100%', maxWidth: 600, display: 'flex', justifyContent: 'center' }}>
                        <input
                          placeholder={aufgaben.length > 0 ? "Oder eigene Aufgabe eingeben..." : "Aufgabe eingeben..."}
                          value={aufgaben.includes(aufgabe) ? '' : aufgabe}
                          onChange={e => setAufgabe(e.target.value)}
                          style={{
                            fontSize: '16px',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: '2px solid #e0e0e0',
                            outline: 'none',
                            width: '100%',
                            textAlign: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            background: '#fff'
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#c00'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                        />
                      </div>
                    </div>
                  );
                })()}
                {step === 1 && effectiveQuickTruppCreation && (
                  <div style={{ display: 'flex', gap: 16, justifyContent: 'center', maxWidth: 500, margin: '0 auto' }}>
                    <button 
                      onClick={() => setIsEmergencyTrupp(false)} 
                      style={{
                        background: !isEmergencyTrupp ? '#28a745' : '#f8f9fa',
                        color: !isEmergencyTrupp ? '#fff' : '#333',
                        border: !isEmergencyTrupp ? '2px solid #28a745' : '2px solid #e0e0e0',
                        borderRadius: '12px', 
                        padding: '24px 32px', 
                        cursor: 'pointer', 
                        fontWeight: '600', 
                        fontSize: '18px',
                        transition: 'all 0.2s ease',
                        minWidth: '180px',
                        boxShadow: !isEmergencyTrupp ? '0 4px 12px rgba(40, 167, 69, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <span style={{ fontSize: '32px' }}>✓</span>
                      <span>Normaltrupp</span>
                    </button>
                    <button 
                      onClick={() => setIsEmergencyTrupp(true)} 
                      style={{
                        background: isEmergencyTrupp ? '#c00' : '#f8f9fa',
                        color: isEmergencyTrupp ? '#fff' : '#333',
                        border: isEmergencyTrupp ? '2px solid #c00' : '2px solid #e0e0e0',
                        borderRadius: '12px', 
                        padding: '24px 32px', 
                        cursor: 'pointer', 
                        fontWeight: '600', 
                        fontSize: '18px',
                        transition: 'all 0.2s ease',
                        minWidth: '180px',
                        boxShadow: isEmergencyTrupp ? '0 4px 12px rgba(204, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <span style={{ fontSize: '32px' }}>🚨</span>
                      <span>Notfalltrupp</span>
                    </button>
                  </div>
                )}
                {step === 2 && !effectiveQuickTruppCreation && (
                  <div style={{ display: 'flex', gap: 16, justifyContent: 'center', maxWidth: 500, margin: '0 auto' }}>
                    <button 
                      onClick={() => setIsEmergencyTrupp(false)} 
                      style={{ 
                        background: !isEmergencyTrupp ? '#28a745' : '#f8f9fa', 
                        color: !isEmergencyTrupp ? '#fff' : '#333',
                        border: !isEmergencyTrupp ? '2px solid #28a745' : '2px solid #e0e0e0',
                        borderRadius: '12px', 
                        padding: '24px 32px', 
                        cursor: 'pointer', 
                        fontWeight: '600', 
                        fontSize: '18px',
                        transition: 'all 0.2s ease',
                        minWidth: '180px',
                        boxShadow: !isEmergencyTrupp ? '0 4px 12px rgba(40, 167, 69, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <span style={{ fontSize: '32px' }}>✓</span>
                      <span>Normaltrupp</span>
                    </button>
                    <button 
                      onClick={() => setIsEmergencyTrupp(true)} 
                      style={{
                        background: isEmergencyTrupp ? '#c00' : '#f8f9fa',
                        color: isEmergencyTrupp ? '#fff' : '#333',
                        border: isEmergencyTrupp ? '2px solid #c00' : '2px solid #e0e0e0',
                        borderRadius: '12px', 
                        padding: '24px 32px', 
                        cursor: 'pointer', 
                        fontWeight: '600', 
                        fontSize: '18px',
                        transition: 'all 0.2s ease',
                        minWidth: '180px',
                        boxShadow: isEmergencyTrupp ? '0 4px 12px rgba(204, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8
                      }}
                    >
                      <span style={{ fontSize: '32px' }}>🚨</span>
                      <span>Notfalltrupp</span>
                    </button>
                  </div>
                )}
                {step === 2 && effectiveQuickTruppCreation && (
                  <>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 30, flexWrap: 'wrap' }}>
                      <button 
                        onClick={() => setStartdruck(Math.max(0, startdruck - 10))} 
                        style={{ 
                          fontSize: 36, 
                          color: '#c00', 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          fontWeight: 900, 
                          padding: '12px 16px', 
                          borderRadius: '8px', 
                          transition: 'all 0.2s ease' 
                        }} 
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'} 
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >&lt;</button>
                      <span style={{ fontSize: 42, fontWeight: 900, minWidth: 120, textAlign: 'center', color: '#333' }}>{startdruck} Bar</span>
                      <button 
                        onClick={() => setStartdruck(Math.min(400, startdruck + 10))} 
                        style={{ 
                          fontSize: 36, 
                          color: '#c00', 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          fontWeight: 900, 
                          padding: '12px 16px', 
                          borderRadius: '8px', 
                          transition: 'all 0.2s ease' 
                        }} 
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'} 
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >&gt;</button>
                  </div>
                  {startdruck <= 270 && (
                    <div style={{ 
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 10,
                      background: '#fff3cd', 
                      border: '1px solid #ffeaa7', 
                      borderRadius: 8, 
                      padding: '12px 16px', 
                      marginTop: 8,
                      textAlign: 'center',
                      color: '#856404',
                      fontSize: 14,
                      fontWeight: 500,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}>
                      ⚠️ Warnung: Startdruck von 270 Bar oder weniger ist ungewöhnlich niedrig. 
                      Bitte überprüfen Sie den korrekten Wert.
                    </div>
                  )}
                  </>
                )}
                {step === 3 && !effectiveQuickTruppCreation && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 16 }}>
                    {getStandorte().length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', width: '100%', maxWidth: 500 }}>
                        {getStandorte().map((st: string, i: number) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setStandort(st)}
                            style={{
                              background: standort === st ? '#c00' : '#f8f9fa',
                              color: standort === st ? '#fff' : '#333',
                              borderRadius: '8px',
                              padding: '12px 16px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              fontSize: '14px',
                              border: standort === st ? '2px solid #c00' : '2px solid #e0e0e0',
                              boxShadow: standort === st ? '0 4px 12px rgba(204, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                              transition: 'all 0.2s ease',
                              minWidth: '100px'
                            }}
                          >{st}</button>
                        ))}
                      </div>
                    )}
                    <div style={{ width: '100%', maxWidth: 500, display: 'flex', justifyContent: 'center' }}>
                      <input
                        placeholder={getStandorte().length > 0 ? "Oder eigenen Standort eingeben..." : "Standort eingeben..."}
                        value={getStandorte().includes(standort) ? '' : standort}
                        onChange={e => setStandort(e.target.value)}
                        style={{
                          fontSize: '16px',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '2px solid #e0e0e0',
                          outline: 'none',
                          width: '100%',
                          textAlign: 'center',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s ease',
                          background: '#fff'
                        }}
                        onFocus={(e) => e.currentTarget.style.borderColor = '#c00'}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                      />
                    </div>
                  </div>
                )}
                {step === 4 && !effectiveQuickTruppCreation && (
                  <>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 30, flexWrap: 'wrap' }}>
                      <button 
                        onClick={() => setStartdruck(Math.max(0, startdruck - 10))} 
                        style={{ 
                          fontSize: 36, 
                          color: '#c00', 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          fontWeight: 900, 
                          padding: '12px 16px', 
                          borderRadius: '8px', 
                          transition: 'all 0.2s ease' 
                        }} 
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'} 
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >&lt;</button>
                      <span style={{ fontSize: 42, fontWeight: 900, minWidth: 120, textAlign: 'center', color: '#333' }}>{startdruck} Bar</span>
                      <button 
                        onClick={() => setStartdruck(Math.min(400, startdruck + 10))} 
                        style={{ 
                          fontSize: 36, 
                          color: '#c00', 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          fontWeight: 900, 
                          padding: '12px 16px', 
                          borderRadius: '8px', 
                          transition: 'all 0.2s ease' 
                        }} 
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'} 
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >&gt;</button>
                  </div>
                                                                {startdruck <= 270 && (
                                  <div style={{ 
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    zIndex: 10,
                                    background: '#fff3cd', 
                                    border: '1px solid #ffeaa7', 
                                    borderRadius: 8, 
                                    padding: '12px 16px', 
                                    marginTop: 8,
                                    textAlign: 'center',
                                    color: '#856404',
                                    fontSize: 14,
                                    fontWeight: 500,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                  }}>
                                    ⚠️ Warnung: Startdruck von 270 Bar oder weniger ist ungewöhnlich niedrig. 
                                    Bitte überprüfen Sie den korrekten Wert.
                  </div>
                                )}
                  </>
                )}
                {step === 5 && !effectiveQuickTruppCreation && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 16 }}>
                      {getFunkkanaele().length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', width: '100%', maxWidth: 500 }}>
                          {getFunkkanaele().map((fk: string, i: number) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => { setFunkkanal(fk); setFunkkanalSelected(true); }}
                              style={{
                                background: funkkanal === fk ? '#c00' : '#f8f9fa',
                                color: funkkanal === fk ? '#fff' : '#333',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                fontSize: '14px',
                                border: funkkanal === fk ? '2px solid #c00' : '2px solid #e0e0e0',
                                boxShadow: funkkanal === fk ? '0 4px 12px rgba(204, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                                transition: 'all 0.2s ease',
                                minWidth: '100px'
                              }}
                            >{fk}</button>
                          ))}
                        </div>
                      )}
                      {/* Freitextfeld für Funkkanal */}
                      <div style={{ width: '100%', maxWidth: 500, display: 'flex', justifyContent: 'center' }}>
                      <input
                          placeholder={getFunkkanaele().length > 0 ? "Oder eigenen Funkkanal eingeben..." : "Funkkanal eingeben..."}
                          value={getFunkkanaele().includes(funkkanal) ? '' : funkkanal}
                        onChange={e => { setFunkkanal(e.target.value); setFunkkanalSelected(true); }}
                        style={{
                            fontSize: '16px',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: '2px solid #e0e0e0',
                          outline: 'none',
                            width: '100%',
                          textAlign: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            background: '#fff'
                        }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#c00'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                      />
                      </div>
                    </div>
                  </>
                )}
                {step === 6 && (
                  <>
                    {/* NFC Status and Scan */}
                    <div style={{ marginBottom: 16, textAlign: 'center' }}>
                      {nfcStatus === 'checking' && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                          <div style={{ 
                            fontSize: 20, 
                            animation: isScanning ? 'pulse 1s infinite' : 'none',
                            color: '#c00'
                          }}>
                            📱
                          </div>
                          <div style={{ fontSize: 14, color: '#666' }}>
                            Prüfe NFC-Verfügbarkeit...
                          </div>
                        </div>
                      )}
                      
                      {nfcStatus === 'available' && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                          <div style={{ 
                            fontSize: 20, 
                            animation: isScanning ? 'pulse 1s infinite' : 'none',
                            color: '#c00'
                          }}>
                            📱
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>NFC aktiv - Scanne automatisch</div>
                            <div style={{ fontSize: 12 }}>Halte ein NFC-Tag an das Gerät</div>
                          </div>
                        </div>
                      )}
                      
                      {nfcStatus === 'unavailable' && (
                        <div style={{ 
                          fontSize: 12, 
                          color: '#999', 
                          marginBottom: 8,
                          fontStyle: 'italic'
                        }}>
                          NFC nicht verfügbar
                        </div>
                      )}
                      
                      {/* NFC Scan Result */}
                      {nfcScanResult && (
                        <div style={{
                          padding: 12,
                          marginBottom: 16,
                          borderRadius: 8,
                          backgroundColor: nfcScanResult.success ? '#d4edda' : '#f8d7da',
                          color: nfcScanResult.success ? '#155724' : '#721c24',
                          border: `1px solid ${nfcScanResult.success ? '#c3e6cb' : '#f5c6cb'}`,
                          textAlign: 'center',
                          fontSize: 14
                        }}>
                          {nfcScanResult.message}
                        </div>
                      )}
                    </div>

                    {/* Autocomplete Field */}
                    <div style={{ marginBottom: 12, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', width: '100%', maxWidth: 400 }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                          <input
                            placeholder="Name suchen oder eingeben (Vorname Nachname)"
                            value={autocompleteValue}
                            onChange={e => handleAutocompleteChange(e.target.value)}
                            onKeyPress={handleAutocompleteKeyPress}
                            style={{ 
                              fontSize: 15, 
                              padding: '8px 10px', 
                              borderRadius: 8, 
                              border: '1.5px solid #c00', 
                              outline: 'none', 
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                          />
                          
                          {/* Autocomplete Suggestions */}
                          {showSuggestions && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              background: '#fff',
                              border: '1px solid #ccc',
                              borderRadius: '0 0 8px 8px',
                              borderTop: 'none',
                              maxHeight: 150,
                              overflowY: 'auto',
                              zIndex: 1000,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}>
                              {autocompleteSuggestions.map((user, index) => (
                                <div
                                  key={index}
                                  onClick={() => handleAutocompleteSelect(user)}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #eee',
                                    fontSize: 14
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                                >
                                  {user.firstName} {user.lastName}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <button 
                          type="button" 
                          onClick={handleAutocompleteAdd}
                          style={{ 
                            background: '#c00', 
                            color: '#fff', 
                            border: 'none', 
                            borderRadius: 8, 
                            padding: '8px 12px', 
                            fontWeight: 700,
                            flexShrink: 0
                          }}
                        >
                          +
                        </button>
                        <button 
                          type="button" 
                          onClick={refreshUsers}
                          title="Benutzer neu laden"
                          style={{ 
                            background: '#666', 
                            color: '#fff', 
                            border: 'none', 
                            borderRadius: 8, 
                            padding: '8px 12px', 
                            fontWeight: 700,
                            fontSize: 12,
                            flexShrink: 0
                          }}
                        >
                          🔄
                        </button>
                      </div>
                    </div>

                    {/* Member List */}
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <div style={{ width: '100%', maxWidth: 400 }}>
                        <ul style={{ 
                          listStyle: 'none', 
                          padding: 0, 
                          margin: 0, 
                          height: `${Math.max(120, mitglieder.length * 40)}px`, 
                          overflowY: mitglieder.length * 40 > 120 ? 'auto' : 'hidden', 
                          width: '100%' 
                        }}>
                          {mitglieder.map((m, i) => (
                            <li key={i} style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              marginBottom: 4,
                              background: '#f8f8f8',
                              borderRadius: 6,
                              border: '1px solid #e0e0e0'
                            }}>
                              <span style={{ fontWeight: 500 }}>{m.vorname} {m.nachname}</span>
                              <button 
                                onClick={() => {
                                  const updatedMitglieder = mitglieder.filter((_, index) => index !== i);
                                  setMitglieder(updatedMitglieder);
                                }}
                                style={{ 
                                  background: '#c00', 
                                  color: '#fff', 
                                  border: 'none', 
                                  borderRadius: 4, 
                                  padding: '4px 8px', 
                                  fontWeight: 700,
                                  fontSize: 12,
                                  cursor: 'pointer'
                                }}
                              >
                                Entfernen
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </>
                )}
                {step === 7 && !skipLeaderSelection && (
                  <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <div style={{ width: '100%', maxWidth: 400 }}>
                      <ul style={{ 
                        listStyle: 'none', 
                        padding: 0, 
                        margin: 0, 
                        height: `${Math.max(120, mitglieder.length * 40)}px`, 
                        overflowY: mitglieder.length * 40 > 120 ? 'auto' : 'hidden', 
                        width: '100%' 
                      }}>
                        {mitglieder.map((m, i) => (
                          <li key={i} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '8px 12px',
                            marginBottom: 4,
                            background: '#f8f8f8',
                            borderRadius: 6,
                            border: '1px solid #e0e0e0'
                          }}>
                            <label style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              cursor: 'pointer',
                              fontWeight: 500,
                              margin: 0
                            }}>
                              <input 
                                type="radio" 
                                name="truppfuehrer" 
                                checked={truppfuehrerIndex === i} 
                                onChange={() => setTruppfuehrerIndex(i)}
                                style={{ marginRight: 8 }}
                              />
                              {m.vorname} {m.nachname}
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Button-Bereich unten - wie Status-Modal */}
            <div id="modal-bottom" style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '24px 40px', 
              borderTop: '1px solid #e0e0e0',
              marginTop: 'auto'
            }}>
              {step === 1 && !effectiveQuickTruppCreation && (
                <>
                  <button 
                    onClick={resetTruppModal} 
                    style={modalButtonStyle}
                  >
                    Abbrechen
                  </button>
                  <button 
                    onClick={goToNextStep} 
                    style={aufgabe ? modalButtonPrimaryStyle : modalButtonPrimaryDisabledStyle} 
                    disabled={!aufgabe}
                  >
                    Weiter
                  </button>
                </>
              )}
              {step === 1 && effectiveQuickTruppCreation && (
                <>
                  <button 
                    onClick={resetTruppModal} 
                    style={modalButtonStyle}
                  >
                    Abbrechen
                  </button>
                  <button 
                    onClick={goToNextStep} 
                    style={modalButtonPrimaryStyle}
                  >
                    Weiter
                  </button>
                </>
              )}
              {step === 2 && (
                <>
                  <button onClick={goToPrevStep} style={modalButtonStyle}>Zurück</button>
                  <button onClick={goToNextStep} style={modalButtonPrimaryStyle}>Weiter</button>
                </>
              )}
              {step === 3 && !effectiveQuickTruppCreation && (
                <>
                  <button onClick={goToPrevStep} style={modalButtonStyle}>Zurück</button>
                  <button 
                    onClick={goToNextStep} 
                    style={standort ? modalButtonPrimaryStyle : modalButtonPrimaryDisabledStyle} 
                    disabled={!standort}
                  >
                    Weiter
                  </button>
                </>
              )}
              {step === 4 && !effectiveQuickTruppCreation && (
                <>
                  <button onClick={goToPrevStep} style={modalButtonStyle}>Zurück</button>
                  <button onClick={goToNextStep} style={modalButtonPrimaryStyle}>Weiter</button>
                </>
              )}
              {step === 5 && !effectiveQuickTruppCreation && (
                <>
                  <button onClick={goToPrevStep} style={modalButtonStyle}>Zurück</button>
                  <button 
                    onClick={goToNextStep} 
                    style={funkkanalSelected ? modalButtonPrimaryStyle : modalButtonPrimaryDisabledStyle} 
                    disabled={!funkkanalSelected}
                  >
                    Weiter
                  </button>
                </>
              )}
              {step === 6 && (
                <>
                  <button onClick={goToPrevStep} style={modalButtonStyle}>Zurück</button>
                  <button 
                    onClick={goToNextStep} 
                    style={mitglieder.length > 0 ? modalButtonPrimaryStyle : modalButtonPrimaryDisabledStyle} 
                    disabled={mitglieder.length === 0}
                  >
                    {skipLeaderSelection ? 'Trupp anlegen' : 'Weiter'}
                  </button>
                </>
              )}
              {step === 7 && !skipLeaderSelection && (
                <>
                  <button onClick={goToPrevStep} style={modalButtonStyle}>Zurück</button>
                  <button 
                    onClick={() => { void handleSaveTrupp(); }} 
                    style={truppfuehrerIndex !== null ? modalButtonPrimaryStyle : modalButtonPrimaryDisabledStyle} 
                    disabled={truppfuehrerIndex === null}
                  >
                    Trupp anlegen
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
        {statusDialog.open && statusDialog.trupp && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: isMobileLayout ? 10 : 0, boxSizing: 'border-box' }}>
             <div
               className={isMobileLayout ? 'einsatzdetail-modal-mobile updatemodal-container' : 'updatemodal-container'}
               style={{
                 background: '#fff',
                 borderRadius: isMobileLayout ? 14 : 16,
                 padding: 0,
                 minWidth: isMobileLayout ? 0 : 600,
                 width: isMobileLayout ? '100%' : 600,
                 maxWidth: isMobileLayout ? '100%' : 600,
                 // Feste Höhe über alle Schritte, damit Footer/Weiter nicht springt
                 height: isMobileLayout ? 'min(430px, 85vh)' : 480,
                 minHeight: isMobileLayout ? 'min(430px, 85vh)' : 480,
                 maxHeight: isMobileLayout ? '85vh' : 480,
                 overflow: 'hidden',
                 position: 'relative',
                 display: 'flex',
                 flexDirection: 'column',
                 boxShadow: '0 4px 32px #0002',
               }}
             >
            {/* X-Button zum Schliessen */}
            <button
              onClick={() => setStatusDialog({ open: false, trupp: null })}
              style={{
                position: 'absolute',
                top: isMobileLayout ? 8 : 15,
                right: isMobileLayout ? 8 : 15,
                background: 'transparent',
                border: 'none',
                fontSize: isMobileLayout ? 22 : 24,
                cursor: 'pointer',
                color: '#666',
                width: 30,
                height: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'background-color 0.2s',
                zIndex: 2,
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Schliessen"
            >
              ×
            </button>
            {/* Titelbereich oben */}
            <div
              className="updatemodal-title"
              id="modal-title"
              style={{
                width: '100%',
                textAlign: 'center',
                fontWeight: 700,
                fontSize: isMobileLayout ? 16 : 22,
                marginBottom: isMobileLayout ? 0 : 5,
                letterSpacing: isMobileLayout ? 0.2 : 1,
                padding: isMobileLayout ? '14px 40px 8px 16px' : '20px 20px 0 20px',
                lineHeight: 1.25,
                boxSizing: 'border-box',
              }}
            >
              Statusmeldung für {statusDialog.trupp.name}
            </div>
            {/* Pagination-Übersicht im Tutorial-Stil */}
            <div className="updatemodal-pagination" style={{ 
              padding: isMobileLayout ? '10px 12px' : '16px 40px', 
              background: '#f8f9fa', 
              borderBottom: '1px solid #e0e0e0',
              marginBottom: isMobileLayout ? 8 : 15,
              flexShrink: 0,
            }}>
              {(() => {
                const includeFeedback = getFeedbacks().length > 0 && !statusDialog.isReactivation && !statusDialog.isQuickTrupp;
                const activeSteps = getActiveStatusSteps({ includeFeedback });
                const currentIndex = Math.max(0, activeSteps.indexOf(statusStep));
                const progressPercentage = activeSteps.length > 0 ? ((currentIndex + 1) / activeSteps.length) * 100 : 0;

                return (
                  <>
                    {/* Progress Bar */}
                    <div style={{
                      width: '100%',
                      height: isMobileLayout ? '4px' : '6px',
                      background: '#e0e0e0',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      marginBottom: isMobileLayout ? '10px' : '16px'
                    }}>
                      <div style={{
                        height: '100%',
                        background: '#c00',
                        borderRadius: '3px',
                        width: `${progressPercentage}%`,
                        transition: 'width 0.3s ease'
                      }} />
                    </div>

                    {/* Step Labels */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: isMobileLayout ? '4px' : '8px'
                    }}>
                      {activeSteps.map((stepNumber, index) => {
                        const isActive = stepNumber === statusStep;
                        const isCompleted = index < currentIndex;

                        return (
                          <div 
                            key={stepNumber}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: isMobileLayout ? '4px' : '6px',
                              flex: 1,
                              textAlign: 'center',
                              minWidth: 0,
                            }}
                          >
                            <div style={{
                              width: isMobileLayout ? '24px' : '28px',
                              height: isMobileLayout ? '24px' : '28px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 600,
                              fontSize: isMobileLayout ? '11px' : '12px',
                              transition: 'all 0.3s ease',
                              background: isCompleted ? '#28a745' : (isActive ? '#c00' : '#e0e0e0'),
                              color: isCompleted || isActive ? 'white' : '#666',
                              transform: isActive ? 'scale(1.1)' : 'scale(1)',
                              boxShadow: isActive ? '0 4px 12px rgba(204, 0, 0, 0.4)' : 'none'
                            }}>
                              {isCompleted ? '✓' : (index + 1)}
                            </div>
                            <span style={{
                              fontSize: isMobileLayout ? '10px' : '11px',
                              fontWeight: isActive ? 600 : 500,
                              color: isCompleted ? '#28a745' : (isActive ? '#c00' : '#666'),
                              lineHeight: 1.2
                            }}>
                              {STATUS_STEP_LABELS[stepNumber] || `Schritt ${stepNumber}`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
            {/* Mittlerer Bereich - Tutorial-Stil (feste Flex-Höhe, Overflow scrollt intern) */}
            <div id="modal-middle" style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: isMobileLayout ? '12px' : '20px', minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' as any }}>
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: isMobileLayout ? 10 : 0, minHeight: '100%' }}>
                {statusStep === 1 && (
                  <div style={{ display: 'flex', gap: isMobileLayout ? 10 : 16, justifyContent: 'center', width: '100%', maxWidth: 300 }}>
                    <button 
                      onClick={() => { setStatusOk(true); }} 
                      style={{ 
                        background: statusOk ? '#28a745' : '#f8f9fa', 
                        color: statusOk ? '#fff' : '#333', 
                        border: statusOk ? '2px solid #28a745' : '2px solid #e0e0e0',
                        borderRadius: '10px', 
                        padding: isMobileLayout ? '12px 14px' : '16px 24px', 
                        cursor: 'pointer', 
                        fontWeight: '600', 
                        fontSize: isMobileLayout ? '14px' : '16px',
                        transition: 'all 0.2s ease',
                        minWidth: isMobileLayout ? 0 : '120px',
                        flex: isMobileLayout ? 1 : undefined,
                        boxShadow: statusOk ? '0 4px 12px rgba(40, 167, 69, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onMouseEnter={(e) => {
                        if (!statusOk) {
                          e.currentTarget.style.background = '#e9ecef';
                          e.currentTarget.style.borderColor = '#c00';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!statusOk) {
                          e.currentTarget.style.background = '#f8f9fa';
                          e.currentTarget.style.borderColor = '#e0e0e0';
                        }
                      }}
                    >
                      ✓ OK
                    </button>
                    <button 
                      onClick={() => { setStatusOk(false); }} 
                      style={{ 
                        background: !statusOk ? '#fd7e14' : '#f8f9fa', 
                        color: !statusOk ? '#fff' : '#333', 
                        border: !statusOk ? '2px solid #fd7e14' : '2px solid #e0e0e0',
                        borderRadius: '10px', 
                        padding: isMobileLayout ? '12px 14px' : '16px 24px', 
                        cursor: 'pointer', 
                        fontWeight: '600', 
                        fontSize: isMobileLayout ? '14px' : '16px',
                        transition: 'all 0.2s ease',
                        minWidth: isMobileLayout ? 0 : '120px',
                        flex: isMobileLayout ? 1 : undefined,
                        boxShadow: !statusOk ? '0 4px 12px rgba(253, 126, 20, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onMouseEnter={(e) => {
                        if (statusOk) {
                          e.currentTarget.style.background = '#e9ecef';
                          e.currentTarget.style.borderColor = '#fd7e14';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (statusOk) {
                          e.currentTarget.style.background = '#f8f9fa';
                          e.currentTarget.style.borderColor = '#e0e0e0';
                        }
                      }}
                    >
                      ✗ Nicht OK
                    </button>
                  </div>
                )}
                {statusStep === 2 && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: isMobileLayout ? 10 : 16 }}>
                      {getStandorte().length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobileLayout ? 8 : 12, justifyContent: 'center', width: '100%', maxWidth: 500 }}>
                          {getStandorte().map((st: string, i: number) => (
                            <button
                              key={i}
                              type="button"
                              className="status-choice-btn"
                              onClick={() => setEditStandort(st)}
                              style={{
                                background: editStandort === st ? '#c00' : '#f8f9fa',
                                color: editStandort === st ? '#fff' : '#333',
                                borderRadius: '8px',
                                padding: isMobileLayout ? '8px 12px' : '12px 16px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                fontSize: isMobileLayout ? '13px' : '14px',
                                border: editStandort === st ? '2px solid #c00' : '2px solid #e0e0e0',
                                boxShadow: editStandort === st ? '0 4px 12px rgba(204, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                                transition: 'all 0.2s ease',
                                minWidth: isMobileLayout ? '72px' : '100px'
                              }}
                            >{st}</button>
                          ))}
                        </div>
                      )}
                      {/* Freitextfeld für Standort */}
                      <div style={{ width: '100%', maxWidth: 500, display: 'flex', justifyContent: 'center' }}>
                        <input
                          placeholder={getStandorte().length > 0 ? "Oder eigenen Standort eingeben..." : "Standort eingeben..."}
                          value={getStandorte().includes(editStandort) ? '' : editStandort}
                          onChange={e => setEditStandort(e.target.value)}
                          style={{
                            fontSize: '16px',
                            padding: isMobileLayout ? '10px 12px' : '12px 16px',
                            borderRadius: '8px',
                            border: '2px solid #e0e0e0',
                            outline: 'none',
                            width: '100%',
                            textAlign: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            background: '#fff',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#c00'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                        />
                      </div>
                    </div>
                  </>
                )}
                {statusStep === 3 && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: isMobileLayout ? 10 : 16 }}>
                      {getAufgaben().filter(a => a !== 'Warten').length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobileLayout ? 8 : 12, justifyContent: 'center', width: '100%', maxWidth: 600 }}>
                      {getAufgaben().filter(a => a !== 'Warten').map(a => (
                        <button
                          key={a}
                          className="status-choice-btn"
                          onClick={() => { setEditAufgabe(a); }}
                          style={{
                            background: editAufgabe === a ? '#c00' : '#f8f9fa',
                            color: editAufgabe === a ? '#fff' : '#333',
                            borderRadius: '8px',
                            padding: isMobileLayout ? '8px 12px' : '12px 16px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            fontSize: isMobileLayout ? '13px' : '14px',
                            border: editAufgabe === a ? '2px solid #c00' : '2px solid #e0e0e0',
                            boxShadow: editAufgabe === a ? '0 4px 12px rgba(204, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            minWidth: isMobileLayout ? '72px' : '100px'
                          }}
                        >
                          {a}
                        </button>
                      ))}
                        </div>
                      )}
                      {/* Freitextfeld für Aufgabe */}
                      <div style={{ width: '100%', maxWidth: 600, display: 'flex', justifyContent: 'center' }}>
                        <input
                          placeholder={getAufgaben().filter(a => a !== 'Warten').length > 0 ? "Oder eigene Aufgabe eingeben..." : "Aufgabe eingeben..."}
                          value={getAufgaben().includes(editAufgabe) ? '' : editAufgabe}
                          onChange={e => setEditAufgabe(e.target.value)}
                          style={{
                            fontSize: '16px',
                            padding: isMobileLayout ? '10px 12px' : '12px 16px',
                            borderRadius: '8px',
                            border: '2px solid #e0e0e0',
                            outline: 'none',
                            width: '100%',
                            textAlign: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            background: '#fff',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#c00'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                        />
                      </div>
                    </div>
                  </>
                )}
                {statusStep === 4 && (
                  <>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobileLayout ? 12 : 30, flexWrap: 'nowrap', width: '100%' }}>
                      <button onClick={() => setEditDruck(Math.max(0, editDruck - 10))} style={{ fontSize: isMobileLayout ? 28 : 36, color: '#c00', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 900, padding: isMobileLayout ? '8px 12px' : '12px 16px', borderRadius: '8px', transition: 'all 0.2s ease', flexShrink: 0, lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'} onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>&lt;</button>
                      <span style={{ fontSize: isMobileLayout ? 28 : 42, fontWeight: 900, minWidth: isMobileLayout ? 96 : 120, textAlign: 'center', color: '#333', flexShrink: 0 }}>{editDruck} Bar</span>
                      <button onClick={() => setEditDruck(Math.min(400, editDruck + 10))} style={{ fontSize: isMobileLayout ? 28 : 36, color: '#c00', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 900, padding: isMobileLayout ? '8px 12px' : '12px 16px', borderRadius: '8px', transition: 'all 0.2s ease', flexShrink: 0, lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'} onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>&gt;</button>
                    </div>
                    {editDruck <= 270 && statusDialog.isReactivation && (
                      <div style={{ 
                        position: isMobileLayout ? 'relative' : 'absolute',
                        top: isMobileLayout ? undefined : '100%',
                        left: 0,
                        right: 0,
                        zIndex: 10,
                        background: '#fff3cd', 
                        border: '1px solid #ffeaa7', 
                        borderRadius: 8, 
                        padding: isMobileLayout ? '8px 10px' : '12px 16px', 
                        marginTop: 8,
                        textAlign: 'center',
                        color: '#856404',
                        fontSize: isMobileLayout ? 12 : 14,
                        fontWeight: 500,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        width: '100%',
                        boxSizing: 'border-box',
                      }}>
                        ⚠️ Warnung: Startdruck von 270 Bar oder weniger ist ungewöhnlich niedrig. 
                        Bitte überprüfen Sie den korrekten Wert.
                      </div>
                    )}
                  </>
                )}
                {statusStep === 5 && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: isMobileLayout ? 10 : 16 }}>
                      {getFunkkanaele().length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobileLayout ? 8 : 12, justifyContent: 'center', width: '100%', maxWidth: 600 }}>
                        {getFunkkanaele().map((fk: string, i: number) => (
                          <button
                            key={i}
                            type="button"
                            className="status-choice-btn"
                            onClick={() => { setFunkkanal(fk); setFunkkanalSelected(true); }}
                            style={{
                              background: funkkanal === fk ? '#c00' : '#f8f9fa',
                              color: funkkanal === fk ? '#fff' : '#333',
                              borderRadius: '8px',
                              padding: isMobileLayout ? '8px 12px' : '12px 16px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              fontSize: isMobileLayout ? '13px' : '14px',
                              border: funkkanal === fk ? '2px solid #c00' : '2px solid #e0e0e0',
                              boxShadow: funkkanal === fk ? '0 4px 12px rgba(204, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                              transition: 'all 0.2s ease',
                              minWidth: isMobileLayout ? '56px' : '100px'
                            }}
                          >{fk}</button>
                        ))}
                      </div>
                      )}
                      {/* Freitextfeld für Funkkanal */}
                      <div style={{ width: '100%', maxWidth: 600, display: 'flex', justifyContent: 'center' }}>
                        <input
                          placeholder={getFunkkanaele().length > 0 ? "Oder eigenen Funkkanal eingeben..." : "Funkkanal eingeben..."}
                          value={getFunkkanaele().includes(funkkanal) ? '' : funkkanal}
                          onChange={e => { setFunkkanal(e.target.value); setFunkkanalSelected(true); }}
                          style={{
                            fontSize: '16px',
                            padding: isMobileLayout ? '10px 12px' : '12px 16px',
                            borderRadius: '8px',
                            border: '2px solid #e0e0e0',
                            outline: 'none',
                            width: '100%',
                            textAlign: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            background: '#fff',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#c00'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                        />
                      </div>
                    </div>
                  </>
                )}
                {statusStep === 6 && !statusDialog.isReactivation && !statusDialog.isQuickTrupp && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: isMobileLayout ? 10 : 16 }}>
                      {getFeedbacks().length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobileLayout ? 8 : 12, justifyContent: 'center', width: '100%', maxWidth: 600 }}>
                        {getFeedbacks().map((feedback: string, i: number) => (
                          <button
                            key={i}
                            type="button"
                            className="status-choice-btn"
                            onClick={() => { setSelectedFeedback(feedback); }}
                            style={{
                              background: selectedFeedback === feedback ? '#c00' : '#f8f9fa',
                              color: selectedFeedback === feedback ? '#fff' : '#333',
                              borderRadius: '8px',
                              padding: isMobileLayout ? '8px 12px' : '12px 16px',
                              cursor: 'pointer',
                              fontWeight: '500',
                              fontSize: isMobileLayout ? '13px' : '14px',
                              border: selectedFeedback === feedback ? '2px solid #c00' : '2px solid #e0e0e0',
                              boxShadow: selectedFeedback === feedback ? '0 4px 12px rgba(204, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                              transition: 'all 0.2s ease',
                              minWidth: isMobileLayout ? '72px' : '100px'
                            }}
                          >{feedback}</button>
                        ))}
                      </div>
                      )}
                      {/* Freitextfeld für Feedback */}
                      <div style={{ width: '100%', maxWidth: 600, display: 'flex', justifyContent: 'center' }}>
                        <input
                          placeholder={getFeedbacks().length > 0 ? "Oder eigenes Feedback eingeben..." : "Feedback eingeben..."}
                          value={getFeedbacks().includes(selectedFeedback) ? '' : selectedFeedback}
                          onChange={e => setSelectedFeedback(e.target.value)}
                          style={{
                            fontSize: '16px',
                            padding: isMobileLayout ? '10px 12px' : '12px 16px',
                            borderRadius: '8px',
                            border: '2px solid #e0e0e0',
                            outline: 'none',
                            width: '100%',
                            textAlign: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s ease',
                            background: '#fff',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => e.currentTarget.style.borderColor = '#c00'}
                          onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* Button-Bereich unten - Tutorial-Stil */}
             <div className="updatemodal-footer" style={{ 
               display: 'flex', 
               justifyContent: 'space-between', 
               alignItems: 'center', 
               gap: isMobileLayout ? 8 : 12,
               padding: isMobileLayout ? '12px 14px' : '24px 40px', 
               borderTop: '1px solid #e0e0e0', 
               marginTop: 'auto',
               flexShrink: 0,
             }}>
              {(() => {
                const includeFeedback = getFeedbacks().length > 0 && !statusDialog.isReactivation && !statusDialog.isQuickTrupp;
                const activeSteps = getActiveStatusSteps({ includeFeedback });
                const { prev, next, isFirst, isLast } = getStatusStepNeighbors(statusStep, activeSteps);

                const canContinue = (() => {
                  if (statusStep === 1) return statusOk !== null;
                  if (statusStep === 2) return Boolean(editStandort);
                  if (statusStep === 3) return Boolean(editAufgabe) && editAufgabe !== 'Warten';
                  if (statusStep === 4) return Boolean(editDruck) && editDruck > 0;
                  if (statusStep === 5) return Boolean(funkkanal);
                  return true;
                })();

                const goBack = () => {
                  if (prev != null) setStatusStep(prev);
                };
                const goNextOrSave = () => {
                  if (!canContinue) return;
                  if (next != null) setStatusStep(next);
                  else handleStatusSave();
                };

                return (
                  <>
                    {isFirst ? (
                      <button 
                        onClick={() => setStatusDialog({ open: false, trupp: null })} 
                        style={{
                          padding: isMobileLayout ? '10px 14px' : '12px 24px',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: isMobileLayout ? '14px' : '16px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          background: '#6c757d',
                          color: 'white',
                          flex: isMobileLayout ? 1 : undefined,
                        }}
                      >
                        Abbrechen
                      </button>
                    ) : (
                      <button
                        onClick={goBack}
                        style={{
                          ...modalButtonStyle,
                          ...(isMobileLayout ? { padding: '10px 14px', fontSize: 14, flex: 1 } : null),
                        }}
                      >
                        Zurück
                      </button>
                    )}
                    <button
                      onClick={goNextOrSave}
                      style={{
                        ...(!canContinue ? modalButtonPrimaryDisabledStyle : modalButtonPrimaryStyle),
                        ...(isMobileLayout ? { padding: '10px 14px', fontSize: 14, flex: 1 } : null),
                      }}
                      disabled={!canContinue}
                    >
                      {isLast ? 'Speichern' : 'Weiter'}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      {mitgliederDialog.open && mitgliederDialog.trupp && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div className={window.innerWidth <= 600 ? 'einsatzdetail-modal-mobile' : ''} style={{ background: '#fff', borderRadius: 16, padding: 24, minWidth: 500, maxWidth: 500, minHeight: 400, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 4px 32px #0002' }}>
            {/* Titelbereich oben */}
            <div id="modal-title" style={{ width: '100%', textAlign: 'center', fontWeight: 700, fontSize: 22, marginBottom: 20, letterSpacing: 1 }}>
              {localEinsatzleiterMode ? 'Mitglieder anzeigen' : 'Mitglieder bearbeiten'}
            </div>
            {/* NFC Status */}
            {isNFCAvailable() && (
              <div style={{ 
                fontSize: 12, 
                color: '#999', 
                marginBottom: 16,
                fontStyle: 'italic',
                textAlign: 'center'
              }}>
                NFC aktiv - Scanne automatisch
              </div>
            )}
            {/* Mittlerer Bereich */}
            <div style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 20px' }}>
              <div style={{ width: '100%' }}>
                <div style={{ marginBottom: 16, fontWeight: 700, fontSize: 16, color: '#333' }}>
                  {localEinsatzleiterMode ? 'Mitglieder:' : 'Aktuelle Mitglieder:'}
                </div>
                <ul style={{ listStyle: 'none', margin: 0, maxHeight: 200, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 8, padding: '8px 0' }}>
                  {editMitglieder.map((m, i) => (
                    <li key={i} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 12, 
                      padding: '12px 16px',
                      borderBottom: i < editMitglieder.length - 1 ? '1px solid #f0f0f0' : 'none',
                      backgroundColor: editTruppfuehrerIndex === i ? '#f8f8f8' : 'transparent'
                    }}>
                      {!localEinsatzleiterMode && (
                      <input 
                        type="radio" 
                        name="tf" 
                        checked={editTruppfuehrerIndex === i} 
                        onChange={() => setEditTruppfuehrerIndex(i)}
                        style={{ margin: 0 }}
                      />
                      )}
                      <span style={{ flex: 1, fontWeight: 500 }}>
                        {m.vorname} {m.nachname}
                        {localEinsatzleiterMode && editTruppfuehrerIndex === i && (
                          <span style={{ color: '#c00', fontWeight: 700, marginLeft: 8 }}>(Truppführer)</span>
                        )}
                      </span>
                      {!localEinsatzleiterMode && (
                      <button 
                        onClick={() => handleMitgliedRemove(i)} 
                        style={{ 
                          background: '#c00', 
                          color: '#fff', 
                          border: 'none', 
                          borderRadius: 6, 
                          padding: '6px 12px', 
                          fontWeight: 700, 
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                      >
                        Entfernen
                      </button>
                      )}
                    </li>
                  ))}
                  {editMitglieder.length === 0 && (
                    <li style={{ padding: '20px', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                      Keine Mitglieder vorhanden
                    </li>
                  )}
                </ul>
                
                {!localEinsatzleiterMode && (
                <div style={{ marginTop: 24, marginBottom: 16 }}>
                  <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 16, color: '#333' }}>
                    Neues Mitglied hinzufügen:
                  </div>
                  <div style={{ position: 'relative', display: 'flex', gap: 12, alignItems: 'center' }}>
                    <input 
                      placeholder="Name eingeben oder NFC-Tag scannen..." 
                      value={mitgliederAutocompleteValue} 
                      onChange={e => handleMitgliederAutocompleteChange(e.target.value)}
                      onKeyPress={handleMitgliederAutocompleteKeyPress}
                      style={{ 
                        fontSize: 15, 
                        padding: '10px 12px', 
                        borderRadius: 8, 
                        border: '1.5px solid #c00', 
                        outline: 'none', 
                        flex: 1,
                        minWidth: 200
                      }} 
                    />
                    <button 
                      onClick={handleMitgliederAutocompleteAdd} 
                      style={{ 
                        background: '#c00', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 8, 
                        padding: '10px 16px', 
                        fontWeight: 700,
                        fontSize: 16,
                        minWidth: 50
                      }}
                    >
                      +
                    </button>
                  </div>
                  
                  {/* Autocomplete-Liste */}
                  {mitgliederAutocompleteShowList && mitgliederAutocompleteUsers.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#fff',
                      border: '1px solid #ccc',
                      borderRadius: 8,
                      maxHeight: 200,
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      marginTop: 4
                    }}>
                      {mitgliederAutocompleteUsers.map((user, index) => (
                        <div
                          key={index}
                          onClick={() => handleMitgliederAutocompleteSelect(user)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: index < mitgliederAutocompleteUsers.length - 1 ? '1px solid #eee' : 'none',
                            backgroundColor: mitgliederAutocompleteSelectedUser === user ? '#f0f0f0' : 'transparent'
                          }}
                          onMouseEnter={() => setMitgliederAutocompleteSelectedUser(user)}
                        >
                          {user.firstName} {user.lastName}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* NFC-Scan-Ergebnis */}
                  {nfcScanResult && (
                    <div style={{
                      marginTop: 8,
                      padding: '8px 12px',
                      borderRadius: 6,
                      fontSize: 14,
                      backgroundColor: nfcScanResult.success ? '#d4edda' : '#f8d7da',
                      color: nfcScanResult.success ? '#155724' : '#721c24',
                      border: `1px solid ${nfcScanResult.success ? '#c3e6cb' : '#f5c6cb'}`
                    }}>
                      {nfcScanResult.message}
                    </div>
                  )}
                </div>
                )}
              </div>
            </div>
            {/* Button-Bereich unten */}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: 12, marginTop: 20 }}>
              {localEinsatzleiterMode ? (
                <button onClick={() => setMitgliederDialog({ open: false, trupp: null })} style={modalButtonPrimaryStyle}>Schliessen</button>
              ) : (
                <>
              <button onClick={() => setMitgliederDialog({ open: false, trupp: null })} style={modalButtonStyle}>Abbrechen</button>
              <button onClick={handleMitgliederSave} style={modalButtonPrimaryStyle} disabled={editMitglieder.length === 0 || editTruppfuehrerIndex === null}>Speichern</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Auditlog-Modal wird von TruppKachel-Komponente verwaltet */}
      {/* Mayday Modal ganz oben */}
      {maydayOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 6000 }}>
          {maydayStep === 1 && (
            <div className={window.innerWidth <= 600 ? 'einsatzdetail-modal-mobile' : ''} style={{ background: '#fff', borderRadius: 16, padding: 32, minWidth: 340, maxWidth: 500, display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', boxShadow: '0 8px 40px #0002' }}>
              <h3 style={{ color: '#c00', fontWeight: 900, fontSize: 28, marginBottom: 8 }}>MAYDAY</h3>
              <div style={{ marginBottom: 16, fontWeight: 700 }}>Wähle den betroffenen Trupp</div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                {trupps.filter((trupp: Trupp) => !trupp.outOfService && trupp.aufgabe !== 'Warten').length === 0 ? (
                  <div style={{ color: '#888', fontSize: 18, fontWeight: 500, padding: 24 }}>Keine aktiven Trupps im Einsatz</div>
                ) : (
                  (trupps.filter((trupp: Trupp) => !trupp.outOfService && trupp.aufgabe !== 'Warten') as Trupp[]).map((trupp: Trupp) => (
                    <div key={trupp.id} onClick={async () => {
                      setMaydayTrupp(trupp);
                      setMaydayStep(2);
                      const auditUser = getAuditUserInfo();
                      const triggeredBy = auditUser.realUser || 'Unbekannter Benutzer';
                      
                      // Lokalen State aktualisieren
                      const updatedTrupp = { 
                        ...trupp, 
                        mayday: true,
                        maydayTriggeredBy: triggeredBy,
                        maydayTriggeredAt: Date.now()
                      };
                      
                      setTrupps(trupps.map(t => t.id === trupp.id ? updatedTrupp : t));
                      
                      // An API senden
                      try {
                        // Prüfe ob es ein API-Team ist (kleine ID) oder lokales Team (große ID)
                        if (trupp.id < 1000000) {
                          // API-Team - verwende updateTeamInAPI
                          await updateTeamInAPI(updatedTrupp);
                        } else {
                          // Lokales Team - verwende saveTeamToAPI
                          await saveTeamToAPI(updatedTrupp);
                        }
                      
                      } catch (error) {
                        console.error('❌ Fehler beim Senden des Mayday-Status an API:', error);
                      }
                      
                      addAudit({ type: 'Mayday', message: `Mayday-Alarm für '${trupp.name}' ausgelöst.`, shortDescription: 'Mayday Alarm ausgelöst', data: { truppId: trupp.id, name: trupp.name, einsatzId: einsatz.id } });
                    }}
                      style={{
                        background: '#eee',
                        color: '#222',
                        borderRadius: 12,
                        padding: 24,
                        cursor: 'pointer',
                        minWidth: 140,
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: 20,
                        boxShadow: '0 2px 8px #0002',
                        border: '2px solid #c00',
                        marginBottom: 0,
                      }}
                    >{trupp.name}</div>
                  ))
                )}
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button onClick={() => setMaydayOpen(false)} style={{ background: '#eee', color: '#222', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, minWidth: 100, cursor: 'pointer', boxShadow: '0 2px 8px #c002' }}>Abbrechen</button>
              </div>
            </div>
          )}
          {maydayStep === 2 && maydayTrupp && (
            <div className={window.innerWidth <= 600 ? 'einsatzdetail-modal-mobile' : ''} style={{ background: '#fff', borderRadius: 16, padding: 32, minWidth: 340, maxWidth: 500, display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', boxShadow: '0 8px 40px #0002' }}>
              <h3 style={{ color: '#c00', fontWeight: 900, fontSize: 28, marginBottom: 8 }}>MAYDAY</h3>
              <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 8 }}>{maydayTrupp.name}</div>
              <div style={{ marginBottom: 16, fontWeight: 500 }}>Folgende Fragen an den Trupp stellen:</div>
              <ul style={{ fontSize: 17, marginBottom: 16 }}>
                <li style={{ marginBottom: 6 }}>Wo bist du?</li>
                <li style={{ marginBottom: 6 }}>Wie viele Personen?</li>
                <li style={{ marginBottom: 6 }}>Status?</li>
                <li style={{ marginBottom: 6 }}>Benötigst du Hilfe?</li>
                <li style={{ marginBottom: 6 }}>Kannst du dich selbst retten?</li>
            </ul>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                <button onClick={() => setMaydayStep(1)} style={{ background: '#eee', color: '#222', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, minWidth: 100, cursor: 'pointer', boxShadow: '0 2px 8px #c002' }}>Zurück</button>
                <button onClick={() => { setMaydayOpen(false); setMaydayStep(1); setMaydayTrupp(null); }} style={{ background: '#eee', color: '#222', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, minWidth: 100, cursor: 'pointer', boxShadow: '0 2px 8px #c002' }}>Schliessen</button>
          </div>
            </div>
          )}
        </div>
      )}
      {/* Floating Mayday - Modal überlagert diesen Button (höherer z-index) */}
      {!localEinsatzleiterMode && !modalOpen && !statusDialog.open && !mitgliederDialog.open && !showBulkFunkkanalModal && !maydayOpen && (
        <button
          className="floating-mayday-btn"
          onClick={einsatzGeschlossen ? undefined : () => { setMaydayOpen(true); setMaydayStep(1); setMaydayTrupp(null); }}
          style={{
            position: 'fixed',
            left: '50%',
            bottom: isMobileLayout ? 12 : 15,
            transform: 'translateX(-50%)',
            zIndex: 5000,
            background: '#c00',
            color: '#fff',
            border: 'none',
            borderRadius: 999,
            padding: isMobileLayout ? '8px 16px' : '15px 32px',
            fontWeight: 900,
            fontSize: isMobileLayout ? 15 : 32,
            boxShadow: '0 4px 24px #0003',
            display: 'flex',
            alignItems: 'center',
            gap: isMobileLayout ? 6 : 24,
            cursor: einsatzGeschlossen ? 'not-allowed' : 'pointer',
            letterSpacing: isMobileLayout ? 0.8 : 2,
            textTransform: 'uppercase',
            minWidth: isMobileLayout ? 0 : 220,
            width: 'auto',
            maxWidth: isMobileLayout ? '70vw' : undefined,
            justifyContent: 'center',
            opacity: einsatzGeschlossen ? 0.5 : 1,
            lineHeight: 1.1,
          }}
          disabled={einsatzGeschlossen}
        >
          <WarningIcon
            className="floating-mayday-icon"
            style={{ fontSize: isMobileLayout ? 18 : 44, marginRight: isMobileLayout ? 0 : 8 }}
          />
          MAYDAY
        </button>
      )}
      {showEinsatzBeendenModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, minWidth: 320, maxWidth: 400, boxShadow: '0 4px 32px #0002', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h3 style={{ color: '#c00', fontWeight: 900, fontSize: 22, marginBottom: 16 }}>Einsatz wirklich schliessen?</h3>
            <div style={{ fontSize: 16, marginBottom: 24, textAlign: 'center' }}>Alle Trupps werden aus dem Einsatz genommen und alle Zeiten gestoppt. Diese Aktion kann nicht rückgängig gemacht werden.</div>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
              <button onClick={() => setShowEinsatzBeendenModal(false)} style={{ background: '#eee', color: '#222', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, minWidth: 100, cursor: 'pointer', boxShadow: '0 2px 8px #c002' }}>Abbrechen</button>
              <button onClick={async () => {
                // Remote-Einsatz? Dann Backend-Call
                const remoteEinsaetze = JSON.parse(localStorage.getItem('remoteEinsaetze') || '[]');
                const remoteIdx = remoteEinsaetze.findIndex((e: any) => String(e.id ?? e.opsID ?? e.name) === String(einsatz.id));
                if (remoteIdx !== -1) {
                  const ok = await closeRemoteEinsatz();
                  if (!ok) return;
                }
                
                // Alle aktiven Trupps aus dem Einsatz nehmen und in DB aktualisieren
                const activeTrupps = trupps.filter(t => !t.outOfService);
                
                // Zuerst alle aktiven Trupps in der DB als "aus dem Einsatz genommen" markieren
                for (const trupp of activeTrupps) {
                  try {
                    // Auditlog-Eintrag für jeden Trupp
                    addAudit({ 
                      type: 'Trupp aus Einsatz', 
                      message: `Trupp '${trupp.name}' aus dem Einsatz genommen.`, 
                      data: { 
                        name: trupp.name, 
                        truppId: trupp.id,
                        einsatzId: einsatz.id 
                      } 
                    });
                    
                    // Wenn authentifiziert, auch in DB aktualisieren
                    if (isAuthenticatedSync()) {
                      await updateTeamInAPI({
                        ...trupp,
                        outOfService: true,
                        alarmMuted: false,
                        alarmMutedAt: undefined,
                        geschlossen: true
                      });
                    }
                  } catch (error) {
                    console.error(`Fehler beim Aktualisieren von Trupp ${trupp.name}:`, error);
                  }
                }
                
                // Alle Trupps auf aus dem Einsatz setzen, Zeiten stoppen
                setTrupps(trupps => {
                  const neueTrupps = trupps.map(t => ({
                    ...t,
                    outOfService: true, // Mark as out of service
                    alarmMuted: false, // Stummschaltung beenden, Countdown sofort stoppen
                    alarmMutedAt: undefined,
                    createdAt: undefined, // Reset creation time
                    startedAt: undefined, // Reset start time - use undefined to send NULL to database
                    abfrageResetAt: undefined,
                    pausedAt: undefined,
                    pausedElapsed: undefined,
                    originalAufgabe: t.aufgabe, // Save current task to originalAufgabe before marking as out of service
                  }));
      
      // Mayday-Benachrichtigungen werden von MaydayNotificationManager verwaltet
                  
                  // Einsatz im LocalStorage als geschlossen markieren und Trupps aktualisieren
                  const einsaetze = JSON.parse(localStorage.getItem('einsaetze') || '[]');
                  // Lokaler Einsatz
                  const localIdx = einsaetze.findIndex((e: any) => String(e.id) === String(einsatz.id));
                  if (localIdx !== -1) {
                    einsaetze[localIdx].geschlossen = true;
                    einsaetze[localIdx].trupps = neueTrupps;
                    localStorage.setItem('einsaetze', JSON.stringify(einsaetze));
                  }
                  // Remote-Einsatz
                  if (remoteIdx !== -1) {
                    remoteEinsaetze[remoteIdx].geschlossen = true;
                    remoteEinsaetze[remoteIdx].trupps = neueTrupps;
                    localStorage.setItem('remoteEinsaetze', JSON.stringify(remoteEinsaetze));
                  }
                  
                  // Zusätzlich: Trupps auch direkt im LocalStorage speichern (für den Fall, dass sie separat gespeichert werden)
                  saveTeamsToLocalStorage(neueTrupps, einsatz);
                  
                  return neueTrupps;
                });
                
                setEinsatzGeschlossen(true);
                setShowEinsatzBeendenModal(false);
                
                // Haupt-Auditlog-Eintrag für Einsatz-Ende
                addAudit({ 
                  type: 'Einsatz beendet', 
                  message: `Einsatz '${einsatz.name}' wurde beendet. Alle ${activeTrupps.length} aktiven Trupps wurden aus dem Einsatz genommen.`, 
                  data: { 
                    einsatzId: einsatz.id, 
                    name: einsatz.name,
                    activeTruppsCount: activeTrupps.length
                  } 
                });
                
                // Nach 2 Sekunden zurück zum Dashboard navigieren
                setTimeout(() => {
                  resetEinsatzleiterMode();
                  navigate('/dashboard');
                }, 2000);
              }} style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, minWidth: 100, cursor: 'pointer', boxShadow: '0 2px 8px #c002' }}>Einsatz schliessen</button>
                </div>
                </div>
        </div>
      )}
      {einsatzGeschlossen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: 60, background: '#c00', color: '#fff', fontWeight: 900, fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, letterSpacing: 2, boxShadow: '0 2px 12px #c002' }}>
          Einsatz geschlossen - Weiterleitung zum Dashboard...
                </div>
      )}
      {/* Globale Einstellungen Modal */}
      {canAccessSettingsState && (
        <GlobalSettingsModal 
          isOpen={settingsOpen} 
          onClose={() => {
            setSettingsOpen(false);
            try {
              const v = Number(localStorage.getItem('maxTrupps')) || Number(localStorage.getItem('maxTruppsProEinsatz'));
              if (v > 0 && v !== maxTrupps) {
                setMaxTrupps(v);
              }
            } catch {}
          }}
          orgID={(() => {
            try {
              const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
              if (accessToken) {
                const payload = JSON.parse(atob(accessToken.split('.')[1]));
                return payload.orgID || 0;
              }
            } catch (error) {
              console.error('Error extracting orgID from token:', error);
            }
            return 0;
          })()}
        />
      )}
      {/* Delete-Einsatz-Modal */}
      {showDeleteEinsatzModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, minWidth: 320, maxWidth: 400, boxShadow: '0 4px 32px #0002', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h3 style={{ color: '#c00', fontWeight: 900, fontSize: 22, marginBottom: 16 }}>Einsatz wirklich löschen?</h3>
            <div style={{ fontSize: 16, marginBottom: 24, textAlign: 'center' }}>Dieser Einsatz und alle zugehörigen lokalen Daten werden unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.</div>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
              <button onClick={() => setShowDeleteEinsatzModal(false)} style={{ background: '#eee', color: '#222', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, minWidth: 100, cursor: 'pointer', boxShadow: '0 2px 8px #c002' }}>Abbrechen</button>
              <button onClick={reallyDeleteEinsatz} style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, minWidth: 100, cursor: 'pointer', boxShadow: '0 2px 8px #c002' }}>Löschen</button>
            </div>
          </div>
        </div>
      )}
      {showTruppTypeModal.open && showTruppTypeModal.trupp && (() => {
        const typeTrupp = trupps.find(t => t.id === showTruppTypeModal.trupp?.id) || showTruppTypeModal.trupp;
        const makeEmergency = typeTrupp.emergencyTrupp !== 1;
        return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, minWidth: 320, maxWidth: 400, boxShadow: '0 4px 32px #0002', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h3 style={{ color: '#c00', fontWeight: 900, fontSize: 22, marginBottom: 16, textAlign: 'center' }}>
              {makeEmergency ? 'Zum Notfalltrupp machen?' : 'Zum Normaltrupp machen?'}
            </h3>
            <div style={{ fontSize: 16, marginBottom: 24, textAlign: 'center' }}>
              {makeEmergency
                ? <>Soll <b>{typeTrupp.name}</b> wirklich zum Notfalltrupp gemacht werden?</>
                : <>Soll <b>{typeTrupp.name}</b> wirklich zum Normaltrupp gemacht werden?</>}
            </div>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
              <button onClick={() => setShowTruppTypeModal({ open: false, trupp: null })} style={{ background: '#eee', color: '#222', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, minWidth: 100, cursor: 'pointer', boxShadow: '0 2px 8px #c002' }}>Abbrechen</button>
              <button
                onClick={() => {
                  handleEmergencyChange(typeTrupp, makeEmergency);
                  setShowTruppTypeModal({ open: false, trupp: null });
                }}
                style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, minWidth: 100, cursor: 'pointer', boxShadow: '0 2px 8px #c002' }}
              >
                Bestätigen
              </button>
            </div>
          </div>
        </div>
        );
      })()}
      {showDeleteTruppModal.open && showDeleteTruppModal.trupp && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, minWidth: 320, maxWidth: 400, boxShadow: '0 4px 32px #0002', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h3 style={{ color: '#c00', fontWeight: 900, fontSize: 22, marginBottom: 16 }}>Trupp wirklich löschen?</h3>
            <div style={{ fontSize: 16, marginBottom: 24, textAlign: 'center' }}>Der Trupp <b>{showDeleteTruppModal.trupp.name}</b> und alle zugehörigen Daten werden unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.</div>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
              <button onClick={() => setShowDeleteTruppModal({ open: false, trupp: null })} style={{ background: '#eee', color: '#222', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, minWidth: 100, cursor: 'pointer', boxShadow: '0 2px 8px #c002' }}>Abbrechen</button>
              <button onClick={reallyDeleteTrupp} style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, minWidth: 100, cursor: 'pointer', boxShadow: '0 2px 8px #c002' }}>Löschen</button>
            </div>
          </div>
        </div>
      )}
      {renameTruppModal.open && renameTruppModal.trupp && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, minWidth: 320, maxWidth: 460, boxShadow: '0 4px 32px #0002', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <h3 style={{ color: '#c00', fontWeight: 900, fontSize: 22, margin: 0 }}>Truppnummer bearbeiten</h3>
            <div style={{ fontSize: 14, color: '#666' }}>
              Passe den Truppnamen direkt an (z.B. Trupp 7).
            </div>
            <input
              value={renameTruppValue}
              onChange={(e) => setRenameTruppValue(e.target.value)}
              placeholder="Truppname eingeben..."
              style={{
                fontSize: 16,
                padding: '10px 12px',
                borderRadius: 8,
                border: '1.5px solid #ddd',
                outline: 'none'
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#c00'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = '#ddd'; }}
            />
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 6 }}>
              <button
                onClick={() => { setRenameTruppModal({ open: false, trupp: null }); setRenameTruppValue(''); }}
                style={{ background: '#eee', color: '#222', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveTruppRename}
                disabled={!renameTruppValue.trim()}
                style={{ background: !renameTruppValue.trim() ? '#ccc' : '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, cursor: !renameTruppValue.trim() ? 'not-allowed' : 'pointer' }}
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
      {/* State für Druckanpassung beim Aus-Einsatz-Nehmen */}
      {showPressureAdjustModal.open && showPressureAdjustModal.trupp && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, minWidth: 320, maxWidth: 400, boxShadow: '0 4px 32px #0002', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h3 style={{ color: '#c00', fontWeight: 900, fontSize: 22, marginBottom: 16 }}>Druckanpassung für {showPressureAdjustModal.trupp?.name}</h3>
            <div style={{ fontSize: 16, marginBottom: 24, textAlign: 'center' }}>
              {showPressureAdjustModal.mode === 'adjust' ? 'Bitte gib den aktuellen Druck ein:' : 'Bitte gib den finalen Druck ein:'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
              <button onClick={() => setFinalPressure(Math.max(0, finalPressure - 10))} style={{ fontSize: 24, color: '#c00', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 900, padding: '0 8px' }}>&lt;</button>
              <span style={{ fontSize: 28, fontWeight: 900, minWidth: 80, textAlign: 'center' }}>{finalPressure} Bar</span>
              <button onClick={() => setFinalPressure(Math.min(400, finalPressure + 10))} style={{ fontSize: 24, color: '#c00', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 900, padding: '0 8px' }}>&gt;</button>
            </div>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
              <button onClick={() => setShowPressureAdjustModal({ open: false, trupp: null, mode: 'out' })} style={{ background: '#eee', color: '#222', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, minWidth: 100, cursor: 'pointer', boxShadow: '0 2px 8px #c002' }}>Abbrechen</button>
              <button onClick={async () => {
                if (!showPressureAdjustModal.trupp) return;
                const trupp = showPressureAdjustModal.trupp;
                const previousPressure = trupp.currentPressure || trupp.startdruck;

                if (showPressureAdjustModal.mode === 'adjust') {
                  const isImEinsatz = !trupp.outOfService && trupp.aufgabe !== 'Warten' && !!(trupp.createdAt || trupp.startedAt);
                  if (!isImEinsatz) {
                    setShowPressureAdjustModal({ open: false, trupp: null, mode: 'out' });
                    return;
                  }
                  const now = Date.now();
                  const updatedTrupps = trupps.map(t => t.id === trupp.id ? {
                    ...t,
                    currentPressure: finalPressure,
                    mitglieder: t.mitglieder.map(m => ({ ...m, druck: finalPressure })),
                    abfrageCountdown: 300,
                    abfrageResetAt: now,
                  } : t);
                  setTrupps(updatedTrupps);
                  saveTeamsToLocalStorage(updatedTrupps);
                  if (isAuthenticatedSync()) {
                    try {
                      await updateTeamInAPI(updatedTrupps.find(t => t.id === trupp.id)!);
                    } catch (error) {
                      console.error('Error updating pressure in API:', error);
                    }
                  }
                  addAudit({
                    type: 'Druckanpassung',
                    message: `Druck angepasst: ${previousPressure} → ${finalPressure} Bar.`,
                    shortDescription: 'Druck angepasst',
                    data: {
                      name: trupp.name,
                      previousPressure,
                      finalPressure,
                      einsatzId: einsatz.id,
                      truppId: trupp.id
                    }
                  });
                  setShowPressureAdjustModal({ open: false, trupp: null, mode: 'out' });
                  return;
                }
                
                // Druckanpassung auditloggen
                addAudit({
                  type: 'Druckanpassung',
                  message: `Druckanpassung: Finaler Druck angepasst: ${finalPressure} Bar.`,
                  shortDescription: 'Druck angepasst',
                  data: {
                    name: showPressureAdjustModal.trupp.name,
                    previousPressure,
                    finalPressure,
                    einsatzId: einsatz.id,
                    truppId: showPressureAdjustModal.trupp.id
                  }
                });
                
                // Trupp aus dem Einsatz nehmen
                const now = Date.now();
                
                // Berechne aktuelle Werte vor dem Pausieren
                const startTime = (trupp.startedAt && trupp.startedAt > 0) ? trupp.startedAt : trupp.createdAt;
                const elapsed = startTime ? Math.floor((now - startTime) / 1000) : 0;
                
                const updatedTrupps = trupps.map(t => t.id === trupp.id ? {
                  ...t,
                  outOfService: true, // Mark as out of service
                  alarmMuted: false, // Stummschaltung beenden, Countdown sofort stoppen
                  alarmMutedAt: undefined,
                  // Save original startdruck before any changes
                  originalStartdruck: t.startdruck,
                  // Don't update startdruck, only currentPressure
                  currentPressure: finalPressure, // Aktuellen Druck setzen
                  mitglieder: t.mitglieder.map(m => ({ ...m, druck: finalPressure })), // Update member pressures
                  originalAufgabe: t.aufgabe, // Save current task to originalAufgabe before marking as out of service
                  // Pause all timers
                  pausedAt: now,
                  pausedElapsed: elapsed,
                  // Clear active timers
                  createdAt: undefined,
                  startedAt: undefined, // Clear start time - use undefined to send NULL to database
                  abfrageCountdown: 300,
                  abfrageResetAt: undefined,
                } : t);
                
                setTrupps(updatedTrupps);
                
                // Update API
                if (isAuthenticatedSync()) {
                  try {
                    await updateTeamInAPI(updatedTrupps.find(t => t.id === trupp.id)!);
                  } catch (error) {
                    console.error('Error updating team in API:', error);
                  }
                }
                
                saveTeamsToLocalStorage(updatedTrupps);
                
                // Trupp aus Einsatz auditloggen
                addAudit({
                  type: 'Trupp aus Einsatz',
                  message: `Trupp '${trupp.name}' aus dem Einsatz genommen.`,
                  shortDescription: 'Trupp aus Einsatz',
                  data: {
                    name: trupp.name,
                    einsatzId: einsatz.id,
                    truppId: trupp.id,
                    finalPressure,
                    alt: {
                      aufgabe: trupp.aufgabe,
                      standort: trupp.standort,
                      bar: trupp.startdruck,
                      funkkanal: trupp.funkkanal
                    }
                  }
                });
                
                setShowPressureAdjustModal({ open: false, trupp: null, mode: 'out' });
              }} style={{ background: '#c00', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 15, minWidth: 100, cursor: 'pointer', boxShadow: '0 2px 8px #c002' }}>Bestätigen</button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes pulse {
          0% { box-shadow: 0 4px 24px rgba(204, 0, 0, 0.4); }
          50% { box-shadow: 0 4px 32px rgba(204, 0, 0, 0.8); }
          100% { box-shadow: 0 4px 24px rgba(204, 0, 0, 0.4); }
        }
        @media (max-width: 768px) {
          .einsatzdetail-header-row {
            display: none !important;
          }
          .einsatzdetail-header-mobile {
            display: block !important;
            position: relative !important;
            margin-bottom: 20px !important;
            margin-top: 4px !important;
          }
          .einsatzdetail-header-back {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            z-index: 2;
          }
          .einsatzdetail-header-block {
            width: 100% !important;
            min-width: 0;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            margin-left: 0 !important;
            padding-left: 36px !important;
            padding-right: 36px !important;
            box-sizing: border-box !important;
          }
          .einsatzdetail-header-title {
            font-size: 15px !important;
            font-weight: 700 !important;
            text-align: center !important;
            margin-bottom: 0 !important;
            max-width: 100% !important;
          }
          .einsatzdetail-header-block > div:nth-child(2) {
            font-size: 11px !important;
            margin-top: 0 !important;
            margin-bottom: 2px !important;
          }
          .einsatzdetail-header-actions {
            display: flex !important;
            flex-direction: row !important;
            justify-content: center !important;
            align-items: center !important;
            flex-wrap: wrap !important;
            gap: 6px !important;
            margin: 8px 0 0 0 !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .einsatzdetail-header-actions button {
            width: 36px !important;
            height: 36px !important;
            padding: 6px !important;
            font-size: 17px !important;
            border-radius: 10px !important;
          }
          .einsatzdetail-outer {
            padding-left: 12px !important;
            padding-right: 12px !important;
            padding-bottom: 24px !important;
          }
          .einsatzdetail-trupps-row {
            display: flex !important;
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 8px !important;
            max-height: unset !important;
            width: 100% !important;
          }
          .einsatzdetail-trupps-row > div {
            width: 100% !important;
            min-width: 0 !important;
            max-width: none !important;
            height: auto !important;
            max-height: none !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            align-self: stretch !important;
            font-size: 15px !important;
            box-sizing: border-box !important;
            overflow: visible !important;
            position: relative !important;
            background: transparent !important;
          }
          .einsatzdetail-modal-mobile {
            margin: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            max-height: 92vh !important;
          }
          .updatemodal-container.einsatzdetail-modal-mobile {
            overflow: hidden !important;
            height: min(430px, 85vh) !important;
            min-height: min(430px, 85vh) !important;
            max-height: 85vh !important;
          }
          .floating-mayday-btn {
            padding: 10px 18px !important;
            font-size: 16px !important;
            letter-spacing: 1px !important;
            gap: 8px !important;
            min-width: 0 !important;
            width: auto !important;
            max-width: calc(100vw - 48px) !important;
            bottom: 12px !important;
            box-shadow: 0 3px 14px #0003 !important;
          }
          .floating-mayday-icon {
            font-size: 22px !important;
            margin-right: 0 !important;
          }
          .einsatzdetail-trupps-row {
            padding-bottom: 72px !important;
          }
        }
        @media (min-width: 769px) {
          .einsatzdetail-trupps-row {
            display: grid !important;
            grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
            gap: 12px !important;
            align-items: stretch !important;
            justify-items: stretch !important;
            flex-direction: unset !important;
            padding-bottom: 0 !important;
          }
          .einsatzdetail-trupps-row > div {
            width: 100% !important;
            max-width: none !important;
            min-width: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
            box-sizing: border-box !important;
          }
          .einsatzdetail-trupps-row > div > * {
            width: 100% !important;
            max-width: none !important;
            box-sizing: border-box !important;
          }
          .einsatzdetail-outer {
            padding-left: 32px !important;
            padding-right: 32px !important;
            padding-top: 16px !important;
            padding-bottom: 20px !important;
            margin-top: 64px !important;
          }
          .einsatzdetail-modal-mobile {
            margin: 10px !important;
            width: 100%;
            min-width: unset !important;
          }
          body .einsatzdetail-trupps-row > .einsatzdetail-empty-placeholder {
            width: 100vw !important;
            margin: 0 !important;
            max-width: unset !important;
            min-width: unset !important;
          }
        }
      `}</style>

      {/* Einsatz Edit Modal */}
      {einsatzEditModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 20, minWidth: 450, maxWidth: 450, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 4px 32px #0002' }}>
            {/* X-Button oben rechts */}
            <button 
              onClick={() => setEinsatzEditModalOpen(false)}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'none',
                border: 'none',
                fontSize: 24,
                color: '#666',
                cursor: 'pointer',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Schliessen"
            >
              ×
            </button>
            {/* Titelbereich oben */}
            <div style={{ width: '100%', textAlign: 'center', fontWeight: 700, fontSize: 22, marginBottom: 20, letterSpacing: 1 }}>
              Einsatz bearbeiten
            </div>
            
            {/* Einsatzart */}
            <div style={{ width: '100%', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 16 }}>Einsatzart:</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {operationsTypes.length > 0 ? (
                  operationsTypes.map((type) => (
                    <div
                      key={type.id}
                      onClick={() => {
                        setEditEinsatzCategoryID(type.id);
                        setEditEinsatzTyp(type.name);
                      }}
                      style={{
                        background: editEinsatzCategoryID === type.id ? '#c00' : '#eee',
                        color: editEinsatzCategoryID === type.id ? '#fff' : '#222',
                        borderRadius: 8,
                        padding: '12px 8px',
                        cursor: 'pointer',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: 14,
                        transition: 'background 0.2s',
                        userSelect: 'none',
                      }}
                    >
                      {type.name}
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: 'center', color: '#666', gridColumn: '1 / span 2' }}>
                    Lade Einsatz-Typen...
                  </div>
                )}
              </div>
            </div>

            {/* Einsatzort */}
            <div style={{ width: '100%', marginBottom: 24 }}>
              <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 16 }}>Einsatzort:</div>
              <input
                placeholder="Ort/Adresse"
                value={editEinsatzOrt}
                onChange={e => setEditEinsatzOrt(e.target.value)}
                style={{
                  fontSize: 16,
                  padding: '12px 16px',
                  borderRadius: 8,
                  border: '2px solid #c00',
                  outline: 'none',
                  width: '100%',
                  boxSizing: 'border-box',
                  boxShadow: '0 2px 8px #c002',
                }}
              />
            </div>

            {/* Button-Bereich */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={handleSaveEinsatzEdit}
                style={{
                  background: '#c00',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 24px',
                  fontWeight: 'bold',
                  fontSize: 15,
                  minWidth: 100,
                  cursor: 'pointer',
                  opacity: !editEinsatzOrt ? 0.6 : 1,
                  boxShadow: '0 2px 8px #c002',
                }}
                disabled={!editEinsatzOrt}
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NFC Toast Overlay */}
      {nfcScanResult && (
        <div style={{
          position: 'fixed',
          top: 20,
          right: 20,
          padding: '12px 16px',
          borderRadius: 8,
          backgroundColor: nfcScanResult.success ? '#d4edda' : '#f8d7da',
          color: nfcScanResult.success ? '#155724' : '#721c24',
          border: `1px solid ${nfcScanResult.success ? '#c3e6cb' : '#f5c6cb'}`,
          fontSize: 14,
          fontWeight: 500,
          zIndex: 10000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxWidth: 300,
          wordWrap: 'break-word'
        }}>
          {nfcScanResult.message}
        </div>
      )}

      {/* Mayday-Benachrichtigungen */}
      {einsatz && (
        <MaydayNotificationManager
          einsatzId={einsatz.id}
          trupps={trupps}
          onNotificationChange={() => {}} // Leere Funktion, da State nicht mehr hier verwaltet wird
        />
      )}

      {/* Trupp Auditlog Modal – alle Trupps des Überwachers */}
      {showTruppAuditlog && (
        <TruppAuditlogModal
          trupps={trupps.map((t) => ({ id: t.id, name: t.name }))}
          onClose={() => setShowTruppAuditlog(false)}
          auditlog={auditlog}
        />
      )}

      {/* Benutzer-Identifikation für technische Accounts */}
      <UserIdentificationModal
        open={showUserIdentModal}
        confirmMode={false}
        onConfirm={handleUserIdentified}
        onCancel={() => setShowUserIdentModal(false)}
        excludedUserKeys={excludedSupervisorSelectionKeys}
      />

      {/* Trupp-Übergabe Modal bei Benutzerwechsel */}
      <TruppHandoverModal
        open={showHandoverModal}
        previousUserName={handoverPreviousUserName}
        newUserName={handoverNewIdentity ? `${handoverNewIdentity.firstName} ${handoverNewIdentity.lastName}`.trim() : ''}
        trupps={handoverTrupps}
        onConfirm={handleHandoverConfirm}
        onCancel={handleHandoverCancel}
      />

      <BulkFunkkanalModal
        open={showBulkFunkkanalModal}
        trupps={myActiveTruppsForBulkChannel}
        availableChannels={getFunkkanaele()}
        saving={bulkFunkkanalSaving}
        onConfirm={handleBulkFunkkanalChange}
        onCancel={() => {
          if (!bulkFunkkanalSaving) setShowBulkFunkkanalModal(false);
        }}
      />

    </div>
  );
};
export default EinsatzDetail; 